import {
  withLegacyToleranceNames,
  answerToleranceMode, answerSymmetry,
  angleModeFor, addSliderVersion, applySliderVersion, removeSliderVersion, versionRollKey, writeSliderVersion, type FieldVersion,
  visibleTargeted, SET_ANSWER_TRIGGER, displayEvaluation, resolveFeedbackTriggers, computeSliderAcceptedRanges, isSliderCorrectUnreachable, isSliderValueDefaultIncorrectUnreachable, isSliderValueFeedbackFullySubsumed, doesSliderValueFeedbackOverlapAccepted, findSliderOverlappingFeedbackPairs, hasReachableStepInRange, hasReachableLogStepInRange, sliderTickCount, shouldShowMajorTickValues, MAX_READABLE_MAJOR_TICKS, buildSliderHandleTrigger, parseSliderHandleTrigger, evaluateSlider, evaluateFormulaRaw, computeFormulaExtrema, hasVariableChip, probeRoll, type SliderBoundedInterval, type SliderScaleMode, type SliderOrientation, type SliderHandleId, type SliderAnswer, type SliderBlockData as BlocksSliderBlockData, type FeedbackState, sanitizeTriggerValue, type GradingMode, triggerFault, type TriggerFault, analyseOverlapAcrossRolls, chipNamesIn, resolveTriggerValues, forEachRoll, OVERLAP_ENUMERATION_MAX_COMBOS, overlappingAnswerPairs, compareExpressionsAcrossRolls, type ComparisonVerdict, type ScenarioFieldBranch, type DisplayVersion, writeScenarioRow, applyScenarioRow, remapVersionsToScenarios, syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, scenarioInForce, markScenarioReviewed, unreviewedCount, SLIDER_VERSION_KEYS, type DrivenFollower } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  MoveHorizontal,
  Percent,
  Ruler,
  Trash2,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { betweenInversionVerdict, type BlockIssue, incompleteBetweenRowIds, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, triggerRangeInversionVerdict, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { CollapsedInputBlock, CollapsedBlockBar, collapsedIssueBar, collapsedModeBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { answerBarHasContent, AnswerBarText, activeIntervalIds, chipsToPlainText, ConditionChipRow, toTestBarChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass, UnreachableChip } from './eval-chip';
import { ChipDivider, FeedbackCard, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, chipEvalIcon, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { FormulaField, FormulaWithValues, resolvedTriggerChip } from './formula-field';
import { gradingModes, positiveSlotTitleFor } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { SliderTrack } from './slider-track';
import { SymmetryToggle, ToleranceRow } from './tolerance-row';
import { TriggerKindSelect, triggerKindOf } from './trigger-kind';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { answerPositionsKey, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { BlockQuestion, AnswerDisplayFrame, BlockPreviewProvider, hasRollableVariables, htmlHasVariableChips, rollVariableValues } from './variable-chip-preview';
import { VariableTestController } from './variable-test-controller';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { BetweenAnswerRow } from '@/components/task/between-answer-row';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { useExtraBlockSetting } from '@/hooks/use-capabilities';
import { catchAllChips, catchAllTitle, neutralChips } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { sliderChips, sliderTargetedTitle, answerExprTexts} from '@/lib/block-state-labels';

/** The raw bound values a trigger carries, for `resolvedTriggerChip`. Reads the stored string
 * rather than a parsed number, because the whole point is to find the formula text as authored. */
function triggerValuesOf(trigger: string): string[] {
  const parts = trigger.replace(/^[ab]:/, '').split(':');
  return parts.slice(1);
}
// ─── Types ────────────────────────────────────────────────────────────────────

interface SliderBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by the page-level Submit in Preview — see project_preview_interactive_rollout. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

type ConditionType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'between';
type ToleranceMode = 'absolute' | 'percentage';
type TriggerType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'range';

interface ExactAnswer {
  id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
  /** Both per answer since the tolerance move. Absent on answers authored before it, which
   *  fall back to the HANDLE's own pair — always read through `answerSymmetry`/
   *  `answerToleranceMode`, never directly. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
  toleranceMode?: ToleranceMode;
}
interface BetweenAnswer { id: string; low: string; high: string }
interface TickLabelRow { id: string; value: string; label: string }

/** Handle B's full grading config — bundled the same way @agavi/blocks' SliderHandleConfig is,
 * but string-valued during editing (matches every other numeric field in this file, e.g. min/
 * max/threshold — parsed to numbers only at evaluation/display time via num()). Present in state
 * unconditionally so toggling dualHandle on/off never needs lazy init. */
interface HandleBConfig {
  conditionType: ConditionType;
  answers: ExactAnswer[];
  toleranceMode: ToleranceMode;
  toleranceSymmetry: 'symmetric' | 'asymmetric';
  betweenAnswers: BetweenAnswer[];
  threshold: string;
  defaultPosition: string;
}

/** How long a tick-label marker may be. See the maxLength note at its input. */
const SLIDER_TICK_LABEL_MAX = 18;

export interface SliderBlockData extends DrivenFollower {
  /** Per-field angle modes, keyed by `fieldId` — what the in-field toggle writes. A slider is
   *  not an angle instrument at all; its formulas just needed SOME mode, and now each field
   *  carries its own like everywhere else. */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  /** This block's question bank — see `versions.ts`. A version carries the accepted ANSWERS and
   *  the error model; the scale, labels and ticks stay shared (§3.2). */
  versions?: FieldVersion<{ states: FeedbackState[] }>[];
  /** Following a scenario: the leader, and one answer setup per variant. A value block's row
   *  carries `SLIDER_VERSION_KEYS` flat, exactly as a version does — see `ScenarioFieldBranch`.
   *  Mutually exclusive with `versions` (§2.2). */
  followsBlockId?: string;
  scenarioBranches?: ScenarioFieldBranch<{ states: FeedbackState[] }>[];
  question: string;
  min: string;
  max: string;
  stepSize: string;
  logStepsPerDecade: string;
  scaleMode: SliderScaleMode;
  defaultPosition: string;
  conditionType: ConditionType;
  answers: ExactAnswer[];
  toleranceMode: ToleranceMode;
  toleranceSymmetry: 'symmetric' | 'asymmetric';
  betweenAnswers: BetweenAnswer[];
  threshold: string;
  dualHandle: boolean;
  handleB: HandleBConfig;
  orientation: SliderOrientation;
  reverseDirection: boolean;
  minLabel: string;
  maxLabel: string;
  showLabels: boolean;
  showTickMarks: boolean;
  majorTickInterval: string;
  minorTickInterval: string;
  showMajorTickValues: boolean;
  tickLabels: TickLabelRow[];
  unitSuffix: string;
  showLiveReadout: boolean;
  gradingMode: GradingMode;
  feedback: { states: FeedbackState[] };
}

const ONE_SIDED: ConditionType[] = ['lt', 'lte', 'gt', 'gte'];

// Readability thresholds for tick density — well below the hard-crash safety cap in
// @agavi/blocks' computeTickPositions, these exist purely so a teacher gets an inline warning
// before shipping a slider whose ticks (and, for majors, their auto-rendered values) would just
// be an unreadable smear. Majors carry text labels, so they saturate readability much sooner
// than the plain unlabeled minor tick marks.
const MAX_READABLE_MINOR_TICKS = 200;

const CONDITION_TYPES: { id: ConditionType; symbol: string }[] = [
  { id: 'exact', symbol: '=' },
  { id: 'lt', symbol: '<' },
  { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' },
  { id: 'gte', symbol: '≥' },
  { id: 'between', symbol: '↔' },
];

// The condition-type selector's visible label is a bare math symbol (e.g. "<") — fine to read
// visually, but a screen reader announces the character name ("less-than sign") rather than its
// meaning. These give each option a real accessible name instead, matching the equivalent control
// in numerical-block.tsx and this file's own SliderTriggerEditor (which already does this).
const CONDITION_ARIA_KEYS: Record<ConditionType, string> = {
  exact: 'conditionExact', lt: 'conditionLt', lte: 'conditionLte', gt: 'conditionGt', gte: 'conditionGte', between: 'conditionBetween',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Every name a SliderTrack needs, in one derivation for all five call sites.
 *
 * The five were passing the same six props by hand and had drifted: the nudge buttons' names were
 * built by gluing a handle word onto a direction ("Slider 1 Decrease by one step" — two fragments
 * with a capital in the middle), and the handle itself was named "Slider", which a reader then
 * announces after the role it already said, as "Slider, slider, 5".
 *
 * The names now follow the student-label convention in `docs/accessible-names.md`: the name says
 * what the role cannot, and never repeats it. So a handle is a "Slider handle" — the role supplies
 * "slider", the name supplies which part of it you are holding — and "1 of 2" comes with it,
 * because knowing there is a second handle is the thing a listener cannot see.
 *
 * `handleLabel1/2` are the exception and stay short: they are VISIBLE text beside the nudge pair,
 * on an `aria-hidden` span, so they are read with the eyes and never spoken.
 */
function sliderTrackLabels(t: (key: string, opts?: Record<string, unknown>) => string, dual: boolean) {
  return {
    ariaLabel: dual ? t('blocks.slider.handleAria', { handle: 1, total: 2 }) : t('blocks.slider.handleAriaSingle'),
    ariaLabelB: t('blocks.slider.handleAria', { handle: 2, total: 2 }),
    ariaLabelNudgeDown: dual ? t('blocks.slider.nudgeDownHandle', { handle: 1 }) : t('blocks.slider.nudgeDown'),
    ariaLabelNudgeUp: dual ? t('blocks.slider.nudgeUpHandle', { handle: 1 }) : t('blocks.slider.nudgeUp'),
    ariaLabelNudgeDownB: t('blocks.slider.nudgeDownHandle', { handle: 2 }),
    ariaLabelNudgeUpB: t('blocks.slider.nudgeUpHandle', { handle: 2 }),
    handleLabel1: t('blocks.slider.handle1'),
    handleLabel2: t('blocks.slider.handle2'),
  };
}

function num(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Whether a numeric field has actually been filled in.
 *
 * `num()` coerces a blank field to 0, which is right for rendering (a track has to have SOME
 * position) and wrong for validation: an untouched answer box became a real answer of zero, and got
 * reported as "an answer sits outside the slider range" on a scale that starts above zero. Nothing
 * had been authored at all. Validation must ask this first, never infer emptiness from a value.
 */
function hasNum(s: string): boolean {
  return s.trim() !== '' && Number.isFinite(parseFloat(s));
}

/*
 * Log mode's resolution, shown to teachers as SIGNIFICANT FIGURES rather than the "steps per
 * decade" count it is stored as (see quantizeLogStep in packages/blocks — mantissaStep = 9 /
 * stepsPerDecade). Nobody should have to derive 9, 90, 900 by hand, least of all past the
 * round-number guesses of 10/100/1000.
 *
 * It was labelled "Decimal Places", and that was actively misleading, because decimal places are
 * the one thing this setting does NOT hold constant. Walk a setting across the decades:
 *
 *   1 sig fig    1, 2, 3      10, 20, 30      100, 200, 300     step x10 each decade
 *   2 sig figs   1, 1.1, 1.2  10, 11, 12      100, 110, 120     step x10 each decade
 *   3 sig figs   1, 1.01      10, 10.1        100, 101          step x10 each decade
 *
 * The decimal places change every decade; the DIGIT COUNT is what stays put. So "1 decimal place"
 * read as a promise of tenths and delivered tenths, then units, then tens — the surprise a teacher
 * hits at 100. "2 significant figures" describes the same lattice and predicts it correctly.
 *
 * It is also the vocabulary this product already uses (numerical's `sigFigsRequired`/`sigFigsCount`)
 * and the vocabulary its audience teaches. Purely a display conversion: `logStepsPerDecade` is
 * untouched on disk, so nothing needs migrating — only the offset by one, since 9 steps per decade
 * is 1 significant figure rather than 0 decimal places.
 */
function logSigFigsFromStepsPerDecade(raw: string): string {
  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) return '1';
  return String(Math.max(1, Math.round(Math.log10(n / 9)) + 1));
}
function stepsPerDecadeFromLogSigFigs(figs: string): string {
  const f = parseInt(figs, 10);
  if (!Number.isFinite(f) || f < 1) return '9';
  return String(9 * Math.pow(10, f - 1));
}

// A handle's answer setup counts as incomplete if it has no plausible correct answer at all —
// either nothing has been entered yet, or everything that has been entered falls entirely outside
// the slider's own [min, max] (a value the slider can never actually reach is functionally the
// same as no answer: the question could never be marked correct). Used to surface a single
// coarse "Answer setup incomplete" signal on the block's bottom bar; the finer-grained per-field
// warnings live in the editor itself (AnswerSetupFields).

// ─── Standardized issue reporting ──────────────────────────────────────────────
/**
 * Every defect this block can detect, in one list — the collapsed card's bar, the editor's warning
 * bar and the test bar's notices all read it, so they cannot disagree about what is wrong.
 *
 * This replaces "Pass A of the bulk standardization", which deliberately aggregated only what the
 * block already displayed (2 real keys, the fewest in the codebase) and deferred every new check to
 * this audit. The two helpers it named — `isSliderValueFeedbackFullySubsumed` and
 * `isSliderValueDefaultIncorrectUnreachable` — are surfaced here at last; Flow's reachability check
 * has trusted them all along.
 *
 * The slider-specific hazards below all share one property: each produces a block that LOOKS
 * authored and cannot be answered correctly. A log scale starting at zero is undefined, an answer
 * outside the track is unreachable, and a tolerance spanning the whole track accepts everything.
 * None of them was detected before.
 */
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedSliderData(
  rawD: SliderBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): SliderBlockData {
  const rawVersioned = applySliderVersion(rawD, versionId);
  return { ...rawVersioned, feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode) };
}

/**
 * How an issue addresses one of slider's own controls.
 *
 * Slider carried no `BlockIssue.fields` at all, so every one of its ~26 issues rendered as inert
 * text rather than a button (`IssueRow` deliberately draws a plain div when there is nothing to
 * point at). A teacher read "Slider 1 must have a lower correct value than Slider 2" and then went
 * hunting for the two boxes themselves, while the same message on keyword/dropdown/mcq/ordering/
 * fitb scrolls to and focuses the offending field.
 *
 * Literal ids for the singleton settings; the per-answer rows use the answer's own id, the way
 * every option/word row already does. `min` and `max` each render twice (a linear branch and a log
 * one) but only one is mounted at a time, so one id per concept stays unambiguous.
 */
/**
 * The field ids for slider's settings — and they are the DATA KEYS, deliberately.
 *
 * `data-field-id` started as the issues system's vocabulary, where any unique string would do, and
 * these were spelled `slider-min`, `slider-step` and so on. Undo now shares that vocabulary: it
 * diffs an op's before and after, gets back the key that changed, and looks for a control tagged
 * with it. A tag that is not the key is therefore invisible to undo while looking, at the call
 * site, exactly like one that works — which is what had happened here. Every one of these controls
 * was tagged, and not one of them could be found.
 *
 * `altText` was the same mistake at a smaller scale (`'alt-text'` beside an already-correct
 * `'transcript'`). Renaming costs nothing on the issues side: it matches on whatever string it is
 * given, and `slider-issue-fields.test.ts` reads these through `Object.values`.
 */
export const SLIDER_FIELD_IDS = {
  min: 'min',
  max: 'max',
  step: 'stepSize',
  logSigFigs: 'logStepsPerDecade',
  majorTick: 'majorTickInterval',
  minorTick: 'minorTickInterval',
  posA: 'defaultPosition',
  posB: 'handleB.defaultPosition',
} as const;

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `sliderIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function sliderBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return sliderIssues(buildNormalizedSliderData(initData(block.data), ctx.t), ctx);
}

export function sliderIssues(d: SliderBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  /* DERIVED HERE rather than handed in: both call sites computed the identical expression from the
     identical inputs, which is two derivations of one fact kept in step by hand — and the board
     would have been a third. */
  const resolve = ctx.resolve ?? ((str: string, fieldId = '') =>
    evaluateFormulaRaw(str, {}, angleModeFor(d, fieldId)));
  const setupIncomplete = isAnswerSetupIncomplete(d, num(d.min), num(d.max), resolve)
    || (!!d.dualHandle && isAnswerSetupIncomplete(d.handleB, num(d.min), num(d.max), resolve));
  const issues: BlockIssue[] = [];
  const answerSetup = t('blocks.areas.answerSetup');
  const feedbackCat = t('blocks.areas.feedback');

  /**
   * §4.4 — a row copied by the projection is "copied, not decided". The box shows a count
   * beside the pager for the row you are on; this is the half that reaches the publication
   * gate, so a teacher who never pages to scenario 5 still learns it was never reviewed.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: answerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  // Every other block has this; slider was passed the question and never looked at it.
  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true,
      category: t('blocks.areas.question'), detail: t('blocks.issues.questionBlank') });
  }
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  if (d.gradingMode === 'graded' && setupIncomplete) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', supersededBy: ['range-inverted', 'log-domain', 'step-invalid', 'step-too-large', 'log-step-invalid', 'tick-interval-invalid', 'minor-tick-order', 'major-tick-dense', 'minor-tick-dense', 'tick-label-outside', 'handle-order', 'default-position-outside', 'default-position-b-outside',
      // Three variants, not a stale key: `n` is '' in single-handle mode and '1'/'2' when dual.
      'answer-outside-track', 'answer-outside-track1', 'answer-outside-track2'], category: answerSetup, detail: t('blocks.slider.barSetupIncomplete') });
  }

  const minN = num(d.min), maxN = num(d.max), stepN = num(d.stepSize);
  // A field that has not been filled in is not a defect — it is a field that has not been filled in.
  // Every check below asks hasNum() first, because num() turns a blank box into a real 0 and a
  // blank slider would otherwise report a range of 0-0 with an answer sitting outside it.
  const rangeGiven = hasNum(d.min) && hasNum(d.max);
  const rangeSane = rangeGiven && minN < maxN;

  if (rangeGiven && minN >= maxN) {
    issues.push({ key: 'range-inverted', fields: [SLIDER_FIELD_IDS.min, SLIDER_FIELD_IDS.max], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueRangeInverted') });
  }
  if (d.scaleMode === 'log' && hasNum(d.min) && minN <= 0) {
    // A log axis through zero has no defined mapping — logStepValue and valueToFraction both need
    // log(min). Silently produced a track a student could not use.
    issues.push({ key: 'log-domain', fields: [SLIDER_FIELD_IDS.min], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueLogDomain') });
  }
  if (d.scaleMode === 'linear' && hasNum(d.stepSize) && stepN <= 0) {
    issues.push({ key: 'step-invalid', fields: [SLIDER_FIELD_IDS.step], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueStepInvalid') });
  }
  // Only meaningful once the range itself makes sense: against an INVERTED range (max - min < 0)
  // every positive step is "larger than the range", which is how a step of 1 got flagged on a
  // 0-to-4.67e9 track whose bounds were the wrong way round. A cascade like that buries the one
  // error the teacher can act on.
  if (rangeSane && d.scaleMode === 'linear' && hasNum(d.stepSize) && stepN > 0 && stepN > (maxN - minN)) {
    issues.push({ key: 'step-too-large', fields: [SLIDER_FIELD_IDS.step], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueStepTooLarge') });
  }
  if (d.scaleMode === 'log' && hasNum(d.logStepsPerDecade) && num(d.logStepsPerDecade) <= 0) {
    issues.push({ key: 'log-step-invalid', fields: [SLIDER_FIELD_IDS.logSigFigs], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueLogStepInvalid') });
  }
  if (d.showTickMarks && d.scaleMode === 'linear' && hasNum(d.majorTickInterval) && num(d.majorTickInterval) <= 0) {
    issues.push({ key: 'tick-interval-invalid', fields: [SLIDER_FIELD_IDS.majorTick], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueTickIntervalInvalid') });
  }

  // One value each variable can genuinely take — the smallest, arbitrarily, since any member of the
  // domain answers "can this expression resolve at all?".
  const probeValues = probeRoll(variables);

  // Per-handle answer reachability. An answer the track cannot reach is the failure most likely to
  // survive authoring unnoticed, because nothing about the editor looks wrong.
  const handles: Array<[SliderHandleId, string]> = d.dualHandle ? [['a', '1'], ['b', '2']] : [['a', '']];
  for (const [handle, n] of handles) {
    if (!rangeSane) break;
    const src = handle === 'b' ? d.handleB : d;
    /** This handle's answer ROWS, as the issue panel addresses them. Every check in this loop is
     *  about that handle's answers collectively (an unreachable band, a value off the track), so it
     *  names all of them and `focusIssueField` takes the first one that is mounted. */
    const answerFields = src.conditionType === 'between'
      ? src.betweenAnswers.map(a => a.id)
      : src.answers.map(a => a.id);
    const positions: string[] = src.conditionType === 'exact'
      ? src.answers.map(a => a.value)
      : src.conditionType === 'between'
        ? src.betweenAnswers.flatMap(a => [a.low, a.high])
        : [src.threshold];
    /** The same positions WITH their ids, so a validity check reads each field's own mode. */
    const positionRows: { value: string; fieldId: string }[] = src.conditionType === 'exact'
      ? src.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
      : src.conditionType === 'between'
        ? src.betweenAnswers.flatMap(a => [
          { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
        : [{ value: src.threshold, fieldId: 'threshold' }];
    const authored = positions.filter(pos => pos.trim() !== '');
    const filled = positions.filter(hasNum).map(num);

    // A formula that cannot resolve grades as nothing at all, so say so before anything downstream
    // reasons about a NaN. Catches a typo, a deleted variable, and bad syntax alike.
    //
    // Judged against a PROBE roll built from the variables' own domains, never against `resolve`.
    // `resolve` is bound to the current roll, and on the unselected canvas card there is no roll at
    // all — so every formula answer would resolve to NaN and be reported broken purely for being
    // looked at from the wrong surface. The probe asks the real question: can this expression
    // resolve for a value the variable can actually take?
    if (positionRows.some(r => r.value.trim() !== '' && !hasNum(r.value)
      && !Number.isFinite(evaluateFormulaRaw(r.value, probeValues, angleModeFor(d, r.fieldId))))) {
      issues.push({ key: `answer-formula-invalid${n}`, severity: 'error', kind: 'defect', category: answerSetup,
        detail: t(d.dualHandle ? 'blocks.slider.issueAnswerFormulaInvalidHandle' : 'blocks.answers.issueAnswerFormulaInvalid', { n }) });
    }

    // Reachability is a claim about EVERY roll, not the one on screen. `hasNum` is false for
    // "{n}*5", so the plain-number path below silently skipped formula answers entirely — they were
    // never range-checked at all. Extrema closes that: tolerance is deliberately omitted from the
    // config (so the fields default to 0) because this asks whether the ANSWER can leave the track,
    // which is a different question from whether its tolerance BAND overhangs the end.
    const extremaCfg = {
      conditionType: src.conditionType,
      answers: src.answers.map(a => ({ value: a.value })),
      betweenAnswers: src.betweenAnswers.map(a => ({ low: a.low, high: a.high })),
      threshold: src.threshold,
      toleranceMode: 'absolute' as const,
      toleranceSymmetry: 'symmetric' as const,
      angleMode: 'radians' as const,
    };
    const answerExtrema = computeFormulaExtrema(extremaCfg, variables);
    // No extrema (all plain numbers, or a domain too large to enumerate) falls back to the literal
    // values. When formulas ARE present and extrema is unavailable, `filled` is empty and no claim
    // is made — the same "never wrongly accuse" policy the NaN guards use.
    const outsideTrack = answerExtrema
      ? (answerExtrema.lo < minN || answerExtrema.hi > maxN)
      : filled.some(v => v < minN || v > maxN);
    if (outsideTrack) {
      issues.push({ key: `answer-outside-track${n}`, fields: answerFields, severity: 'error', kind: 'defect', category: answerSetup,
        detail: t(d.dualHandle ? 'blocks.slider.issueAnswerOutsideTrackHandle' : 'blocks.slider.issueAnswerOutsideTrack', { n }) });
    }
    // A band covering the whole track means every position is correct — authored as grading, but
    // grading nothing. Only worth saying once an answer exists to widen.
    if (authored.length > 0) {
      const ranges = computeSliderAcceptedRanges(handleRangeConfigFrom(d, handle, resolve));
      if (ranges.some(r => r.lo <= minN && r.hi >= maxN)) {
        issues.push({ key: `tolerance-covers-track${n}`, fields: answerFields, severity: 'warning', kind: 'defect', category: answerSetup,
          detail: t(d.dualHandle ? 'blocks.slider.issueToleranceCoversTrackHandle' : 'blocks.slider.issueToleranceCoversTrack', { n }) });
      }
    }

    /*
     * The band exists, sits on the track, and still no student can land in it.
     *
     * A stepped slider only stops on min, min+step, min+2*step… so a tolerance narrower than the gap
     * between two of those can fall cleanly between them: 66.5 +/- 5% on a 0-100 track with step 10
     * accepts [63.175, 69.825], and the nearest positions are 60 and 70. The answer is not wrong,
     * not off the track, and not too wide. It is unanswerable, and until now nothing in the block
     * said so — `hasReachableStepInRange` was consulted only by display code, which responded by
     * printing a substitute number.
     *
     * numerical's `sigfig-unsatisfiable` is the same statement about a different lattice, and this
     * is deliberately its twin: same severity, same kind, same "only once something is authored".
     *
     * QUANTIFIED BY ENUMERATION, not by extrema, and that is the whole reason `forEachRoll` appears
     * in this file. Extrema answers `answer-outside-track` above because leaving the track is
     * MONOTONE in the answer value — push the answer far enough either way and it leaves, so the
     * envelope decides it. Step-reachability is not monotone: slide a formula answer across its
     * domain and whether a step falls inside its band flickers on and off. An envelope cannot see
     * that and one probe roll samples it once. Only "no roll at all admits a step" is worth
     * reporting — a setup that works for some students is a softer statement, and saying it here
     * would fire on almost every band narrower than a step.
     */
    /*
     * LOG mode has its own lattice, and asking the linear question about it is not an
     * approximation — it is a different slider. A log track from 1 to 100 at 9 steps per decade
     * stops on 1..9, 10..90, 100; `stepSize` plays no part. The first version of this check used
     * `stepSize` regardless and, on the block that exposed it, reached the right verdict from the
     * wrong lattice — which is the worst way for a wrong method to behave, because it looks fine.
     */
    const isLog = d.scaleMode === 'log';
    const logSteps = num(d.logStepsPerDecade);
    const latticeExists = isLog ? logSteps > 0 && minN > 0 : stepN > 0;
    if (authored.length > 0 && rangeSane && latticeExists) {
      const admits = (vals: Record<string, string>) => {
        const at = (v: string, fieldId = '') =>
          (v.trim() === '' ? NaN : evaluateFormulaRaw(v, vals, angleModeFor(d, fieldId)));
        const ranges = computeSliderAcceptedRanges(handleRangeConfigFrom(d, handle, at));
        // Nothing resolvable, or an unbounded one-sided condition: a different issue owns each of
        // those, and stacking this on top sends the teacher after the wrong thing.
        if (ranges.length === 0) return true;
        if (!ranges.every(r => Number.isFinite(r.lo) && Number.isFinite(r.hi))) return true;
        return ranges.some(r => isLog
          ? hasReachableLogStepInRange(r.lo, r.hi, minN, maxN, logSteps)
          : hasReachableStepInRange(r.lo, r.hi, minN, maxN, stepN));
      };
      const names = chipNamesIn(positions);
      const unreachable = names.length === 0
        ? !admits(probeValues)
        : (() => {
            let everAdmits = false;
            const swept = forEachRoll(names, variables, vals => {
              if (admits(vals)) { everAdmits = true; return 'stop'; }
              return undefined;
            }, OVERLAP_ENUMERATION_MAX_COMBOS);
            // A domain too large to enumerate makes no claim, same policy as the extrema fallback.
            return swept && !everAdmits;
          })();
      if (unreachable) {
        issues.push({ key: `answer-unreachable-step${n}`, fields: answerFields, severity: 'error', kind: 'defect', category: answerSetup,
          detail: t(d.dualHandle ? 'blocks.slider.issueAnswerUnreachableStepHandle' : 'blocks.slider.issueAnswerUnreachableStep', { n }) });
      }
    }
  }

  /*
   * Two OR'd answers on one handle that accept some of the same positions. See numerical's own note
   * — same check, same shared `overlappingAnswerPairs`, third and fourth callers of four.
   */
  for (const [handle, n] of handles) {
    if (!rangeSane) break;
    const src = handle === 'b' ? d.handleB : d;
    if (src.conditionType !== 'exact' && src.conditionType !== 'between') continue;
    const list = src.conditionType === 'exact' ? src.answers : src.betweenAnswers;
    if (list.length < 2) continue;
    const regions = list.map((a, i) => {
      const cfg = handleRangeConfigFrom(d, handle, resolve);
      const ranges = computeSliderAcceptedRanges(src.conditionType === 'exact'
        ? { ...cfg, answers: [cfg.answers[i]!], betweenAnswers: [] }
        : { ...cfg, answers: [], betweenAnswers: [cfg.betweenAnswers[i]!] });
      return { id: a.id, intervals: ranges.filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi)) };
    });
    if (overlappingAnswerPairs(regions).length > 0) {
      issues.push({ key: `answer-overlap${n}`, severity: 'warning', kind: 'defect', category: answerSetup,
        detail: t('blocks.answers.issueAnswerOverlap') });
    }
  }

  // An inverted between pair (low above high) accepts nothing at all. This had ONLY an inline
  // message, so removing that without adding this would have deleted the block's only warning for
  // it — unlike the out-of-range messages, each of which duplicated an existing issue.
  for (const [handle, n] of handles) {
    const src = handle === 'b' ? d.handleB : d;
    if (src.conditionType !== 'between') continue;
    const inverted = src.betweenAnswers.some(a =>
      a.low.trim() !== '' && a.high.trim() !== ''
      && !hasVariableChip(a.low) && !hasVariableChip(a.high)
      && resolve(a.low, a.id) > resolve(a.high, a.id));
    if (inverted) {
      issues.push({ key: `between-inverted${n}`, severity: 'error', kind: 'defect', category: answerSetup,
        detail: t('blocks.conditions.betweenInverted') });
    }
    // A row with exactly one bound filled is DROPPED by the evaluator, so it grades as though it
    // were not there. See `incompleteBetweenRowIds`.
    const halfRows = incompleteBetweenRowIds(src.betweenAnswers);
    if (halfRows.length > 0) {
      issues.push({ key: `between-incomplete${n}`, fields: halfRows, severity: 'error', kind: 'incompletion',
        category: answerSetup, detail: t('blocks.answers.issueBetweenIncomplete') });
    }
    // The formula case, which the literal check above deliberately skips (`!hasVariableChip`) — so
    // until now a `between` row with equation bounds was never checked for inversion at all.
    const vRows = src.betweenAnswers.filter(a => hasVariableChip(a.low) || hasVariableChip(a.high));
    const vVerdict = betweenInversionVerdict(vRows, variables, 'radians');
    if (vVerdict === 'always') {
      issues.push({ key: `between-inverted-formula${n}`, severity: 'error', kind: 'defect', category: answerSetup,
        detail: t('blocks.conditions.betweenInverted') });
    } else if (vVerdict === 'sometimes') {
      issues.push({ key: `between-may-invert${n}`, severity: 'warning', kind: 'defect', category: answerSetup,
        detail: t('blocks.conditions.betweenMayInvert') });
    } else if (vVerdict === 'unknown') {
      issues.push({ key: 'answers-unverifiable', severity: 'warning', kind: 'unverified', category: answerSetup,
        detail: t('blocks.answers.issueTooWideToVerify') });
    }
  }

  if (d.dualHandle) {
    const posA = num(d.defaultPosition), posB = num(d.handleB.defaultPosition);
    if (Number.isFinite(posA) && Number.isFinite(posB) && posA > posB) {
      issues.push({ key: 'handle-order', fields: [SLIDER_FIELD_IDS.posA, SLIDER_FIELD_IDS.posB], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.validationHandleOrder') });
    }

    /*
     * The two answers must be satisfiable AT THE SAME TIME.
     *
     * The handles cannot cross — slider 1 is always at or below slider 2, enforced on every drag by
     * `clampForHandle` — so a pair of answers is only gradeable if some accepted value for 1 is at
     * or below some accepted value for 2. `283 ± 5%` on slider 1 with `1` on slider 2 asks for a
     * state the control physically cannot enter, and every submission grades incorrect forever.
     *
     * Nothing caught it. `handle-order` looks like this check and is not: it compares the STARTING
     * POSITIONS, which are where the handles begin, not what they must end up on. A block could
     * start 10/100 (fine) and demand 283/1 (impossible) and report no problem at all — which is
     * exactly the block that surfaced this.
     *
     * Compared as min-of-1 against max-of-2, the loosest reading: it fires only when EVERY accepted
     * value for slider 1 sits above EVERY accepted value for slider 2, so overlapping bands and
     * touching bands both pass.
     */
    // BOTH handles' answer rows: neither value is wrong on its own, it is the pair that cannot be
    // satisfied, which is also why the editor rings both.
    const crossedFields = [
      ...(d.conditionType === 'between' ? d.betweenAnswers : d.answers).map(a => a.id),
      ...(d.handleB.conditionType === 'between' ? d.handleB.betweenAnswers : d.handleB.answers).map(a => a.id),
    ];
    /*
     * THREE verdicts, because the honest answer depends on the rolls:
     *
     *   always     no student can ever be right      -> error, the original wording
     *   sometimes  most rolls work, some cannot be    -> warning, and it says "can" not "must"
     *   unknown    the variable space was too wide    -> reported, not passed over in silence
     *
     * The old check answered from ONE roll and asserted `always` regardless, which is how a block
     * whose sliders were correctly ordered on the scenario in front of the teacher still claimed
     * they were crossed.
     */
    const crossed = sliderAnswersCrossedVerdict(d, variables);
    if (crossed === 'always') {
      issues.push({ key: 'handle-answers-impossible', fields: crossedFields, severity: 'error', kind: 'defect', category: answerSetup,
        detail: t('blocks.slider.issueHandleAnswersImpossible') });
    } else if (crossed === 'sometimes') {
      issues.push({ key: 'handle-answers-may-cross', fields: crossedFields, severity: 'warning', kind: 'defect', category: answerSetup,
        detail: t('blocks.slider.issueHandleAnswersMayCross') });
    } else if (crossed === 'unknown') {
      issues.push({ key: 'answers-unverifiable', fields: crossedFields, severity: 'warning', kind: 'unverified', category: answerSetup,
        detail: t('blocks.answers.issueTooWideToVerify') });
    }
  }

  const defPos = num(d.defaultPosition);
  if (rangeSane && hasNum(d.defaultPosition) && (defPos < minN || defPos > maxN)) {
    issues.push({ key: 'default-position-outside', fields: [SLIDER_FIELD_IDS.posA], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueDefaultPositionOutside') });
  }
  // Handle B was checked ONLY by an inline message, so its starting position could sit off the
  // track with nothing reported anywhere the teacher would see after closing the block.
  const defPosB = num(d.handleB.defaultPosition);
  if (d.dualHandle && rangeSane && hasNum(d.handleB.defaultPosition) && (defPosB < minN || defPosB > maxN)) {
    issues.push({ key: 'default-position-b-outside', fields: [SLIDER_FIELD_IDS.posB], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueDefaultPositionBOutside') });
  }

  // ── Tick marks ──
  // All three were inline-only. Each is a contradiction between two authored numbers (or between an
  // interval and the range it has to divide), never an absence, so all three are defects.
  if (d.showTickMarks && d.scaleMode === 'linear') {
    const majorN = num(d.majorTickInterval), minorN = num(d.minorTickInterval);
    const majorGiven = hasNum(d.majorTickInterval), minorGiven = hasNum(d.minorTickInterval);
    const minorNotSmaller = majorGiven && minorGiven && minorN > 0 && majorN > 0 && minorN >= majorN;
    if (minorNotSmaller) {
      issues.push({ key: 'minor-tick-order', fields: [SLIDER_FIELD_IDS.minorTick], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueMinorTickOrder') });
    }
    // Suppressed while the order itself is wrong — an inverted pair makes both counts meaningless,
    // the same cascade guard `step-too-large` already uses against an inverted range.
    if (!minorNotSmaller && rangeSane) {
      if (majorGiven && sliderTickCount(minN, maxN, majorN) > MAX_READABLE_MAJOR_TICKS) {
        issues.push({ key: 'major-tick-dense', fields: [SLIDER_FIELD_IDS.majorTick], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueMajorTickDense') });
      }
      if (minorGiven && sliderTickCount(minN, maxN, minorN) > MAX_READABLE_MINOR_TICKS) {
        issues.push({ key: 'minor-tick-dense', fields: [SLIDER_FIELD_IDS.minorTick], severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueMinorTickDense') });
      }
    }
  }
  /*
   * There is deliberately NO "resolution too fine" warning here.
   *
   * One existed. It was written when a lattice finer than the track meant values a pointer could
   * not select at all — 27,000 stops on a 218px track leaves about four in five unreachable by
   * dragging, and which four is an artifact of pixel rounding. That was a real defect and worth
   * saying.
   *
   * The nudge pair removed the premise. Every stop is now reachable, on every configuration
   * including dual-handle, and hold-to-repeat means correcting a drag that landed sixty stops away
   * is a few seconds of holding rather than sixty taps. A warning that says "too many steps to
   * select by dragging" while the block ships a control built precisely for that is telling the
   * teacher off for using a feature.
   *
   * What survives is the check that still describes a defect: `answer-unreachable-step` fires when
   * NO stop lands inside the accepted band, which is unanswerable however patient the student is,
   * and is independent of how fine the lattice happens to be.
   */

  // A tick label pinned to a value the track cannot reach never renders — authored and invisible,
  // with no report anywhere once the inline message was the only witness.
  if (rangeSane && d.tickLabels.some(tl => {
    const v = num(tl.value);
    return hasNum(tl.value) && (v < minN || v > maxN);
  })) {
    issues.push({ key: 'tick-label-outside', severity: 'error', kind: 'defect', category: answerSetup, detail: t('blocks.slider.issueTickLabelOutside') });
  }

  // ── Feedback ──
  if (hasEmptyFeedbackContent(d.feedback.states)) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: feedbackCat, detail: t('blocks.feedbackPanel.missingText') });
  }

  /**
   * What is wrong with each targeted condition — a question this block was not asking at all.
   *
   * Slider validates its own setup thoroughly (log domains, tick density, step size, handle order:
   * twenty-one issue keys) and then said nothing whatever about the CONDITIONS its feedback fires
   * on, despite every one of those inputs accepting a formula. `{n}/0` in a slider trigger produced
   * NaN for every student and was reported by no one, exactly as it was in numerical until a probe
   * roll made the question answerable.
   *
   * `allowWrap` is false: a slider is a line, and a low bound above a high bound accepts nothing.
   * Compass passes true, because there a range through north is the ordinary case.
   */
  const triggerFaults = d.feedback.states
    .filter(st => st.type === 'targeted')
    .map(st => triggerFault(st.trigger, {
      resolve, declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(probeValues)), allowWrap: false,
    }))
    .filter((f): f is TriggerFault => f !== null);

  if (triggerFaults.includes('unresolvable')) {
    issues.push({ key: 'trigger-unresolvable', severity: 'error', kind: 'defect', category: feedbackCat, detail: t('blocks.conditions.issueUnresolvable') });
  }
  if (triggerFaults.includes('no-value')) {
    issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: feedbackCat, detail: t('blocks.conditions.issueNoValue') });
  }
  if (triggerFaults.includes('range-incomplete')) {
    issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: feedbackCat, detail: t('blocks.conditions.issueRangeIncomplete') });
  }
  if (triggerFaults.includes('range-inverted')) {
    issues.push({ key: 'trigger-range-inverted', severity: 'error', kind: 'defect', category: feedbackCat, detail: t('blocks.conditions.betweenInverted') });
  }
  // The formula case, quantified — see angle's note for why this is a warning even when it always
  // inverts, and why the `Math.min/max` swap in the evaluators is deliberately left alone.
  {
    const trigVerdict = triggerRangeInversionVerdict(
      d.feedback.states.filter(st => st.type === 'targeted').map(st => st.trigger), variables, 'radians');
    if (trigVerdict === 'always' || trigVerdict === 'sometimes') {
      issues.push({ key: 'trigger-range-may-invert', severity: 'warning', kind: 'defect', category: feedbackCat,
        detail: t('blocks.conditions.betweenMayInvert') });
    }
  }

  /*
   * Two targeted bands on ONE handle that accept some of the same positions.
   *
   * This counted identical trigger STRINGS, so it reported two cards reading `a:range:10:30` and
   * said nothing about `a:range:10:30` beside `a:range:20:40` — the case a teacher actually
   * creates. Every other value block had a real finder; slider was the one left out, and on a
   * dual-handle block it has to group by handle or it reports two conditions about two different
   * inputs as a collision. See `findSliderOverlappingFeedbackPairs`.
   */
  const targeted = d.feedback.states.filter(st => st.type === 'targeted');
  const triggers = targeted.map(st => st.trigger);
  // Identical triggers, which the band comparison below cannot see: `exact` is excluded from it on
  // purpose, and `set_answer` carries no band at all, so two cards both pointing at the answer
  // would otherwise go unreported. This half is the check that was already here; it was the ONLY
  // half, which is the bug.
  const duplicated = new Set(triggers).size !== triggers.length;
  /*
   * A targeted condition that lands on the AUTHORED BAND collides with the Set Answer card.
   *
   * The band comparison above cannot see this: `set_answer` carries no band of its own to
   * compare, and `exact` triggers are excluded from it anyway. But a Set Answer card IS the band,
   * so a card reading "Slider 1: = 10" on an answer of 10 fires at exactly the same moment — and
   * unlike Correct, Set Answer is NOT terminal, so both fire and the student gets two cards.
   *
   * Only where Set Answer can actually fire. In graded the same collision is already reported,
   * and reported better: there Correct wins first and is terminal, so the targeted card is dead
   * rather than duplicated, which is what `feedback-subsumed` says below.
   *
   * `overlaps` OR `subsumed`, because they answer different halves and neither alone is "shares
   * ground with the band": a condition sitting wholly inside it reports subsumed and NOT
   * overlaps.
   */
  const setAnswerLive = visibleTargeted(d.feedback.states, d.gradingMode)
    .some(st => st.trigger === SET_ANSWER_TRIGGER);
  const collidesWithSetAnswer = setAnswerLive && targeted.some(st => {
    if (st.trigger === SET_ANSWER_TRIGGER) return false;
    const { handle } = parseSliderHandleTrigger(st.trigger);
    if (handle === 'b' && !d.dualHandle) return false;   // orphaned; reported on its own
    const band = computeSliderAcceptedRanges(handleRangeConfigFrom(d, handle, resolve));
    return doesSliderValueFeedbackOverlapAccepted(st.trigger, band)
      || isSliderValueFeedbackFullySubsumed(st.trigger, band);
  });
  if (duplicated || collidesWithSetAnswer || findSliderOverlappingFeedbackPairs(triggers).length > 0) {
    issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: feedbackCat, detail: t('blocks.slider.feedbackOverlapWarning') });
  }

  /**
   * Subsumed / overlapping Correct — quantified over the roll space rather than judged at the probe.
   *
   * Slider resolves against a deterministic probe, which is better than numerical's old random
   * sample and still one point on a band that moves: an answer of `{n}*10` slides the accepted
   * window across the track, so a condition can be swallowed for some rolls and live for others,
   * and one point cannot tell those apart.
   *
   * Per HANDLE, because that is what a slider's band is. A `b:` condition on a single-handle slider
   * is already reported as orphaned and is skipped here rather than judged against a band that does
   * not exist.
   */
  const overlap = analyseOverlapAcrossRolls({
    triggers: targeted.map(st => st.trigger),
    variables,
    names: chipNamesIn([
      ...[d, d.handleB].flatMap(src => src ? [
        ...src.answers.map(a => a.value),
        ...src.betweenAnswers.flatMap(a => [a.low, a.high]),
        src.threshold,
      ] : []),
      ...targeted.map(st => st.trigger),
    ]),
    atRoll: vals => {
      const resolveAt = (v: string, fieldId = '') => evaluateFormulaRaw(v, vals, angleModeFor(d, fieldId));
      const bandFor = (handle: SliderHandleId) =>
        computeSliderAcceptedRanges(handleRangeConfigFrom(d, handle, resolveAt));
      const live = (trigger: string) => {
        const { handle } = parseSliderHandleTrigger(trigger);
        return handle === 'b' && !d.dualHandle ? null : bandFor(handle);
      };
      return {
        subsumed: trig => { const b = live(trig); return !!b && isSliderValueFeedbackFullySubsumed(trig, b); },
        overlaps: trig => { const b = live(trig); return !!b && doesSliderValueFeedbackOverlapAccepted(trig, b); },
      };
    },
  });
  // Graded only. Every verdict here is about the ACCEPTED BAND, and in a participation mode
  // there is no band in force — correct is never checked, so nothing is subsumed by it and
  // nothing overlaps it. Reporting these outside graded would warn about a condition the
  // evaluator will happily fire.
  if (d.gradingMode === 'graded' && overlap.any.alwaysSubsumed) {
    issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: feedbackCat, detail: t('blocks.slider.feedbackSubsumedWarning') });
  }
  if (d.gradingMode === 'graded' && overlap.any.alwaysOverlaps) {
    issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: feedbackCat, detail: t('blocks.slider.feedbackOverlapsCorrectWarning') });
  }
  // The collision a teacher cannot see, because the roll in front of them does not contain it.
  if (d.gradingMode === 'graded' && overlap.any.mayCollide) {
    issues.push({ key: 'feedback-may-overlap-correct', severity: 'warning', kind: 'defect',
      supersededBy: ['feedback-overlaps-correct', 'feedback-subsumed'],
      category: feedbackCat, detail: t('blocks.conditions.mayOverlapCorrect') });
  }

  // A targeted state naming handle B while dual handle is off can never be reached.
  if (!d.dualHandle && targeted.some(st => parseSliderHandleTrigger(st.trigger).handle === 'b')) {
    issues.push({ key: 'feedback-orphan-handle', severity: 'warning', kind: 'defect', category: feedbackCat, detail: t('blocks.slider.issueFeedbackOrphanHandle') });
  }

  return issues;
}

/** `resolve` is REQUIRED and must never default to `num`. `num('{n}*5')` is **0** — it parseFloats
 * and falls back to zero — so a defaulted resolver would silently grade every formula answer as
 * zero. That is the `num('')` bug class again, except it produces a wrong ANSWER rather than a
 * false warning. A required parameter makes each call site state which roll it is judging against. */
/** Completeness asks whether the teacher AUTHORED an answer — an absence question. A formula
 * counts as authored even where it cannot be resolved: on the unselected canvas card there is no
 * roll at all (`rolledValues` is `{}`), so resolving "{n}*5" gives NaN, and judging on that made a
 * perfectly well-authored formula answer report "setup incomplete". Whether a formula resolves, and
 * whether it lands on the track for every roll, are separate DEFECT checks in `sliderIssues`,
 * which has the variable list needed to answer them honestly.
 *
 * `resolve` is REQUIRED and must never default to `num`. `num('{n}*5')` is **0** — it parseFloats
 * and falls back to zero — so a defaulted resolver would silently grade every formula answer as
 * zero. A required parameter makes each call site state which roll it is judging against. */
function isAnswerSetupIncomplete(
  config: { conditionType: ConditionType; answers: ExactAnswer[]; betweenAnswers: BetweenAnswer[]; threshold: string },
  min: number, max: number,
  resolve: (s: string, fieldId?: string) => number,
): boolean {
  const onTrack = (v: string) => hasVariableChip(v) || (resolve(v) >= min && resolve(v) <= max);
  if (config.conditionType === 'exact') {
    return !config.answers.some(a => a.value.trim() !== '' && onTrack(a.value));
  }
  if (config.conditionType === 'between') {
    return !config.betweenAnswers.some(a =>
      a.low.trim() !== '' && a.high.trim() !== ''
      && (hasVariableChip(a.high) || resolve(a.high, a.id) >= min)
      && (hasVariableChip(a.low) || resolve(a.low, a.id) <= max));
  }
  if (config.threshold.trim() === '') return true;
  return !onTrack(config.threshold);
}

// Blank, not pre-filled — a guessed value (e.g. "50 ± 5") looks like a real answer and a teacher
// has to notice and clear it before typing their own; an empty field has no such trap.
function makeExactAnswer(): ExactAnswer {
  return { id: crypto.randomUUID(), value: '', toleranceValue: '', toleranceAbove: '', toleranceBelow: '' };
}

function makeBetweenAnswer(): BetweenAnswer {
  return { id: crypto.randomUUID(), low: '', high: '' };
}

function makeTickLabel(): TickLabelRow {
  return { id: crypto.randomUUID(), value: '', label: '' };
}

function parseAnswers(rawAnswers: Partial<ExactAnswer>[] | undefined): ExactAnswer[] {
  return rawAnswers?.length
    ? rawAnswers.map(a => ({
        id: a.id ?? crypto.randomUUID(), value: a.value ?? '',
        toleranceValue: a.toleranceValue ?? '', toleranceAbove: a.toleranceAbove ?? '', toleranceBelow: a.toleranceBelow ?? '',
        // Carried, never defaulted: `undefined` MEANS "fall back to the handle", and a literal
        // here would strand every answer authored before the move.
        toleranceSymmetry: a.toleranceSymmetry, toleranceMode: a.toleranceMode,
      }))
    : [makeExactAnswer()];
}

function parseBetween(rawBetween: Partial<BetweenAnswer>[] | undefined): BetweenAnswer[] {
  return rawBetween?.length
    ? rawBetween.map(a => ({ id: a.id ?? crypto.randomUUID(), low: a.low ?? '', high: a.high ?? '' }))
    : [makeBetweenAnswer()];
}

function initHandleB(raw: Record<string, unknown> | undefined | null): HandleBConfig {
  const r = raw ?? {};
  return {
    conditionType: (CONDITION_TYPES.some(c => c.id === r.conditionType) ? r.conditionType : 'exact') as ConditionType,
    answers: parseAnswers(r.answers as Partial<ExactAnswer>[] | undefined),
    toleranceMode: r.toleranceMode === 'absolute' ? 'absolute' : 'percentage',
    toleranceSymmetry: r.toleranceSymmetry === 'asymmetric' ? 'asymmetric' : 'symmetric',
    betweenAnswers: parseBetween(r.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    threshold: (r.threshold as string) ?? '50',
    // Distinct default from handle A's '0' so a freshly dual-enabled slider doesn't render
    // both handles glued together at the same starting position.
    defaultPosition: (r.defaultPosition as string) ?? '100',
  };
}

/**
 * Folds a legacy handle-B feedback set into the block's single set, prefixing each targeted state
 * with `b:` so it still names the input it was authored for.
 *
 * Handle B used to carry its own set. Its DEFAULT states are dropped, not migrated: a block has one
 * verdict now, so a second "Correct"/"No Answer"/"Default Incorrect" has nothing to describe — and
 * they could never fire in the first place, since nothing called the evaluator. Only the targeted
 * states said something specific about handle B's position, so only they are worth keeping.
 */
function migrateHandleBFeedback(
  blockFeedback: { states: FeedbackState[] },
  rawHandleB: Record<string, unknown> | undefined | null,
): { states: FeedbackState[] } {
  const legacy = ((rawHandleB ?? {}).feedback as { states?: FeedbackState[] } | undefined)?.states ?? [];
  const targeted = legacy.filter(st => st.type === 'targeted');
  if (targeted.length === 0) return blockFeedback;
  const existing = new Set(blockFeedback.states.map(st => st.trigger));
  const moved = targeted
    .map(st => ({ ...st, trigger: buildSliderHandleTrigger('b', parseSliderHandleTrigger(st.trigger).condition) }))
    .filter(st => !existing.has(st.trigger));
  return { states: [...blockFeedback.states, ...moved] };
}

function initData(rawInput: Record<string, unknown>): SliderBlockData {
  /* Blocks saved before the rename say `errorValue`; read under the current names. A missed
     one would not crash — `?? ''` turns it into an empty tolerance, and an empty tolerance
     grades EXACT, so every authored band would quietly become a point. */
  const raw = withLegacyToleranceNames(rawInput);
  const rawAnswers = raw.answers as Partial<ExactAnswer>[] | undefined;
  const rawBetween = raw.betweenAnswers as Partial<BetweenAnswer>[] | undefined;
  const rawTickLabels = raw.tickLabels as Partial<TickLabelRow>[] | undefined;
  const minLabel = (raw.minLabel as string) ?? '';
  const maxLabel = (raw.maxLabel as string) ?? '';
  return {
    /*
     * Builds a NEW object field by field, so anything not named is dropped — which is how a bank
     * comes to be created, written to Mongo and then silently gone.
     *
     * Each version's ANSWERS go through the same parsers the block's own do — see
     * tilt-block.tsx, where skipping this threw on an undefined tolerance.
     */
    versions: (raw.versions as SliderBlockData['versions'])?.map(v => ({
      ...v,
      answers: parseAnswers(v.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(v.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),
    /* Named here or silently dropped — see the note above. A row's answers go through the same
       parsers a version's do, for the same reason: skipping it threw on an undefined tolerance. */
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    scenarioBranches: (raw.scenarioBranches as SliderBlockData['scenarioBranches'])?.map(r => ({
      ...r,
      answers: parseAnswers(r.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(r.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),

    question: (raw.question as string) ?? '',
    // A plain 0-100-step-1 range is an immediately usable, easy-to-understand starting point
    // (unlike the answer fields below, which stay blank since there's no equivalently obvious
    // guess for a correct value) — a teacher can just start dragging/previewing right away and
    // override any of these in seconds if their real range is different.
    min: (raw.min as string) ?? '0',
    max: (raw.max as string) ?? '100',
    stepSize: (raw.stepSize as string) ?? '1',
    // 9 hits whole-number mantissas by default (1,2,...,9,10 within each decade) — the natural
    // "round numbers" expectation for a log scale; a teacher can raise this for finer subdivisions
    // (e.g. 90 for tenths) or lower it for coarser ones.
    logStepsPerDecade: (raw.logStepsPerDecade as string) ?? '9',
    scaleMode: raw.scaleMode === 'log' ? 'log' : 'linear',
    defaultPosition: (raw.defaultPosition as string) ?? '0',
    conditionType: (CONDITION_TYPES.some(c => c.id === raw.conditionType) ? raw.conditionType : 'exact') as ConditionType,
    answers: parseAnswers(rawAnswers),
    toleranceMode: raw.toleranceMode === 'absolute' ? 'absolute' : 'percentage',
    toleranceSymmetry: raw.toleranceSymmetry === 'asymmetric' ? 'asymmetric' : 'symmetric',
    betweenAnswers: parseBetween(rawBetween),
    threshold: (raw.threshold as string) ?? '50',
    dualHandle: (raw.dualHandle as boolean) ?? false,
    angleModes: raw.angleModes as Record<string, 'degrees' | 'radians'> | undefined,
    handleB: initHandleB(raw.handleB as Record<string, unknown> | undefined | null),
    orientation: raw.orientation === 'vertical' ? 'vertical' : 'horizontal',
    reverseDirection: (raw.reverseDirection as boolean) ?? false,
    minLabel,
    maxLabel,
    showLabels: (raw.showLabels as boolean) ?? true,
    showTickMarks: (raw.showTickMarks as boolean) ?? false,
    majorTickInterval: (raw.majorTickInterval as string) ?? '',
    minorTickInterval: (raw.minorTickInterval as string) ?? '',
    showMajorTickValues: (raw.showMajorTickValues as boolean) ?? false,
    tickLabels: rawTickLabels?.length
      // Positional fallback, not a random id: this runs during render (see DEFAULT_OPTIONS in
      // mcq-block), so a minted id would change a tick's identity on every pass.
      ? rawTickLabels.map((t, idx) => ({ id: t.id ?? `tick-${idx}`, value: t.value ?? '50', label: t.label ?? '' }))
      : [],
    unitSuffix: (raw.unitSuffix as string) ?? '',
    showLiveReadout: (raw.showLiveReadout as boolean) ?? true,
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as SliderBlockData['gradingMode'],
    // { states: [] } — not {} — matches the fix for the MCQ crash found earlier this session.
    feedback: migrateHandleBFeedback(
      (raw.feedback ?? { states: [] }) as SliderBlockData['feedback'],
      raw.handleB as Record<string, unknown> | undefined | null,
    ),
  };
}

function parseTrigger(trigger: string): { handle: SliderHandleId; type: TriggerType; v1: string; v2: string } {
  const { handle, condition } = parseSliderHandleTrigger(trigger);
  const parts = condition.split(':');
  const raw = parts[0] ?? 'exact';
  const type = (['lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : 'exact') as TriggerType;
  return { handle, type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}

// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildTrigger(handle: SliderHandleId, type: TriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return buildSliderHandleTrigger(handle, type === 'range' ? `range:${a}:${b}` : `${type}:${a}`);
}

// ─── Live test data ───────────────────────────────────────────────────────────
// One shared derivation, called from 3 places (collapsed card, phone Test tab, Preview) — the same
// "canvas card becomes a live test surface" pattern Scale/FITB/Dropdown/MCQ/Keyword already use.
//
// evaluateSlider has existed, fully written, since this block was built and had ZERO callers: its
// grading never ran anywhere in the teacher UI. That is what this connects.

interface SliderTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  notices: BlockIssue[];
}

const sliderTestDataCache: IssueCache<SliderTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft -- see keyword-block. Preview's chips answer for the SUBMITTED
 * handles, which before the first submit is nothing at all, so Behind the Scenes had no Current
 * to show. This one answers for where the handles are RIGHT NOW.
 */
const sliderDraftChipsCache: IssueCache<SliderTestData> = new WeakMap();

/** A handle's own accepted-range config, read from the block data rather than editor state — the
 * unselected canvas card is a separate mounted instance whose editor state froze at mount. */
/**
 * Do the two sliders demand answers they cannot hold at the same time?
 *
 * The handles cannot cross — slider 1 is always at or below slider 2, enforced on every drag — so a
 * pair of answers is gradeable only if some accepted value for 1 sits at or below some accepted
 * value for 2. `283 ± 5%` on slider 1 with `1` on slider 2 asks for a state the control physically
 * cannot enter, and every submission grades incorrect forever.
 *
 * Module-level because two surfaces need the same answer: the issues panel names the problem, and
 * the answer FIELDS highlight to show which two numbers are the problem. Computing it twice is how
 * a warning and a highlight come to disagree about whether anything is wrong.
 *
 * The loosest reading: min-of-1 against max-of-2, so overlapping and merely touching bands pass.
 */
/**
 * Whether the two handles' answers can be satisfied AT THE SAME TIME — quantified over every value
 * the variables can take, not judged from one roll.
 *
 * The handles cannot cross (`clampForHandle` enforces slider 1 <= slider 2 on every drag), so the
 * pair is only gradeable if some accepted value for 1 sits at or below some accepted value for 2.
 *
 * It used to take a `resolve` and answer yes/no from whatever roll that resolver happened to carry.
 * With `{test}` against `{test2}` over the same domain that is true of about half the rolls and
 * false of the rest, so the block asserted "Slider 1 must have a lower correct value than Slider 2"
 * while the scenario on screen showed slider 1 comfortably below slider 2 — the reported bug. A
 * roll-specific fact cannot be reported as a property of the block.
 *
 * The comparison is min-of-1 against max-of-2, the loosest reading, so overlapping and touching
 * bands both pass; only a pair with no satisfiable ordering at all counts. `always` is a real
 * defect (no student can ever be right); `sometimes` is a warning (most rolls work, some cannot be
 * answered); `unknown` means the variable space was too wide to check and is reported as such
 * rather than passed over in silence.
 */
/**
 * Whether ANY answer text has been authored, on either handle.
 *
 * The answer bar needs this to tell its two empty cases apart: a block still being built shows
 * nothing (its setup-incomplete issue already says where it is up to), while one whose authored
 * answers admit nothing says "No Valid Answer". Deliberately about TEXT, not about the answer
 * resolving — a half-typed formula counts as authored.
 */
export function sliderAnswerAuthored(d: SliderBlockData): boolean {
  // `answerExprTexts` switches on the CURRENT condition type, which is the whole point: this used
  // to ask all three field groups at once, and slider defaults `threshold` to '50' when absent — so
  // a brand-new block, whose condition type is `exact` and whose threshold is therefore irrelevant,
  // looked authored and announced "No Valid Answer" where the other three showed no bar at all.
  return answerExprTexts(d).length > 0 || (d.dualHandle && answerExprTexts(d.handleB).length > 0);
}

function sliderAnswersCrossedVerdict(
  d: SliderBlockData, variables: TaskVariable[],
): ComparisonVerdict {
  if (!d.dualHandle) return 'never';
  /** The lowest authored bound for a handle, and the highest — as EXPRESSIONS, so the comparison
   *  can quantify them. Tolerances are literal (they never accept equations), so they fold in as
   *  arithmetic on the expression rather than needing their own quantification. */
  const edge = (handle: SliderHandleId, which: 'lo' | 'hi'): string | null => {
    const src = handle === 'b' ? d.handleB : d;
    if (src.conditionType === 'between') {
      const rows = src.betweenAnswers.filter(a => a.low.trim() !== '' && a.high.trim() !== '');
      if (rows.length === 0) return null;
      // Several OR'd rows: the handle's reachable span runs from the lowest low to the highest high.
      const fn = which === 'lo' ? 'min' : 'max';
      return rows.map(r => `(${which === 'lo' ? r.low : r.high})`)
        .reduce((acc, x) => `${fn}(${acc}, ${x})`);
    }
    if (src.conditionType !== 'exact') {
      return src.threshold.trim() === '' ? null : `(${src.threshold})`;
    }
    const rows = src.answers.filter(a => a.value.trim() !== '');
    if (rows.length === 0) return null;
    const tolOf = (a: typeof rows[number]) => src.toleranceSymmetry === 'asymmetric'
      ? (which === 'lo' ? num(a.toleranceBelow) : num(a.toleranceAbove))
      : num(a.toleranceValue);
    const term = (a: typeof rows[number]) => src.toleranceMode === 'percentage'
      ? `((${a.value}) ${which === 'lo' ? '-' : '+'} abs(${a.value}) * ${tolOf(a)} / 100)`
      : `((${a.value}) ${which === 'lo' ? '-' : '+'} ${tolOf(a)})`;
    const fn = which === 'lo' ? 'min' : 'max';
    return rows.map(term).reduce((acc, x) => `${fn}(${acc}, ${x})`);
  };
  const aLo = edge('a', 'lo'), bHi = edge('b', 'hi');
  if (aLo === null || bHi === null) return 'never';   // nothing authored yet is not a defect

  /*
   * ONE MODE FOR A SYNTHESISED EXPRESSION, and it has to be the one every field in it agrees on.
   *
   * `edge` folds a whole handle's answers into a single `min()`/`max()` expression, so there is
   * no one field whose mode this is. Where the contributing fields all say the same thing, that
   * is the answer. Where they DISAGREE, the comparison has no honest reading — half the terms
   * would be evaluated wrong — so it declines rather than returning a verdict built on it.
   *
   * `unknown` is the existing "cannot be quantified" verdict, and the callers already treat it
   * as "say nothing", which is exactly right: a handle-overlap warning is worth less than a
   * wrong one.
   */
  const contributing = [...d.answers, ...d.betweenAnswers, ...d.handleB.answers, ...d.handleB.betweenAnswers]
    .map(a => angleModeFor(d, 'value' in a ? `${a.id}.value` : a.id));
  const modes = new Set([...contributing,
    angleModeFor(d, 'threshold'), angleModeFor(d, 'handleB.threshold')]);
  if (modes.size > 1) return 'unknown';

  return compareExpressionsAcrossRolls(aLo, bHi, variables, [...modes][0] ?? 'degrees');
}

/** One handle's accepted-range config, resolved. Exported for the attempt signature, which needs
 *  the same bands the block's own issue checks are built from. */
export function handleRangeConfigFrom(
  d: SliderBlockData, handle: SliderHandleId,
  /** `fieldId` so each answer resolves in ITS OWN angle mode — the ids are right here. */
  resolve: (s: string, fieldId: string) => number) {
  const src = handle === 'b' ? d.handleB : d;
  /** Handle B's panel prefixes its single named fields, so its threshold is `handleB.threshold`.
   *  The answers need no prefix: their ids are already unique per handle. */
  const thresholdField = handle === 'b' ? 'handleB.threshold' : 'threshold';
  return {
    conditionType: src.conditionType,
    // Answer values resolve; tolerances stay `num`, because tolerance is numeric by decision
    // (percentage mode already covers "scales with the answer").
    answers: src.answers.map(a => ({
      id: a.id, value: resolve(a.value, `${a.id}.value`),
      toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow),
    })),
    betweenAnswers: src.betweenAnswers.map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceMode: src.toleranceMode,
    toleranceSymmetry: src.toleranceSymmetry,
    threshold: resolve(src.threshold, thresholdField),
  };
}

/** The block data as the evaluator wants it: strings parsed to numbers, one handle or two. */
/** Exported for `angle-mode-grading`, which grades through the REAL chain rather than a
 *  stand-in: the point of that test is that the app's own wiring resolves per field. */
export function evaluatorDataFrom(d: SliderBlockData, resolve: (s: string, fieldId: string) => number): BlocksSliderBlockData {
  const a = handleRangeConfigFrom(d, 'a', resolve);
  return {
    question: d.question,
    min: num(d.min), max: num(d.max), stepSize: num(d.stepSize),
    logStepsPerDecade: num(d.logStepsPerDecade), scaleMode: d.scaleMode,
    defaultPosition: num(d.defaultPosition),
    ...a,
    dualHandle: d.dualHandle,
    handleB: d.dualHandle
      ? { ...handleRangeConfigFrom(d, 'b', resolve), defaultPosition: num(d.handleB.defaultPosition) }
      : null,
    orientation: d.orientation, reverseDirection: d.reverseDirection,
    minLabel: d.minLabel, maxLabel: d.maxLabel, showLabels: d.showLabels,
    showTickMarks: d.showTickMarks, majorTickInterval: num(d.majorTickInterval),
    minorTickInterval: num(d.minorTickInterval), showMajorTickValues: d.showMajorTickValues,
    tickLabels: d.tickLabels.map(l => ({ id: l.id, value: num(l.value), label: l.label })),
    unitSuffix: d.unitSuffix, showLiveReadout: d.showLiveReadout,
    gradingMode: d.gradingMode as BlocksSliderBlockData['gradingMode'],
    // Targeted triggers go through the resolver, not straight through. `SliderTriggerEditor`
    // renders a FormulaField for all three trigger inputs, so a trigger can hold `{n}*5` — and
    // `parseValueTrigger` reads it with parseFloat. Unresolved, every equation trigger was NaN and
    // silently never fired: authorable, listed in the panel, unreachable by any student.
    feedback: { ...d.feedback, states: resolveFeedbackTriggers(d.feedback.states, resolve) },
  };
}

/**
 * The gaps either side of the accepted band, worded once.
 *
 * The feedback panel listed these and the test bar said "Any other value" — the same state
 * described two ways, which is what Compass and Angle already fixed for themselves. `fmt` is a
 * parameter because the two surfaces format numbers differently (the panel abbreviates on a phone),
 * and that is a genuine difference, unlike the wording.
 */

function computeSliderTestData(
  d: SliderBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The locale tag, not a bound formatter: the shared chip derivation lives in `lib/` and formats
   *  its own numbers, so a closure from this component is neither needed nor honoured. */
  locale: string,
  submitted: { a: SliderAnswer; b: SliderAnswer } | null,
  resolve: (s: string, fieldId?: string) => number,
  /** This block's setup problems, surfaced through the test bar the way every other block does.
   * The field existed and was returned empty on all three paths, so slider's test bar never showed
   * an issue at all — the panel was in the answer bar instead. */
  issues: BlockIssue[] = [],
  /** The roll the TEST is running against — legend chips resolve, unlike feedback chips. */
  rolledValuesForChips: Record<string, string> = {},
): SliderTestData {
  const ev = evaluatorDataFrom(d, resolve);
  const testResult = submitted
    ? evaluateSlider(submitted.a, ev, d.dualHandle ? submitted.b : undefined)
    : null;
  const firedIds = new Set((testResult ?? []).map(x => x.id));
  const states = d.feedback.states;
  const chips: TestBarChip[] = [];
  const push = (c: TestBarChip) => chips.push(c);

  /**
   * Both participation arms used to emit the catch-all and RETURN, which put the early exit above
   * the targeted loop below. Harmless while the value blocks could not fire a targeted state
   * outside graded, and wrong the moment they could: the panel listed the targeted positions and
   * the bar said "Any Response".
   *
   * So the mode now decides what to SKIP rather than where to stop — the targeted loop is common
   * to every mode, and only the correct/incorrect fan-out is graded's.
   */
  const participation = d.gradingMode !== 'graded';

  // Optional has no No Answer: a blank there is a real submission, not an omission.
  const unanswered = d.gradingMode === 'optional' ? undefined : states.find(x => x.trigger === 'unanswered');
  if (unanswered) push({ id: unanswered.id, condition: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation: unanswered.evaluation, active: firedIds.has(unanswered.id), category: 'unanswered' });

  // Correct is ONE state describing a conjunction when there are two handles — "handle 1 in range
  // AND handle 2 in range". A bare row of chips reads as alternatives, so the two requirements are
  // joined by an explicit connector, the same device FITB uses across blanks and MCQ multi-select
  // uses across options.
  /** Where each handle actually is, so the legend lights the ONE band or gap it landed in rather
   *  than every chip of the state. `toTestBarChips` applies `active` state-wide to chips with no
   *  identity, which is why this whole row used to light at once. */
  const submittedByHandle = {
    a: submitted ? submitted.a.value : null,
    b: submitted && d.dualHandle ? submitted.b.value : null,
  };
  const correct = participation ? undefined : states.find(x => x.trigger === 'correct');
  if (correct) {
    const handles: SliderHandleId[] = d.dualHandle ? ['a', 'b'] : ['a'];
    const ranges = handles.map(h => computeSliderAcceptedRanges(handleRangeConfigFrom(d, h, resolve)));
    if (isSliderCorrectUnreachable(ranges)) {
      push({ id: correct.id, condition: t('blocks.feedbackPanel.unreachable'), evaluation: correct.evaluation, active: false, reachable: false, category: 'correct' });
    } else {
      // `sliderChips`, not a hand-built label. This arm joined each handle's bands into one string
      // and pushed one chip per handle — a third phrasing of the fact the panel and the answer bar
      // already share, and one that could not light a single band because it was one chip.
      const correctChips = sliderChips(d as unknown as Record<string, unknown>,
        { type: 'default', trigger: 'correct' } as never, t, locale,
        Object.keys(rolledValuesForChips).length > 0 ? rolledValuesForChips : null) ?? [];
      chips.push(...toTestBarChips(correctChips, {
        id: correct.id, category: 'correct', active: firedIds.has(correct.id),
        activeSourceIds: activeIntervalIds(correctChips, submittedByHandle),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
    }
  }

  /** The roll these chips resolve against, shared by the branches below. */
  const chipRoll = Object.keys(rolledValuesForChips).length > 0 ? rolledValuesForChips : null;
  /** `sliderChips` for one state, as the bar's own chips. */
  const barChips = (state: { type: string; trigger: string }, acceptedClaimed = true) =>
    sliderChips(d as unknown as Record<string, unknown>, state as never, t, locale, chipRoll,
      acceptedClaimed) ?? [];

  // `visibleTargeted`, not every stored state: `set_answer` cannot fire in graded, where Correct
  // already IS the answer, and nothing targeted fires in optional at all.
  for (const st of visibleTargeted(states, d.gradingMode)) {
    /*
     * SET ANSWER states the authored condition over BOTH handles, the same chips the feedback
     * card and graded's Correct row draw. It holds no value of its own, so the trigger summary
     * would print "Slider 1: < ?" — an operator with its operand missing, on a card that is not
     * about Slider 1 in the first place.
     */
    if (st.trigger === SET_ANSWER_TRIGGER) {
      const preset = barChips({ type: 'default', trigger: 'correct' });
      chips.push(...toTestBarChips(preset, {
        id: st.id, category: 'targeted', active: firedIds.has(st.id),
        activeSourceIds: activeIntervalIds(preset, submittedByHandle),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
      continue;
    }
    push({
      id: st.id,
      // Resolved, not substituted. This read `test400*5` under a scenario — the formula with its
      // variable swapped in — leaving the arithmetic to the reader while the accepted-answer chip
      // beside it printed a finished number. Same builder numerical uses, so the two blocks cannot
      // drift on what a resolved condition looks like.
      condition: resolvedTriggerChip({
        trigger: st.trigger,
        scenario: rolledValuesForChips,
        summaryOf: resolved => sliderTargetedTitle(resolved, t, d.dualHandle),
        evaluate: (expr, values) =>
          evaluateFormulaRaw(expr, values, angleModeFor(d, `feedback.${st.id}.trigger`)),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      }),
      // Neutral outside graded, like `participationOutcome` returns and like the panel draws.
      evaluation: participation ? 'neutral' : st.evaluation,
      active: firedIds.has(st.id),
      category: 'targeted',
    });
  }

  if (participation) {
    const catchAll = states.find(x => x.trigger === (d.gradingMode === 'optional' ? 'any_response' : 'any_answer'));
    /*
     * OPTIONAL is ONE ROW. Everything accumulated above belongs to a mode that can fire it, and
     * optional fires none of it: the mode is inert, so Any Response is the only outcome it has.
     * Cleared rather than never-pushed, because the rungs above are shared with survey, which
     * does fire them. `legendStatesFor` makes the same call for the blocks that use it.
     */
    if (d.gradingMode === 'optional') chips.length = 0;

    if (catchAll) {
      /*
       * It ENUMERATES, through the same `catchAllChips` the panel uses, so the bar and the card
       * cannot disagree about what still falls through.
       *
       * The plain label this replaces was justified as "slider's domain is open", borrowed from
       * keyword. Slider's domain is not open: it runs min to max, and `sliderChips` states the
       * complement of a two-handle conjunction exactly — not(A and B) is (not A or not B), per
       * handle. Once Set Answer owns 19-21 and 79-81, what is left is statable and was being
       * described as "Any Position Accepted".
       *
       * `acceptedClaimed` is what decides whether the band comes out of the leftovers, and only
       * a Set Answer target claims it; `catchAllChips` keeps the tick and the plain label until
       * something is targeted at all, and refuses the whole question in optional.
       */
      const targetedList = visibleTargeted(states, d.gradingMode);
      chips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, targetedList.length > 0, t,
          () => barChips({ type: 'default', trigger: 'incorrect' },
            targetedList.some(x => x.trigger === SET_ANSWER_TRIGGER))),
        {
          id: catchAll.id,
          category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    return { testResult, testChips: chips, notices: issues };
  }

  const incorrect = states.find(x => x.trigger === 'incorrect');
  if (incorrect) {
    const triggers = states.filter(x => x.type === 'targeted').map(x => x.trigger);
    // Only handle A's coverage is swept: the shared helpers reason about one number line, and a
    // two-handle block has two. Under-claiming here is deliberate — saying "unreachable" when a
    // second handle can still reach it would be a lie, and the reverse is merely less helpful.
    const unreachable = !d.dualHandle
      && isSliderValueDefaultIncorrectUnreachable(computeSliderAcceptedRanges(handleRangeConfigFrom(d, 'a', resolve)), triggers);
    // Both handles, unlike `unreachable` above: Correct needs every handle satisfied, so one
    // empty answer key means everything falls through here whatever the other handle says.
    const noAnswerYet = isSliderCorrectUnreachable(
      (d.dualHandle ? (['a', 'b'] as const) : (['a'] as const))
        .map(h => computeSliderAcceptedRanges(handleRangeConfigFrom(d, h, resolve))));
    /*
     * Default Incorrect, from the SHARED derivation — `sliderChips` in `lib/block-state-labels.ts`,
     * the same call the feedback panel and Flow make.
     *
     * This arm used to build its own list, which is how the legend came to say "Any other value"
     * where the panel said "< 5 or > 5". It also gave up on dual-handle entirely ("the bar has a
     * single row, so joining two number lines would read as one set of alternatives") — the chip
     * list solves that properly, because a connector chip between the two says "or" out loud
     * instead of leaving a comma to imply it.
     */
    const incorrectChips = sliderChips(d as unknown as Record<string, unknown>,
      { type: 'default', trigger: 'incorrect' } as never, t, locale,
      // The legend is a live surface, so it resolves — but only once there IS a roll. An empty map
      // means "not rolled yet", not "no variables", and passing it as a roll would resolve every
      // formula to NaN and print a band around nothing.
      Object.keys(rolledValuesForChips).length > 0 ? rolledValuesForChips : null);
    if (incorrectChips && incorrectChips.length > 0) {
      chips.push(...toTestBarChips(incorrectChips, {
        id: incorrect.id, category: 'incorrect', active: firedIds.has(incorrect.id),
        // Only the gap the handle is actually in — a slider at 6.9 lit "< 26.88" AND "> 29.12" AND
        // both of slider 2's gaps before this, which says nothing about where the handle is.
        activeSourceIds: activeIntervalIds(incorrectChips, submittedByHandle),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
    } else {
      push({
        id: incorrect.id,
        // Nothing to enumerate: either the state cannot fire, or no answer is configured and every
        // position lands here.
        condition: unreachable ? t('blocks.feedbackPanel.unreachable')
          : t(noAnswerYet ? 'blocks.slider.chipAnyValue' : 'blocks.slider.chipAnyOther'),
        evaluation: incorrect.evaluation,
        active: firedIds.has(incorrect.id),
        reachable: !unreachable,
        category: 'incorrect',
      });
    }
  }

  return { testResult, testChips: chips, notices: issues };
}

/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderSliderFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} />;
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

// ─── Answer setup fields (condition type + exact/between answers + threshold) ─────────────────
// Extracted so dual-handle mode can render Slider 1's and Slider 2's answer setup as two stacked
// sections (per explicit request — "sections... rather than put them behind a switcher button"),
// instead of duplicating this ~140-line block by hand for each handle.

interface AnswerSetupFieldsProps {
  /**
   * Qualifies this copy's field ids. Handle B's setup is the SAME component as handle A's, and its
   * data lives one level down (`handleB.conditionType`), which is exactly what the diff reports —
   * so without a prefix both copies claim the unqualified key and an undo of handle B's condition
   * pulses handle A's switch.
   */
  fieldPrefix?: string;

  t: (k: string, opts?: Record<string, unknown>) => string;
  min: number;
  max: number;
  conditionType: ConditionType;
  onConditionTypeChange: (v: ConditionType) => void;
  answers: ExactAnswer[];
  onUpdateAnswer: (id: string, patch: Partial<ExactAnswer>) => void;
  onAddAnswer: () => void;
  onRemoveAnswer: (id: string) => void;
  /** The handle's stored pair, kept only as the fallback for answers authored before the
   *  tolerance settings moved onto each answer. Resolve through `answerToleranceMode`/`answerSymmetry`. */
  legacyScope: { toleranceMode?: ToleranceMode; toleranceSymmetry?: 'symmetric' | 'asymmetric' };
  /** Both flip ONE answer — the settings are per answer. */
  onToggleAnswerMode: (id: string) => void;
  onToggleAnswerSymmetry: (id: string) => void;
  betweenAnswers: BetweenAnswer[];
  onUpdateBetweenAnswer: (id: string, patch: Partial<BetweenAnswer>) => void;
  onAddBetweenAnswer: () => void;
  onRemoveBetweenAnswer: (id: string) => void;
  threshold: string;
  onThresholdChange: (v: string) => void;
  /** Which answer rows the issue list points at, from `issueFieldIds(editorIssues)`. Marks the
   *  answer VALUE fields, so the issue text and the numbers it is about are connected on screen
   *  rather than left for the teacher to pair up.
   *
   *  Read from the issues rather than re-running a check here: this used to be a boolean from a
   *  second call to `sliderAnswersCrossed`, and one function called twice with independently
   *  supplied arguments is how ordering's two surfaces came to disagree. It also generalises for
   *  free — an answer off the track or on an unreachable step now rings for the same reason the
   *  crossed pair does, because the issue names it. */
  flaggedAnswerIds?: ReadonlySet<string>;
  armedAnswerId: string | null;
  setArmedAnswerId: (id: string | null) => void;
  armedBetweenId: string | null;
  setArmedBetweenId: (id: string | null) => void;
  /** Clears the shared rich-text toolbar's active-editor reference — see the block's own
   * disconnectSharedToolbar for why a numeric field must release it on focus. */
  onPlainFieldFocus: () => void;
  /** Task variables offered by the answer fields' chip picker. Answer VALUES accept formulas;
   * tolerance and track geometry deliberately do not (percentage mode already scales a tolerance
   * with its answer, and the track is the instrument the student reads, not the thing asked). */
  variables: TaskVariable[];
  /** The roll these fields preview against — `{}` in plain display, where chips stay unresolved. */
  rolledValues: Record<string, string>;
  /** Resolves an answer field to a number under the CURRENT roll. Passed in rather than computed
   * here so the resolution policy lives at one call site instead of being re-derived per field. */
  resolveAnswer: (s: string, fieldId?: string) => number;
}

function AnswerSetupFields({
  t, min, max, conditionType, onConditionTypeChange, answers, onUpdateAnswer, onAddAnswer, onRemoveAnswer,
  flaggedAnswerIds,
  legacyScope, onToggleAnswerMode, onToggleAnswerSymmetry,
  betweenAnswers, onUpdateBetweenAnswer, onAddBetweenAnswer, onRemoveBetweenAnswer,
  threshold, onThresholdChange, armedAnswerId, setArmedAnswerId, armedBetweenId, setArmedBetweenId, onPlainFieldFocus,
  variables, rolledValues, resolveAnswer, fieldPrefix = '',
}: AnswerSetupFieldsProps) {
  // Deleting a row removes the just-focused button from the DOM, silently dropping focus to
  // <body> — refocus the add-button afterward, matching numerical-block.tsx's removeAnswer/
  // removeBetweenAnswer convention, so keyboard/screen-reader context isn't lost.
  const addAnswerBtnRef = useRef<HTMLButtonElement>(null);
  const addBetweenBtnRef = useRef<HTMLButtonElement>(null);
  /**
   * The two TYPE controls for ONE answer, riding that answer's tolerance row.
   *
   * They used to be handle-level and took a labelled row of their own once there was more than
   * one answer — the trailing row that made a multi-answer setup look unfinished. Per answer
   * they belong on the row they govern, every row is the same shape, and the extra row is gone.
   *
   * The mode's word is dropped: four controls and a box share the line, and `aria-label`
   * carries the name either way.
   */
  function renderTypeControls(a: ExactAnswer) {
    const pct = answerToleranceMode(a, legacyScope, 'absolute') === 'percentage';
    return (
      <>
        {/* `size='square'`, not a width in `className` — see `Button`'s SIZE table for why a
            caller's `px-0` loses to the component's own `px-4`. */}
        <Button size='square' data-field-id={`${fieldPrefix}${a.id}.toleranceMode`} aria-pressed={pct} aria-label={t('blocks.tolerance.toleranceModeAriaLabel')} onClick={() => onToggleAnswerMode(a.id)} className='focus-visible:ring-2 focus-visible:ring-ws-primary focus-visible:ring-offset-1'>
          {pct ? <Percent aria-hidden='true' className='h-4 w-4' /> : <Ruler aria-hidden='true' className='h-4 w-4' />}
        </Button>
        <SymmetryToggle compact fieldId={`${fieldPrefix}${a.id}.toleranceSymmetry`}
          asymmetric={answerSymmetry(a, legacyScope) === 'asymmetric'} onToggle={() => onToggleAnswerSymmetry(a.id)} />
      </>
    );
  }

  return (
    <>
      <SegmentedControl fieldId={`${fieldPrefix}conditionType`}
        options={CONDITION_TYPES.map(c => ({ id: c.id, label: c.symbol, ariaLabel: t(`blocks.conditions.${CONDITION_ARIA_KEYS[c.id]}`) }))}
        value={conditionType}
        onChange={v => onConditionTypeChange(v as ConditionType)}
        aria-label={t('blocks.conditions.typeLabel')}
      stretch
      />

      {conditionType === 'exact' && (
        <div className='flex flex-col gap-3'>
          {answers.map(a => {
            const isAsym = answerSymmetry(a, legacyScope) === 'asymmetric';
            const isPct = answerToleranceMode(a, legacyScope, 'absolute') === 'percentage';
            const isArmed = armedAnswerId === a.id;
            // A correct-answer value the slider itself can never reach (outside its own
            // min/max) can never actually be selected by a student — flagged the same way an
            // off-scale custom tick label is, so the teacher notices before publishing.
            // Resolved under the current roll, so a formula answer is judged as the number this
            // student would actually see. The roll-independent version of this check (does it hold
            // for EVERY roll?) is the block's own issue list, which quantifies over the extrema —
            // this inline marker just flags the value in front of the teacher right now.
            const aOutOfRange = a.value.trim() !== '' && (resolveAnswer(a.value) < min || resolveAnswer(a.value) > max);
            // Standard field-error treatment for the crossed-answers case: the panel names the
            // problem, the fields say WHICH numbers. Both sliders' fields light up, because neither
            // is individually wrong — it is the pair that cannot be satisfied.
            const aFieldError = aOutOfRange || !!flaggedAnswerIds?.has(a.id);
            // The armed-delete highlight wraps BOTH rows — see numerical's note.
            return (
              <div key={a.id} data-field-id={a.id} className={`flex flex-col gap-1 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
                <div className='flex items-center gap-2'>
                {/* No filterNumber here: that strips everything but digits, which is exactly right
                  * for a tolerance and exactly wrong for a formula. FormulaField owns its own input
                  * discipline and the chip picker. */}
                <FormulaField fieldId={`${a.id}.value`}
                  hideEval
                  value={a.value}
                  onChange={v => onUpdateAnswer(a.id, { value: v })}
                  variables={variables}
                  rolledValues={rolledValues}
                  ariaLabel={t('blocks.answers.fieldValue')}
                  hasError={aFieldError}
                  className='min-w-0 flex-1' 
                  onFocus={onPlainFieldFocus}/>
              </div>
              {/* The tolerance has its own row in both modes, and `scientific` because a
                  slider's domain is not bounded the way an angle's is — see `ToleranceRow`. */}
              <ToleranceRow
                idPrefix={a.id}
                asymmetric={isAsym}
                grammar='scientific'
                percent={isPct}
                toleranceValue={a.toleranceValue}
                toleranceBelow={a.toleranceBelow}
                toleranceAbove={a.toleranceAbove}
                onChange={patch => onUpdateAnswer(a.id, patch)}
                onFocus={onPlainFieldFocus}
                trailing={<>
                  {renderTypeControls(a)}
                  {answers.length > 1 && (
                    <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                      aria-label={isArmed ? t('blocks.answers.confirmRemoveAnswer') : t('blocks.answers.removeAnswer')}
                      className='shrink-0'
                      onClick={() => {
                        if (isArmed) { onRemoveAnswer(a.id); setArmedAnswerId(null); requestAnimationFrame(() => addAnswerBtnRef.current?.focus()); }
                        else { setArmedBetweenId(null); setArmedAnswerId(a.id); }
                      }}
                      onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedAnswerId(null); }}
                      onBlur={() => { if (isArmed) setArmedAnswerId(null); }}
                    >
                      <Trash2 className='h-4 w-4' />
                    </IconButton>
                  )}
                </>} />
              </div>
            );
          })}

          {/* With ONE answer these ride that answer's tolerance row and the setup is two
              lines; beyond that they take a labelled row — see `renderTypeControls`. */}
          {answers.length < 5 && (
            <AddRowButton
              ref={addAnswerBtnRef}
              label={t('blocks.answers.addAnswer')}
              onClick={onAddAnswer}
            />
          )}
        </div>
      )}

      {conditionType === 'between' && (
        <div className='flex flex-col gap-3'>
          {betweenAnswers.map(a => {
            const isArmed = armedBetweenId === a.id;
            // Flagged only when the whole [low, high] range misses the slider's own [min, max]
            // entirely (no reachable value could ever satisfy it) — unlike the single-value exact
            // case, a range that merely extends past one end (e.g. -10 to 50 on a 0-100 slider)
            // still has real reachable correct values on the other end, so per-endpoint checks
            // would false-flag a perfectly valid setup.
            const bothBoundsSet = a.low.trim() !== '' && a.high.trim() !== '';
            const rangeOutOfRange = bothBoundsSet && (resolveAnswer(a.high) < min || resolveAnswer(a.low) > max);
            return (
              <BetweenAnswerRow
                key={a.id}
                fieldId={a.id}
                armed={isArmed}
                grouped={betweenAnswers.length > 1}
                to={t('blocks.connectors.to')}
                low={
                  <FormulaField fieldId={`${a.id}.low`} angleModeKey={a.id}
                    hideEval
                    value={a.low}
                    onChange={v => onUpdateBetweenAnswer(a.id, { low: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={t('blocks.triggers.triggerRangeMin')}
                    placeholder={t('blocks.triggers.triggerRangeMin')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                high={
                  <FormulaField fieldId={`${a.id}.high`} angleModeKey={a.id}
                    hideEval
                    value={a.high}
                    onChange={v => onUpdateBetweenAnswer(a.id, { high: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={t('blocks.triggers.triggerRangeMax')}
                    placeholder={t('blocks.triggers.triggerRangeMax')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                action={betweenAnswers.length > 1 && (
                  <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                    aria-label={isArmed ? t('blocks.answers.confirmRemoveRange') : t('blocks.answers.removeRange')}
                    className='shrink-0'
                    onClick={() => {
                      if (isArmed) { onRemoveBetweenAnswer(a.id); setArmedBetweenId(null); requestAnimationFrame(() => addBetweenBtnRef.current?.focus()); }
                      else { setArmedAnswerId(null); setArmedBetweenId(a.id); }
                    }}
                    onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedBetweenId(null); }}
                    onBlur={() => { if (isArmed) setArmedBetweenId(null); }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </IconButton>
                )}
              />
            );
          })}
          {betweenAnswers.length < 5 && (
            <AddRowButton
              ref={addBetweenBtnRef}
              label={t('blocks.answers.addAnswer')}
              onClick={onAddBetweenAnswer}
            />
          )}
        </div>
      )}

      {ONE_SIDED.includes(conditionType) && (() => {
        const thresholdOutOfRange = threshold.trim() !== '' && (resolveAnswer(threshold) < min || resolveAnswer(threshold) > max);
        return (
          <div className='flex flex-col gap-1'>
            <FormulaField fieldId={`${fieldPrefix}threshold`}
              hideEval
              value={threshold}
              onChange={onThresholdChange}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.answers.fieldValue')}
              placeholder={t('blocks.answers.fieldValue')}
              hasError={thresholdOutOfRange}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        );
      })()}
    </>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────

function SliderTriggerEditor({ trigger, onChange, variables, rolledValues, onPlainFieldFocus, dualHandle, gradingMode, answerConditionChips, answerTaken, fieldId }: {
  /** The angle-mode key for this trigger's fields. A range's two bounds share it, because two
   *  bounds of one measurement disagreeing about degrees is never what anyone meant. */
  fieldId: string;
  /** Releases the shared rich-text toolbar — see FormulaField's `onFocus`. */
  onPlainFieldFocus: () => void;
  trigger: string; onChange: (t: string) => void;
  variables: TaskVariable[]; rolledValues: Record<string, string>;
  /** Two handles: the card has to say which one it is about. */
  dualHandle: boolean;
  /** Decides whether the Answer/Custom selector appears at all — see `TriggerKindSelect`. */
  /** Another card already points at the answer — see `TriggerKindSelect`. */
  answerTaken: boolean;
  gradingMode: GradingMode;
  /** The authored condition, read-only, shown where the custom editor would be. */
  answerConditionChips: React.ReactNode;
}) {
  const { t } = useTranslation();
  const kind = triggerKindOf(trigger);
  const { handle: trigHandle, type: trigType, v1, v2 } = parseTrigger(trigger);
  const loNum = parseFloat(v1);
  const hiNum = parseFloat(v2);
  const rangeInverted = trigType === 'range' && v1 !== '' && v2 !== ''
    && Number.isFinite(loNum) && Number.isFinite(hiNum) && loNum > hiNum;
  const TRIGGER_TYPES: { id: TriggerType; symbol: string }[] = [
    { id: 'exact', symbol: '=' }, { id: 'lt', symbol: '<' }, { id: 'lte', symbol: '≤' },
    { id: 'gt', symbol: '>' }, { id: 'gte', symbol: '≥' }, { id: 'range', symbol: '↔' },
  ];

  return (
    <div className='flex flex-col gap-2'>
      <TriggerKindSelect
        trigger={trigger}
        gradingMode={gradingMode}
        answerTaken={answerTaken}
        onChange={onChange}
        customDefault={buildTrigger('a', 'exact', '', '')}
      />
      {/* THE PRESET REPLACES EVERYTHING BELOW, handle switcher included.
          Set Answer is joint — it points at Correct, which `evaluateSlider` decides with
          `submissions.every(...)` — so there is no handle to choose. Offering the switcher would
          imply a per-handle Set Answer, which is exactly the predicate this block does not have:
          it would claim a student had given the authored answer with one handle somewhere else. */}
      {kind === 'answer' ? (
        <div className='pointer-events-none opacity-60'>{answerConditionChips}</div>
      ) : (
      <>
      {/* WHICH slider, then HOW it compares — the two halves of "Slider 2 is at least 40", in that
          order. It belongs on the card and not above the list because one card can target Slider 1
          while the next targets Slider 2; a panel-level switcher made that look like a mode you
          were in rather than a property of the card you were editing. Changing it rewrites the
          handle prefix on this trigger and nothing else. */}
      {dualHandle && (
        <SegmentedControl
          options={[
            { id: 'a', label: t('blocks.slider.handle1') },
            { id: 'b', label: t('blocks.slider.handle2') },
          ]}
          value={trigHandle}
          onChange={h => onChange(buildTrigger(h as SliderHandleId, trigType, v1, v2))}
          aria-label={t('blocks.slider.activeHandleLabel')}
          stretch
        />
      )}
      <div className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol }) => (
          <button
            key={id}
            type='button'
            aria-label={t(`blocks.slider.triggerType${id.charAt(0).toUpperCase()}${id.slice(1)}` as never)}
            onClick={() => onChange(buildTrigger(trigHandle, id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <>
          {/* Stacked, not side by side. Two `w-0 flex-1` fields split an already-narrow feedback
              panel in half, which was tight for a two-digit number and unusable once a bound can
              hold a chip-rendered formula. Full width each, with the separator as a label between
              them, so either bound can show its whole expression. */}
          <div className='flex flex-col gap-1.5'>
            <FormulaField
              fieldId={`${fieldId}.low`} angleModeKey={fieldId}
              hideEval
              value={v1}
              onChange={v => onChange(buildTrigger(trigHandle, 'range', v, v2))}
              variables={variables}
              rolledValues={rolledValues}
              placeholder={t('blocks.triggers.triggerRangeMin')}
              ariaLabel={t('blocks.triggers.triggerRangeMin')}
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
            <span className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <FormulaField
              fieldId={`${fieldId}.high`} angleModeKey={fieldId}
              hideEval
              value={v2}
              onChange={v => onChange(buildTrigger(trigHandle, 'range', v1, v))}
              variables={variables}
              rolledValues={rolledValues}
              placeholder={t('blocks.triggers.triggerRangeMax')}
              ariaLabel={t('blocks.triggers.triggerRangeMax')}
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        </>
      ) : (
        <FormulaField
          fieldId={fieldId}
          hideEval
          value={v1}
          onChange={v => onChange(buildTrigger(trigHandle, trigType, v, ''))}
          variables={variables}
          rolledValues={rolledValues}
          /* No placeholder, matching numerical/compass/angle. A greyed value in an empty condition
             field reads as a value that is already set. The range branch above KEEPS its Min/Max:
             those name which bound a box is, they do not suggest a value for it. */
          ariaLabel={t('blocks.slider.triggerValuePlaceholder')}
          className='w-full' 
          onFocus={onPlainFieldFocus}/>
      )}
      </>
      )}
    </div>
  );
}


/** `dualHandle` is deliberately REQUIRED, not defaulted. It used to default to `false`, and the one
 * caller that forgot to pass it wrote handle-B titles with no handle named — invisible here, but
 * Flow renders the persisted title, so it surfaced as two indistinguishable states over there.
 * A required argument turns that whole class of omission into a compile error. */

function SliderTriggerChip({ trigger, evaluation, dualHandle, scenario, angleMode }: {
  /** THIS trigger's own angle mode — it is a formula field like any other. The caller looks it
   *  up with `angleModeFor`, because only the block knows the map. */
  angleMode: 'degrees' | 'radians';

  trigger: string; evaluation: FeedbackState['evaluation']; dualHandle: boolean; scenario: Record<string, string>;
}) {
  const { t } = useTranslation();
  const chipColor =
    chipToneClass(evaluation);
  return (
    // The shared chip shape, like every other block: symbol, divider, then two-line-clamped text.
    // This was the last chip rendering colour with no icon and no clamp — colour alone is not a
    // distinction a colour-blind teacher can make, and an unclamped label overran the row.
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {chipEvalIcon(evaluation)}
      <ChipDivider />
      {/* The SAME derivation the persisted title uses, not a parallel one — in dual-handle mode a
        * bare summary made handle A and handle B chips identical while the title beside them said
        * "Handle 2:". One derivation is the standing rule precisely so they cannot drift. */}
      {/* `resolvedTriggerChip`, not `chipifyFormulaValues`: the latter substitutes into the
        * formula and stops, so this chip read `ᵗᵉˢᵗ400*5` while the test bar's legend — the same
        * condition, one derivation away — printed the finished number. Slider carried BOTH, which
        * is how the split went unnoticed: whichever surface you looked at was self-consistent. */}
      <span className={TRIGGER_CHIP_TEXT_CLASS}>{resolvedTriggerChip({
        trigger, scenario,
        summaryOf: resolved => sliderTargetedTitle(resolved, t, dualHandle),
        evaluate: (expr, values) => evaluateFormulaRaw(expr, values, angleMode),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      })}</span>
    </span>
  );
}

// ─── Collapsed-view display ────────────────────────────────────────────────────
// A real, disabled SliderTrack — not a bespoke decorative reimplementation — so the collapsed
// canvas card always matches the live/student-facing preview exactly (same tick marks, same
// label placement, same everything) by construction, with zero risk of the two drifting apart.
// Renders only the default/starting position(s), never a mid-drag preview state.

function SliderAnswerSummary({ d, t, locale, isPhone }: {
  d: SliderBlockData;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  isPhone: boolean;
}) {
  // The canvas card is the preview view, DISABLED — same length, same controls, same everything,
  // just not draggable. It was already `disabled` (see the prop further down); what made it differ
  // was `compact`, which drew a shorter track than the one a student gets, and a nudge pair that
  // was hidden rather than greyed. Both are gone, so the card is now a faithful picture.
  return (
    <SliderTrack
      // The card renders the pair greyed, so it still needs their labels — a screen reader
      // announcing an unlabelled button is worse than one announcing a disabled control.
      {...sliderTrackLabels(t, d.dualHandle)}
      min={num(d.min)} max={num(d.max)} scaleMode={d.scaleMode} stepSize={num(d.stepSize)} logStepsPerDecade={num(d.logStepsPerDecade)}
      value={num(d.defaultPosition)}
      onTouch={() => {}} onChange={() => {}} onCommit={() => {}}
      valueB={d.dualHandle ? num(d.handleB.defaultPosition) : undefined}
      onTouchB={() => {}} onChangeB={() => {}} onCommitB={() => {}}
      showLiveReadout={d.showLiveReadout}
      minLabel={d.showLabels ? d.minLabel : undefined} maxLabel={d.showLabels ? d.maxLabel : undefined}
      orientation={d.orientation} reverseDirection={d.reverseDirection}
      showTickMarks={d.showTickMarks}
      majorTickInterval={num(d.majorTickInterval)} minorTickInterval={num(d.minorTickInterval)}
      showMajorTickValues={shouldShowMajorTickValues(d.showMajorTickValues, d.scaleMode, num(d.min), num(d.max), num(d.majorTickInterval))}
      tickLabels={d.tickLabels.map(tl => ({ id: tl.id, value: num(tl.value), label: tl.label }))}
      formatValue={v => fmtLocale(v, locale, undefined, false, isPhone)}
      unitSuffix={d.unitSuffix}
      disabled
    />
  );
}

// Pure function returning the collapsed answer-bar content — mirrors
// numericalCollapsedBar's role (numerical-block.tsx).
/**
 * The collapsed card's answer bar.
 *
 * Renders the chip list it is HANDED, as plain text — it derives nothing. It used to hold its own
 * `summaryFor`/`summaryText` pair plus a separate `formulaBar` branch for unrolled equations, which
 * made it the last rival derivation in this block and the reason its display bar read
 * "Slider 1: x, Slider 2: y" while the editor bar beside it read "... and ...". `sliderChips`
 * already states both, with the connector that carries the meaning: the handles are CONJUNCTIVE,
 * so "and" is not decoration, and a comma left the reader to infer it.
 *
 * Priority order is the shared card convention and the reason this function still exists: the
 * highest-severity issue outranks the answer, so a block whose setup is incomplete shows what is
 * wrong rather than a summary computed from blank fields.
 */
function sliderCollapsedBar(
  d: SliderBlockData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  issues: BlockIssue[],
  answerText: React.ReactNode,
): React.ReactNode {
  const issueBar = collapsedIssueBar(issues);
  if (issueBar) return issueBar;
  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'slider');
  if (answerText === null) return null;
  return (
    <CollapsedBlockBar tone='success'>
      <span className='min-w-0'>{answerText}</span>
    </CollapsedBlockBar>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SliderBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: SliderBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level answer is vestigial once a bank exists. */
  /**
   * FOLLOWING A SCENARIO — the value family's shape, copied from Numerical, which is its
   * precedent. A row carries `SLIDER_VERSION_KEYS` flat, exactly as a version does, so the
   * editor resolves one the same way it resolves the other and `applyScenarioRow` is the only
   * new step.
   */
  const storedForLink = initData(block.data);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedForLink.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedForLink.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersions?.[0]?.id;

  /**
   * WHAT THE TEST BAR PAGES WHEN THIS BLOCK FOLLOWS — the leader's scenarios, not its own bank.
   *
   * Same shape `useScenarioFollower` returns for the blocks that use it, and the same one
   * Dropdown and MCQ already build. A follower has no bank of its own (§2.2 makes the two
   * exclusive), so its test bar had nothing to page and a teacher could not step the scenarios
   * from the surface they were testing on — the only pager was in Answer Setup. The pointer is
   * shared, so this and the leader's own card move together rather than disagreeing.
   */
  const testPager = storedForLink.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;
  /** A version FIRST, then the row on top — a follower has no bank, so only one ever applies. */
  const resolveForEditor = (d: SliderBlockData, versionId: string | undefined) =>
    applyScenarioRow(
      applySliderVersion(d, versionId) as unknown as Record<string, unknown>,
      shownScenarioId, SLIDER_VERSION_KEYS,
    ) as unknown as SliderBlockData;
  const initial = useRef(resolveForEditor(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read the push paths depend on; a ref cannot re-render, so restored HTML needs
   * state to reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedMin, setMin] = useState(initial.current.min);
  const [editedMax, setMax] = useState(initial.current.max);
  const [editedStepSize, setStepSize] = useState(initial.current.stepSize);
  const [editedLogStepsPerDecade, setLogStepsPerDecade] = useState(initial.current.logStepsPerDecade);
  // Editor-only display value for the field above — see logSigFigsFromStepsPerDecade.
  // Derived once at init, then its own independent controlled input from there (not recomputed
  // from logStepsPerDecade on every render, which would fight the teacher mid-edit).
  const [logSigFigs, setLogSigFigs] = useState(() => logSigFigsFromStepsPerDecade(initial.current.logStepsPerDecade));
  const [editedScaleMode, setScaleMode] = useState(initial.current.scaleMode);
  const [editedDefaultPosition, setDefaultPosition] = useState(initial.current.defaultPosition);
  const [editedConditionType, setConditionType] = useState(initial.current.conditionType);
  const [editedAnswers, setAnswers] = useState(initial.current.answers);
  const [editedToleranceMode, setToleranceMode] = useState(initial.current.toleranceMode);
  const [editedToleranceSymmetry, setToleranceSymmetry] = useState(initial.current.toleranceSymmetry);
  const [editedBetweenAnswers, setBetweenAnswers] = useState(initial.current.betweenAnswers);
  const [editedThreshold, setThreshold] = useState(initial.current.threshold);
  const [editedDualHandle, setDualHandle] = useState(initial.current.dualHandle);
  const [editedHandleBConditionType, setHandleBConditionType] = useState(initial.current.handleB.conditionType);
  const [editedHandleBAnswers, setHandleBAnswers] = useState(initial.current.handleB.answers);
  const [editedHandleBToleranceMode, setHandleBToleranceMode] = useState(initial.current.handleB.toleranceMode);
  const [editedHandleBToleranceSymmetry, setHandleBToleranceSymmetry] = useState(initial.current.handleB.toleranceSymmetry);
  const [editedHandleBBetweenAnswers, setHandleBBetweenAnswers] = useState(initial.current.handleB.betweenAnswers);
  const [editedHandleBThreshold, setHandleBThreshold] = useState(initial.current.handleB.threshold);
  const [editedHandleBDefaultPosition, setHandleBDefaultPosition] = useState(initial.current.handleB.defaultPosition);
  const [editedOrientation, setOrientation] = useState(initial.current.orientation);
  const [editedReverseDirection, setReverseDirection] = useState(initial.current.reverseDirection);
  const [editedMinLabel, setMinLabel] = useState(initial.current.minLabel);
  const [editedMaxLabel, setMaxLabel] = useState(initial.current.maxLabel);
  const [editedShowLabels, setShowLabels] = useState(initial.current.showLabels);
  const [editedShowTickMarks, setShowTickMarks] = useState(initial.current.showTickMarks);
  const [editedMajorTickInterval, setMajorTickInterval] = useState(initial.current.majorTickInterval);
  const [editedMinorTickInterval, setMinorTickInterval] = useState(initial.current.minorTickInterval);
  const [editedShowMajorTickValues, setShowMajorTickValues] = useState(initial.current.showMajorTickValues);
  const [editedTickLabels, setTickLabels] = useState(initial.current.tickLabels);
  const [editedUnitSuffix, setUnitSuffix] = useState(initial.current.unitSuffix);
  const [editedShowLiveReadout, setShowLiveReadout] = useState(initial.current.showLiveReadout);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);

  // Shared toolbar state — InputBlockFrame renders one toolbar box (anchored at the top of
  // whichever of editingSlot/the Feedback panel is showing) that targets whichever RichTextEditor
  // last received focus, regardless of which handle (A or B) is currently active in the Feedback
  // panel's Handle 1/Handle 2 switcher below. Exactly one of these for the whole block — never two.
  const sharedToolbar = useSharedEditorToolbar();
  // No `useFocusedField` any more — NumberField decides when a fault is visible, by the one
  // rule every field shares. This block's inputs were its last consumer here.
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Every plain <input> in this block focuses through here: without it the shared toolbar stays
  // pointed at whichever RichTextEditor was last active (the Question), so pressing Bold with the
  // cursor in a numeric field silently formats the question instead. The toolbar also ghosts itself
  // once this clears activeEditor — input-block-frame.tsx already drives `toolbarVisible` from it,
  // so disconnecting is all that's needed to get the greyed-out state too.
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const questionEditorRef = useRef<Editor | null>(null);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   *
   * Thirty-five values rather than numerical's one, which is the only thing that makes this block
   * different: the rule is identical, there is just more of it.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml,
    min: editedMin,
    max: editedMax,
    stepSize: editedStepSize,
    logStepsPerDecade: editedLogStepsPerDecade,
    scaleMode: editedScaleMode,
    defaultPosition: editedDefaultPosition,
    conditionType: editedConditionType,
    answers: editedAnswers,
    toleranceMode: editedToleranceMode,
    toleranceSymmetry: editedToleranceSymmetry,
    betweenAnswers: editedBetweenAnswers,
    threshold: editedThreshold,
    dualHandle: editedDualHandle,
    orientation: editedOrientation,
    reverseDirection: editedReverseDirection,
    minLabel: editedMinLabel,
    maxLabel: editedMaxLabel,
    showLabels: editedShowLabels,
    showTickMarks: editedShowTickMarks,
    majorTickInterval: editedMajorTickInterval,
    minorTickInterval: editedMinorTickInterval,
    showMajorTickValues: editedShowMajorTickValues,
    tickLabels: editedTickLabels,
    unitSuffix: editedUnitSuffix,
    showLiveReadout: editedShowLiveReadout,
    gradingMode: editedGradingMode,
    handleB: { conditionType: editedHandleBConditionType, answers: editedHandleBAnswers, toleranceMode: editedHandleBToleranceMode, toleranceSymmetry: editedHandleBToleranceSymmetry, betweenAnswers: editedHandleBBetweenAnswers, threshold: editedHandleBThreshold, defaultPosition: editedHandleBDefaultPosition },
  };
  const restored = useRestoredData<SliderBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => resolveForEditor(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...resolveForEditor(initData(block.data), editorVersion), ...edited } as SliderBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: SliderBlockData) {
      setMin(next.min);
      setMax(next.max);
      setStepSize(next.stepSize);
      setLogStepsPerDecade(next.logStepsPerDecade);
      setScaleMode(next.scaleMode);
      setDefaultPosition(next.defaultPosition);
      setConditionType(next.conditionType);
      setAnswers(next.answers);
      setToleranceMode(next.toleranceMode);
      setToleranceSymmetry(next.toleranceSymmetry);
      setBetweenAnswers(next.betweenAnswers);
      setThreshold(next.threshold);
      setDualHandle(next.dualHandle);
      setOrientation(next.orientation);
      setReverseDirection(next.reverseDirection);
      setMinLabel(next.minLabel);
      setMaxLabel(next.maxLabel);
      setShowLabels(next.showLabels);
      setShowTickMarks(next.showTickMarks);
      setMajorTickInterval(next.majorTickInterval);
      setMinorTickInterval(next.minorTickInterval);
      setShowMajorTickValues(next.showMajorTickValues);
      setTickLabels(next.tickLabels);
      setUnitSuffix(next.unitSuffix);
      setShowLiveReadout(next.showLiveReadout);
      setGradingMode(next.gradingMode);
      setHandleBConditionType(next.handleB.conditionType);
      setHandleBAnswers(next.handleB.answers);
      setHandleBToleranceMode(next.handleB.toleranceMode);
      setHandleBToleranceSymmetry(next.handleB.toleranceSymmetry);
      setHandleBBetweenAnswers(next.handleB.betweenAnswers);
      setHandleBThreshold(next.handleB.threshold);
      setHandleBDefaultPosition(next.handleB.defaultPosition);
      // Editor-only display value, derived from the field above rather than stored — so it has to
      // be re-derived here too, or an undo of the step size leaves this box reading the old one.
      setLogSigFigs(logSigFigsFromStepsPerDecade(next.logStepsPerDecade));
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(resolveForEditor(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(resolveForEditor(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = () => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump();
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const min = isEditing ? editedMin : restored.data.min;
  const max = isEditing ? editedMax : restored.data.max;
  const stepSize = isEditing ? editedStepSize : restored.data.stepSize;
  const logStepsPerDecade = isEditing ? editedLogStepsPerDecade : restored.data.logStepsPerDecade;
  const scaleMode = isEditing ? editedScaleMode : restored.data.scaleMode;
  /**
   * A log scale is the Extra Block Setting; `linear` is the default and stays. The difficulty is
   * not the setting but what it then asks the teacher to reason about — which values are
   * reachable becomes `logStepsPerDecade` rather than a step size, a minimum at or below zero is
   * undefined, and the tick labels a student reads are no longer evenly spaced.
   */
  const showScaleMode = useExtraBlockSetting(
    scaleMode === 'log' || initial.current.scaleMode === 'log');
  const defaultPosition = isEditing ? editedDefaultPosition : restored.data.defaultPosition;
  const conditionType = isEditing ? editedConditionType : restored.data.conditionType;
  const answers = isEditing ? editedAnswers : restored.data.answers;
  const toleranceMode = isEditing ? editedToleranceMode : restored.data.toleranceMode;
  const toleranceSymmetry = isEditing ? editedToleranceSymmetry : restored.data.toleranceSymmetry;
  const betweenAnswers = isEditing ? editedBetweenAnswers : restored.data.betweenAnswers;
  const threshold = isEditing ? editedThreshold : restored.data.threshold;
  const dualHandle = isEditing ? editedDualHandle : restored.data.dualHandle;
  const orientation = isEditing ? editedOrientation : restored.data.orientation;
  const reverseDirection = isEditing ? editedReverseDirection : restored.data.reverseDirection;
  const minLabel = isEditing ? editedMinLabel : restored.data.minLabel;
  const maxLabel = isEditing ? editedMaxLabel : restored.data.maxLabel;
  const showLabels = isEditing ? editedShowLabels : restored.data.showLabels;
  const showTickMarks = isEditing ? editedShowTickMarks : restored.data.showTickMarks;
  const majorTickInterval = isEditing ? editedMajorTickInterval : restored.data.majorTickInterval;
  const minorTickInterval = isEditing ? editedMinorTickInterval : restored.data.minorTickInterval;
  const showMajorTickValues = isEditing ? editedShowMajorTickValues : restored.data.showMajorTickValues;
  const tickLabels = isEditing ? editedTickLabels : restored.data.tickLabels;
  const unitSuffix = isEditing ? editedUnitSuffix : restored.data.unitSuffix;
  const showLiveReadout = isEditing ? editedShowLiveReadout : restored.data.showLiveReadout;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const handleBConditionType = isEditing ? editedHandleBConditionType : restored.data.handleB.conditionType;
  const handleBAnswers = isEditing ? editedHandleBAnswers : restored.data.handleB.answers;
  const handleBToleranceMode = isEditing ? editedHandleBToleranceMode : restored.data.handleB.toleranceMode;
  const handleBToleranceSymmetry = isEditing ? editedHandleBToleranceSymmetry : restored.data.handleB.toleranceSymmetry;
  const handleBBetweenAnswers = isEditing ? editedHandleBBetweenAnswers : restored.data.handleB.betweenAnswers;
  const handleBThreshold = isEditing ? editedHandleBThreshold : restored.data.handleB.threshold;
  const handleBDefaultPosition = isEditing ? editedHandleBDefaultPosition : restored.data.handleB.defaultPosition;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addSliderVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addSliderVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /** Every field write, routed into the version it belongs to — see mcq-block.tsx's note. */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  /**
   * Link and unlink, adapted from Numerical — the value family's precedent, and the only block
   * of the four that had this. Every comment there applies here verbatim, so only what is
   * Slider's own is restated: the key set is `SLIDER_VERSION_KEYS`, and the editor's fields are
   * re-seeded through `seedEditorFrom` rather than a single `setFields`.
   */
  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data);
    // A bank is the one case that needs a decision before anything is written — §7.1.
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      emit(versionStructure(block.data, {
        ...current, followsBlockId: undefined, scenarioBranches: undefined,
      } as unknown as Record<string, unknown>));
      return;
    }
    const leaderBank = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /* COLLAPSE THE BANK TO VERSION 1 FIRST — `initData`'s own copy is NOT version 1's, because
       `writeFieldVersion` froze the block's top level when the first version was made. The
       modal promises version 1, so version 1 is what has to be resolved. */
    const v1 = current.versions?.[0];
    const base = (current.versions?.length ?? 0) > 1 && v1
      ? applySliderVersion(current, v1.id)
      : current;
    /* Slider's feedback rides outside its data, as Numerical's does, so applying a version
       cannot bring it along — it is lifted off by hand. */
    const baseFeedback = ((current.versions?.length ?? 0) > 1
      ? (v1?.feedback as { states: FeedbackState[] } | undefined) : undefined) ?? feedback;
    const fromBlock = () => ({
      feedback: baseFeedback,
      ...Object.fromEntries(SLIDER_VERSION_KEYS.map(k => [k, (base as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>);
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<FieldVersion<{ states: FeedbackState[] }>, ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.versions,
        v => ({
          feedback: v.feedback,
          ...Object.fromEntries(SLIDER_VERSION_KEYS.filter(k => k in v).map(k => [k, (v as unknown as Record<string, unknown>)[k]])),
        } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
        fromBlock,
      )
      : syncScenarioBranches<ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.scenarioBranches, fromBlock,
      );
    // A row arriving with no feedback gets the block's, so relinking repairs rather than
    // faithfully restoring an unanswerable scenario.
    const healed = branches.map((b): ScenarioFieldBranch<{ states: FeedbackState[] }> =>
      (b.feedback?.states?.length ? b : { ...b, feedback: baseFeedback }));
    seedEditorFrom(base);
    resyncFeedbackRef.current?.(baseFeedback);
    emit(versionStructure(block.data, {
      ...base, feedback: baseFeedback, followsBlockId, scenarioBranches: healed, versions: undefined,
    } as unknown as Record<string, unknown>));
  }

  /** The projection, kept live — a variant added to the leader becomes a row here (§4.1). */
  useScenarioRowProjection<ScenarioFieldBranch<{ states: FeedbackState[] }>>({
    followsBlockId: storedForLink.followsBlockId,
    blocks,
    rows: storedForLink.scenarioBranches,
    template: () => ({
      feedback,
      ...Object.fromEntries(SLIDER_VERSION_KEYS.map(k => [k, (fields as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
    onSync: (next) => {
      const current = initData(block.data);
      emit(versionStructure(block.data, {
        ...current, scenarioBranches: next,
      } as unknown as Record<string, unknown>));
    },
  });

  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={initData(block.data).versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  const linkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedForLink.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applySliderVersion}
    />
  );

  /** The row on screen — graded-only, like every answer key, read off the LEADER's pointer. */
  const activeScenario = gradingMode === 'graded' && storedForLink.scenarioBranches?.length
    ? (storedForLink.scenarioBranches.find(b => b.scenarioId === shownScenarioId)
      ?? storedForLink.scenarioBranches[0])
    : undefined;
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) as { name?: string } | null : null;
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={(leaderVersions ?? []) as { id: string }[]}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;
  /**
   * The ghosted stem for the TEST surface, read off the shared pointer rather than the editor's.
   *
   * Deliberately NOT `activeScenarioVersion`, which is gated on graded mode because it names
   * the row whose ANSWER KEY is being authored. The prose is a different question: a
   * participation block still asks about the scenario, and a teacher testing one still has to
   * be able to read it.
   */
  /* Never in Preview — see `useScenarioFollower`'s own note on why this is not the call
     site's decision to make. */
  const ghostedScenario = isPreview ? null : (
    <ScenarioStimulus
      leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
      version={scenarioInForce(leaderVersions, shownScenarioId) as Record<string, unknown> | null}
      ghosted
    />
  );
  const dismissUnreviewed = () => {
    const current = initData(block.data);
    emit(versionStructure(block.data, {
      ...current,
      scenarioBranches: markScenarioReviewed(current.scenarioBranches ?? [], activeScenario!.scenarioId),
    } as unknown as Record<string, unknown>));
  };

  const emitFields = (next: Partial<SliderBlockData>) => {
    const base = initData(block.data);
    /*
     * A SCENARIO ROW takes the write before a version can, and the two cannot both apply (§2.2).
     *
     * The same bargain `writeSliderVersion` makes, one selector over: the answer-key fields go
     * into the row showing, the shared ones stay on the block. Copied from Numerical, the value
     * family's precedent — including the feedback, which is scenario-specific for the same
     * reason the answer is.
     */
    if (base.scenarioBranches?.length) {
      const merged = { ...base, ...next } as SliderBlockData;
      const written = writeScenarioRow(
        base as unknown as Record<string, unknown>, shownScenarioId,
        merged as unknown as Record<string, unknown>, SLIDER_VERSION_KEYS,
      ) as Record<string, unknown>;
      if ('feedback' in next && shownScenarioId) {
        written.scenarioBranches = (written.scenarioBranches as ScenarioFieldBranch<{ states: FeedbackState[] }>[])
          .map(r => (r.scenarioId === shownScenarioId
            ? { ...r, feedback: next.feedback as { states: FeedbackState[] }, unreviewed: undefined } : r));
      }
      emit(written);
      return;
    }
    emit(writeSliderVersion(base, editorVersion, { ...base, ...next } as SliderBlockData) as unknown as Record<string, unknown>);
  };

  const fields: SliderBlockData = {
    question: questionHtml, min, max, stepSize, logStepsPerDecade, scaleMode, defaultPosition, conditionType,
    answers, toleranceMode, toleranceSymmetry, betweenAnswers, threshold,
    dualHandle,
    handleB: {
      conditionType: handleBConditionType, answers: handleBAnswers, toleranceMode: handleBToleranceMode,
      toleranceSymmetry: handleBToleranceSymmetry, betweenAnswers: handleBBetweenAnswers, threshold: handleBThreshold,
      defaultPosition: handleBDefaultPosition,
    },
    orientation, reverseDirection,
    minLabel, maxLabel, showLabels, showTickMarks, majorTickInterval, minorTickInterval, showMajorTickValues, tickLabels,
    unitSuffix, showLiveReadout, gradingMode,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // Answer equations wait for the teacher to stop typing before being called malformed — every
  // formula is invalid partway through. Keyed on `fields`, the live editor state, and on
  // `block.data` for the card, because those are the two things the two issue lists are built
  // from. No `equation`: this block has no branching selector, only equation-valued answers.
  const { settleLive, settleStored } = useIssueSettling(
    { answers: answerPositionsKey(fields, fields.dualHandle ? fields.handleB : null) },
    { answers: (() => { const sd = initData(block.data); return answerPositionsKey(sd, sd.dualHandle ? sd.handleB : null); })() },
  );

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  // Shared by both handles — gradingMode is one field on SliderBlockData, not per-handle.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle = positiveSlotTitleFor(gradingMode, t);

  const { feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
    },
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const d = currentDataRef.current;
      emitFields({ ...d, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  // Handle B has NO feedback set of its own. A dual-handle slider is two inputs with one verdict
  // (see evaluateSlider), so there is a single set whose targeted states name their handle. The old
  // second instance could be authored and could never fire, and its
  // `currentDataRef.current.handleB.feedback` was permanently a stale placeholder that every writer
  // had to remember to re-merge or silently wipe. Both hazards are gone with it.
  function push(overrides: Partial<SliderBlockData> = {}) {
    emitFields({ ...currentDataRef.current, feedback, ...overrides });
  }

  function pushHandleB(patch: Partial<HandleBConfig> = {}) {
    const d = currentDataRef.current;
    emitFields({ ...d, feedback, handleB: { ...d.handleB, ...patch } });
  }

  // ── Live test state ─────────────────────────────────────────────────────────
  // Two snapshots, exactly as FITB/Scale: `testValue*` is what the handle is showing right now,
  // `submitted*` is what was last evaluated. Keeping them apart is what makes non-continuous mode
  // possible at all — otherwise dragging would re-grade on every pixel.
  const initialA = num(initial.current.defaultPosition);
  const initialB = num(initial.current.handleB.defaultPosition);
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   *
   * `touched` carries across too, and has to: an untouched slider sitting at the same number as a
   * deliberate answer is a different answer, which is the whole reason the flag exists.
   */
  const prior = usePriorAnswer<{ a: SliderAnswer; b: SliderAnswer }>();
  const [testValue, setTestValue] = useState(prior?.a.value ?? initialA);
  const [testValueB, setTestValueB] = useState(prior?.b.value ?? initialB);
  const [testTouched, setTestTouched] = useState(prior?.a.touched ?? false);
  const [testTouchedB, setTestTouchedB] = useState(prior?.b.touched ?? false);
  const [submitted, setSubmitted] = useState<{ a: SliderAnswer; b: SliderAnswer } | null>(prior ?? null);
  // The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
  // of the student's dragging rather than of what they chose to hand in.
  useReportAnswer(submitted ?? undefined);
  const [continuous, setContinuous] = useState(true);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  const [variableTestMode, setVariableTestMode] = useState<'min' | 'max' | 'random' | null>(null);
  /** Whether the collapsed answer bar is showing resolved values rather than "answer is an
   * equation". Separate from the feedback panel's test mode: this is the card's own preview. */

  // The three-mode resolution model, identical to dropdown/numerical (see
  // TaskPreviewRollContext). Preview must show ONE task-level roll shared by every block — a task
  // whose blocks all read {n} has to agree about n — while block-level testing keeps its own
  // private roll, which is the whole point of it: a teacher steps through combinations for the one
  // block they are testing. Display (neither selected nor previewing) resolves nothing and shows
  // chips. Slider was the last input block with no variable context at all.
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  // Rolled EAGERLY at mount, as numerical does, not lazily on selection. An empty roll is not a
  // neutral starting state: `{n}*5` cannot evaluate without a value for n, so the answer field
  // rendered "Invalid expression" until something happened to roll. Every mount having a roll from
  // the first render makes that impossible; selection and the phone Test tab then RE-roll.
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  const taskPreviewRoll = useTaskPreviewRoll();
  /* Recalled answers ride the same map the variables do — see `useRolledWithRecalls`. The
     equation field, the answer bar and the issue list then all read one roll. */
  const rolledValues = useRolledWithRecalls(isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues);
  /** The ONE place an answer field becomes a number. Plain numbers pass straight through, since
   * `evaluateFormulaRaw` evaluates "42" to 42 — so this replaces `num()` at the answer sites
   * without special-casing whether the teacher typed a formula. Tolerance and track geometry keep
   * `num()`: they are numeric by decision, not by accident. */
  const resolveAnswer = (s: string, fieldId = ''): number =>
    evaluateFormulaRaw(s, rolledValues, angleModeFor(fields, fieldId));

  /**
   * The editor's issue list, computed ONCE.
   *
   * It was computed twice with identical inputs (the phone Test tab and the warning bar), and
   * `settleLive` writes its `pending` set on every call, so the second overwrote the first — same
   * result today because the inputs matched, and silently last-one-wins if they ever stopped.
   *
   * Hoisting it also gives the answer rows something to ring FROM. The crossed-answers highlight
   * used to call `sliderAnswersCrossed` itself, a second call site of the function the issue check
   * already uses, with nothing forcing the two to keep matching arguments — the same shape as
   * ordering's bar dropping `itemsAsEquations`. Now the check runs once and the ring reads which
   * fields it named.
   */
  const editorIssues = settleLive(sliderIssues(fields, { ...issueCtx, resolve: resolveAnswer }));
  /** Which answer rows any issue points at — the one derivation the rings read. */
  const flaggedAnswerIds = issueFieldIds(editorIssues);
  /** Whether any ANSWER field carries a variable chip — the gate for showing the test controller at
   * all. Tolerance and track geometry are excluded by design, so a chip could never appear there. */
  /** The authored answer expressions carrying chips — what the rolled bar renders with values
   * substituted, in the order a teacher wrote them. */
  // (The formula-text collection that used to live here fed ONLY the collapsed card, which is a
  // display surface and must read persisted data — it now derives its own from `d`. Nothing in the
  // editor ever needed it.)
  /** Every answer field holding a formula, across both handles — the list the scenario bar prints
   *  and the predicate below reads, so the two cannot disagree about what varies. */
  /** Each formula answer WITH its field id, so the readout resolves each in its own mode. */
  const formulaAnswerRows: { value: string; fieldId: string }[] = (() => {
    const from = (src: { answers: ExactAnswer[]; betweenAnswers: BetweenAnswer[]; threshold: string }) => [
      ...src.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` })),
      ...src.betweenAnswers.flatMap(a => [
        { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }]),
      { value: src.threshold, fieldId: 'threshold' },
    ];
    return [...from(fields), ...(fields.dualHandle ? from(fields.handleB) : [])]
      .filter(r => hasVariableChip(r.value));
  })();
  const formulaAnswerFields: string[] = formulaAnswerRows.map(r => r.value);
  const hasFormulaAnswer = formulaAnswerFields.length > 0;

  const currentAnswers = () => ({
    a: { value: testValue, touched: testTouched },
    b: { value: testValueB, touched: testTouchedB },
  });

  function handleTestSubmit() { setSubmitted(currentAnswers()); }
  function handleTestReset() {
    setTestValue(num(currentDataRef.current.defaultPosition));
    setTestValueB(num(currentDataRef.current.handleB.defaultPosition));
    setTestTouched(false); setTestTouchedB(false);
    setSubmitted(null);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) setSubmitted(currentAnswers());
  }
  /** Commits a handle's position, and in continuous mode grades immediately. Takes the value
   * explicitly rather than reading state, since the setter has not flushed yet at call time. */
  function commitTest(handle: SliderHandleId, value: number) {
    const next = handle === 'b'
      ? { a: { value: testValue, touched: testTouched }, b: { value, touched: true } }
      : { a: { value, touched: true }, b: { value: testValueB, touched: testTouchedB } };
    if (handle === 'b') { setTestValueB(value); setTestTouchedB(true); } else { setTestValue(value); setTestTouched(true); }
    // `&& !isPreview` — continuous testing is a TEACHER affordance (the toggle lives in the test
    // bar), and Preview reuses this same change handler for the student's input. Without the
    // guard, typing an answer graded it instantly: feedback appeared before Submit was ever
    // clicked, which is not what a student sees. Preview submits through previewSubmitSignal
    // and nothing else.
    if (continuous && !isPreview) setSubmitted(next);
  }

  /**
   * DESELECTING resets the test, so the next teacher to open the card sees a clean surface rather
   * than someone else's half-finished answer — same convention as every other tested block.
   *
   * ON THE TRANSITION, never on mount. An effect keyed on `isSelected` also runs the first time,
   * and in Preview a block is ALWAYS unselected — so this fired on arrival and wiped the answer
   * `usePriorAnswer` had just seeded. That is why a revisit showed an empty box however the
   * task's On Return policy was set: the seeding worked and was erased a tick later.
   *
   * `wasSelected` makes the effect answer "did it just become unselected" rather than "is it
   * unselected", which is what the comment above always meant.
   */
  const wasSelected = useRef(isSelected);
  useEffect(() => {
    if (isSelected) setRolledValues(rollVariableValues(variables));
    else if (wasSelected.current) { handleTestReset(); setPhoneTestMode('edit'); }
    wasSelected.current = isSelected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  // Phone's own entry into the Test tab. `isSelected` is always false on phone (selection is
  // tracked via mobileEditingId), so the effect above never fires there — without this, formula
  // answers would sit unresolved until the teacher hit Reroll. Same pairing every other
  // variable-aware block has.
  useEffect(() => {
    if (phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);

  // Preview seeds from the task-level roll, so nothing to roll here; but a Preview mount that never
  // rolls would leave `ownRolledValues` empty, which is correct precisely because `rolledValues`
  // reads `taskPreviewRoll` in that mode.

  // Preview's single page-level Submit. Reacts to the signal CHANGING, never to a per-block click.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setSubmitted(currentAnswers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  // Refocuses the add-button after a tick label is deleted, so keyboard/screen-reader context
  // isn't silently dropped to <body> — same convention as AnswerSetupFields' own add-answer refs.
  const addTickLabelBtnRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  // Live drag preview during authoring — purely local, not persisted.
  const [previewValue, setPreviewValue] = useState(num(defaultPosition));
  const [previewValueB, setPreviewValueB] = useState(num(handleBDefaultPosition));

  // Armed-delete state for answer/between-answer rows — matches numerical-block.tsx's
  // exact pattern (single shared id per list, not per-row local state; see
  // feedback_mcq_style_reference memory — this is a hard rule, not a style choice).
  const [armedAnswerId, setArmedAnswerId] = useState<string | null>(null);
  const [armedBetweenId, setArmedBetweenId] = useState<string | null>(null);
  const [armedHandleBAnswerId, setArmedHandleBAnswerId] = useState<string | null>(null);
  const [armedHandleBBetweenId, setArmedHandleBBetweenId] = useState<string | null>(null);
  const [armedTickId, setArmedTickId] = useState<string | null>(null);

  // Which handle's Answer Setup / Feedback panel is currently shown when dualHandle is on —
  // avoids doubling the vertical space of both sections at once.

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleGradingMode(mode: SliderBlockData['gradingMode']) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push({
      gradingMode: mode,
      feedback: { states: nextFeedbackStates },
    });
  }

  function handleScaleMode(mode: SliderScaleMode) {
    setScaleMode(mode);
    // Percentage tolerance is effectively required in log mode (an absolute ± is meaningless
    // across a huge dynamic range) — nudge on toggle, not a hard lock; teacher can switch back.
    // Scale mode is shared by both handles (one track), so both handles' tolerance get nudged,
    // not just the active one. Log mode also requires min > 0 — nudge min and any default
    // position that's currently blank/non-positive to 1 (a harmless, always-valid starting
    // point), same reasoning. Built as a single onUpdate call (rather than push() followed by
    // pushHandleB()) since each of those reads currentDataRef.current independently — calling
    // both back to back would let the second call's stale snapshot clobber the first's change.
    /* Per answer now: log mode makes an ABSOLUTE band incoherent, so every answer still on
       absolute is moved, not just a single handle-level setting. */
    const toPct = (list: typeof answers, scope: { toleranceMode?: ToleranceMode }) =>
      list.map(a => answerToleranceMode(a, scope, 'absolute') === 'absolute' ? { ...a, toleranceMode: 'percentage' as const } : a);
    const nudgeA = mode === 'log' && answers.some(a => answerToleranceMode(a, restored.data, 'absolute') === 'absolute');
    const nudgeB = mode === 'log' && handleBAnswers.some(a => answerToleranceMode(a, restored.data.handleB, 'absolute') === 'absolute');
    const minP = parseFloat(min);
    const nudgeMin = mode === 'log' && !(Number.isFinite(minP) && minP > 0);
    const posP = parseFloat(defaultPosition);
    const nudgePos = mode === 'log' && !(Number.isFinite(posP) && posP > 0);
    const posBP = parseFloat(handleBDefaultPosition);
    const nudgePosB = mode === 'log' && dualHandle && !(Number.isFinite(posBP) && posBP > 0);
    const nudgedA = nudgeA ? toPct(answers, restored.data) : answers;
    const nudgedB = nudgeB ? toPct(handleBAnswers, restored.data.handleB) : handleBAnswers;
    if (nudgeA) setAnswers(nudgedA);
    if (nudgeB) setHandleBAnswers(nudgedB);
    if (nudgeMin) setMin('1');
    if (nudgePos) setDefaultPosition('1');
    if (nudgePosB) setHandleBDefaultPosition('1');
    const d = currentDataRef.current;
    emitFields({
      ...d, scaleMode: mode, feedback,
      ...(nudgeA ? { answers: nudgedA } : {}),
      ...(nudgeMin ? { min: '1' } : {}),
      ...(nudgePos ? { defaultPosition: '1' } : {}),
      handleB: {
        ...d.handleB,
        ...(nudgeB ? { answers: nudgedB } : {}),
        ...(nudgePosB ? { defaultPosition: '1' } : {}),
      },
    });
  }

  // ── Variable test controller ────────────────────────────────────────────────
  // Lets the teacher resolve formula answers against a concrete scenario, so the card and the
  // feedback chips show real numbers to check targeted feedback against — without implying any one
  // scenario is "the" answer.
  //
  // Unlike numerical, this drives the block's OWN roll (`ownRolledValues`) rather than a second,
  // parallel scenario state. Slider's test surface IS the card, so a separate roll would mean the
  // card and the feedback panel could sit on different values for the same block — the same class
  // of split-source bug the collapsed bar already had over severity.
  const variableExtrema = (() => {
    if (!hasFormulaAnswer) return null;
    const cfgFor = (src: SliderBlockData | SliderBlockData['handleB']) => ({
      conditionType: src.conditionType,
      answers: src.answers.map(a => ({ value: a.value })),
      betweenAnswers: src.betweenAnswers.map(a => ({ low: a.low, high: a.high })),
      threshold: src.threshold,
      toleranceMode: 'absolute' as const,
      toleranceSymmetry: 'symmetric' as const,
      angleMode: 'radians' as const,
    });
    const live = currentDataRef.current;
    const a = computeFormulaExtrema(cfgFor(live), variables);
    if (!live.dualHandle) return a;
    const b = computeFormulaExtrema(cfgFor(live.handleB), variables);
    if (!a || !b) return a ?? b;
    // One roll drives both handles, so the scenario has to be the envelope ACROSS them — picking
    // handle A's extreme would leave handle B showing an arbitrary middle value.
    return {
      lo: Math.min(a.lo, b.lo), hi: Math.max(a.hi, b.hi),
      loValues: a.lo <= b.lo ? a.loValues : b.loValues,
      hiValues: a.hi >= b.hi ? a.hiValues : b.hiValues,
    };
  })();

  function applyVariableTestMode(mode: 'min' | 'max' | 'random') {
    if (mode === 'random') setRolledValues(rollVariableValues(variables));
    else if (variableExtrema) setRolledValues(mode === 'min' ? variableExtrema.loValues : variableExtrema.hiValues);
    else return;
    setVariableTestMode(mode);
  }

  /**
   * `equation → value`, the same line the other three scenario bars carry.
   *
   * Slider drives the BLOCK'S OWN roll rather than a separate scenario — its card is the test
   * surface, so a second roll state would be a second thing to keep in sync — which makes this line
   * more useful here than anywhere, not less: the number on the track and the numbers in the chips
   * both came out of this, and until now nothing said what it was.
   */
  const scenarioCalculation: React.ReactNode = variableTestMode === null ? null : (
    <div className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-ws-text'>
      <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
        {formulaAnswerFields.map((f, i) =>
          <span key={i}><FormulaWithValues formula={f} rolledValues={rolledValues} /></span>)}
      </span>
      {(() => {
        const resolved = formulaAnswerRows
          .map(r => evaluateFormulaRaw(r.value, rolledValues, angleModeFor(fields, r.fieldId)))
          .filter(Number.isFinite);
        return resolved.length === 0 ? null : (
          <span className='font-semibold tabular-nums'>
            {' → '}{resolved.map(n => fmtLocale(n, i18n.language)).join(', ')}
          </span>
        );
      })()}
    </div>
  );

  const variableTestController: React.ReactNode = !hasFormulaAnswer ? null : (
    <VariableTestController
      mode={variableTestMode}
      onSelect={applyVariableTestMode}
      // Slider DOES have a no-scenario resting state — `variableTestMode === null`, where the
      // feedback chips show `{n}` rather than one roll — it just had no way back to it once a
      // scenario was picked. `onClear` was left off on the belief that no such state existed.
      onClear={() => setVariableTestMode(null)}
      extremaAvailable={!!variableExtrema}
      calculation={scenarioCalculation}
    />
  );

  function handleDualHandle(next: boolean) {
    setDualHandle(next);
    // Handle B starts out with its own default ('absolute') toleranceMode — if the slider is
    // already in log mode when dual-handle gets turned on, nudge it to percentage too, same
    // reasoning as handleScaleMode's nudge (single onUpdate call, same stale-ref-clobber concern).
    const nudgeB = next && scaleMode === 'log' && handleBToleranceMode === 'absolute';
    if (nudgeB) setHandleBToleranceMode('percentage');
    const d = currentDataRef.current;
    // Titles carry the "Handle 1/2:" prefix ONLY in dual-handle mode, so toggling changes what
    // every existing targeted title should read — and Flow renders the PERSISTED title, so a stale
    // one is wrong everywhere Flow looks, not just here. Same resync the sensor blocks run on an
    // answer-setup edit, for the same reason.
    const resynced = { states: feedback.states.map(st => st.type === 'targeted'
      ? { ...st, title: sliderTargetedTitle(st.trigger, t, next) }
      : st) };
    setFeedback(resynced);
    emitFields({
      ...d, dualHandle: next, feedback: resynced,
      handleB: { ...d.handleB, ...(nudgeB ? { toleranceMode: 'percentage' as const } : {}) },
    });
  }

  function updateHandleBAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = handleBAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setHandleBAnswers(next);
    pushHandleB({ answers: next });
  }

  function addHandleBAnswer() {
    const next = [...handleBAnswers, makeExactAnswer()];
    setHandleBAnswers(next);
    pushHandleB({ answers: next });
  }

  function removeHandleBAnswer(id: string) {
    if (handleBAnswers.length <= 1) return;
    const next = handleBAnswers.filter(a => a.id !== id);
    setHandleBAnswers(next);
    pushHandleB({ answers: next });
  }

  function updateHandleBBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = handleBBetweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setHandleBBetweenAnswers(next);
    pushHandleB({ betweenAnswers: next });
  }

  function addHandleBBetweenAnswer() {
    const next = [...handleBBetweenAnswers, makeBetweenAnswer()];
    setHandleBBetweenAnswers(next);
    pushHandleB({ betweenAnswers: next });
  }

  function removeHandleBBetweenAnswer(id: string) {
    if (handleBBetweenAnswers.length <= 1) return;
    const next = handleBBetweenAnswers.filter(a => a.id !== id);
    setHandleBBetweenAnswers(next);
    pushHandleB({ betweenAnswers: next });
  }

  function updateTickLabel(id: string, patch: Partial<TickLabelRow>) {
    const next = tickLabels.map(tl => tl.id === id ? { ...tl, ...patch } : tl);
    setTickLabels(next);
    push({ tickLabels: next });
  }

  function addTickLabel() {
    const next = [...tickLabels, makeTickLabel()];
    setTickLabels(next);
    push({ tickLabels: next });
  }

  function removeTickLabel(id: string) {
    const next = tickLabels.filter(tl => tl.id !== id);
    setTickLabels(next);
    push({ tickLabels: next });
  }

  function updateAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = answers.map(a => a.id === id ? { ...a, ...patch } : a);
    setAnswers(next);
    push({ answers: next });
  }

  function addAnswer() {
    const next = [...answers, makeExactAnswer()];
    setAnswers(next);
    push({ answers: next });
  }

  function removeAnswer(id: string) {
    if (answers.length <= 1) return;
    const next = answers.filter(a => a.id !== id);
    setAnswers(next);
    push({ answers: next });
  }

  function updateBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = betweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }

  function addBetweenAnswer() {
    const next = [...betweenAnswers, makeBetweenAnswer()];
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }

  function removeBetweenAnswer(id: string) {
    if (betweenAnswers.length <= 1) return;
    const next = betweenAnswers.filter(a => a.id !== id);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = feedback.states.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** Targeted states for one handle, in authored order — what the Feedback panel shows while that
   * handle is selected. The stored set interleaves both handles' states; the prefix is the filter. */
  function targetedStatesFor(handle: SliderHandleId): FeedbackState[] {
    return feedback.states.filter(st =>
      st.type === 'targeted' && parseSliderHandleTrigger(st.trigger).handle === handle);
  }

  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedStateFor(handle: SliderHandleId) {
    // Blank, not `exact:0`. A seeded 0 is a real condition that fires on a real answer, and it
    // reads as a value the teacher already chose. Same reason the counting loop is gone: it only
    // existed to keep the invented numbers apart.
    const trigger = buildSliderHandleTrigger(handle, 'exact:');
    const targeteds = feedback.states.filter(st => st.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      // `dualHandle` is REQUIRED here: omitting it defaulted the prefix off, so a handle-B state
      // persisted as "at least 50" with nothing naming the handle, and Flow (which renders the
      // persisted title) showed two indistinguishable states.
      title: sliderTargetedTitle(trigger, t, currentDataRef.current.dualHandle),
      trigger, content: '',
      evaluation: currentDataRef.current.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    const next = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  /** Reorders within ONE handle's visible list while leaving the other handle's states where they
   * are — the two are interleaved in a single array, so a naive arrayMove over the whole set would
   * shuffle states the teacher cannot even see. */
  function reorderTargetedWithinHandle(handle: SliderHandleId, activeId: string, overId: string) {
    const targeteds = feedback.states.filter(st => st.type === 'targeted');
    const subset = targetedStatesFor(handle);
    const from = subset.findIndex(st => st.id === activeId);
    const to = subset.findIndex(st => st.id === overId);
    if (from === -1 || to === -1) return;
    const moved = arrayMove(subset, from, to);
    let i = 0;
    const nextTargeted = targeteds.map(st =>
      parseSliderHandleTrigger(st.trigger).handle === handle ? moved[i++]! : st);
    const next = { states: rebuildStates(nextTargeted) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleDone() {
    push();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const drawnVersionId = taskPreviewRoll?.[versionRollKey(block.id)];
    const d = buildNormalizedSliderData(initData(block.data), t, drawnVersionId);
    const testData = memoizedByKey(
      sliderTestDataCache, block.data, [t, submitted, rolledValues, drawnVersionId],
      () => computeSliderTestData(
    d, t, i18n.language, submitted, resolveAnswer),
    );
    /* The same derivation asked about the DRAFT, reported and never rendered. An untouched
       handle still reads as No Answer, which is the honest Current rather than a blank row. */
    const draftData = memoizedByKey(
      sliderDraftChipsCache, block.data,
      [t, testValue, testValueB, testTouched, testTouchedB, rolledValues, drawnVersionId],
      () => computeSliderTestData(d, t, i18n.language, currentAnswers(), resolveAnswer),
    );
    return (
      <AnswerDisplayFrame question={d.question} rolledValues={rolledValues}>
        <SliderTrack
          {...sliderTrackLabels(t, d.dualHandle)}
          min={num(d.min)} max={num(d.max)} scaleMode={d.scaleMode} stepSize={num(d.stepSize)} logStepsPerDecade={num(d.logStepsPerDecade)}
          value={testValue}
          onTouch={() => setTestTouched(true)}
          onChange={setTestValue}
          onCommit={v => commitTest('a', v)}
          valueB={d.dualHandle ? testValueB : undefined}
          onTouchB={() => setTestTouchedB(true)}
          onChangeB={setTestValueB}
          onCommitB={v => commitTest('b', v)}
          showLiveReadout={d.showLiveReadout}
          minLabel={d.showLabels ? d.minLabel : undefined} maxLabel={d.showLabels ? d.maxLabel : undefined}
          orientation={d.orientation} reverseDirection={d.reverseDirection}
          showTickMarks={d.showTickMarks}
          majorTickInterval={num(d.majorTickInterval)} minorTickInterval={num(d.minorTickInterval)}
          showMajorTickValues={shouldShowMajorTickValues(d.showMajorTickValues, d.scaleMode, num(d.min), num(d.max), num(d.majorTickInterval))}
          tickLabels={d.tickLabels.map(tl => ({ id: tl.id, value: num(tl.value), label: tl.label }))}
          formatValue={v => fmtLocale(v, i18n.language, undefined, false, isPhone)}
          unitSuffix={d.unitSuffix}
        />
        {/* The per-block Submit that used to sit here was permanently aria-disabled and wired to
            nothing. Preview has had ONE page-level Submit since previewSubmitSignal landed, so a
            second dead one per block was pure noise. */}
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftData.testChips} states={draftData.testResult} />
        {renderSliderFiredFeedback(testData.testResult)}
      </AnswerDisplayFrame>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedSliderData(initData(block.data), t, shownVersionId);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    // Derived from `d` (PERSISTED data), not from `fields` (live editor state). A collapsed card is
    // a display surface and must show what is stored; `fields` is seeded once at mount and is the
    // editor's working copy, so a card whose block changed underneath it — an undo, another tab, a
    // duplicate rendered from the same component — showed the editor's idea of the answer rather
    // than the block's. Numerical's own collapsed bar is a module function taking `d` for exactly
    // this reason; slider reached for the component-scoped values because they were in scope.
    const cardFormulaTexts: string[] = (() => {
      const out: string[] = [];
      const collect = (src: { answers: ExactAnswer[]; betweenAnswers: BetweenAnswer[]; threshold: string; conditionType: ConditionType }) => {
        if (src.conditionType === 'exact') src.answers.forEach(a => { if (hasVariableChip(a.value)) out.push(a.value); });
        else if (src.conditionType === 'between') src.betweenAnswers.forEach(a => {
          if (hasVariableChip(a.low)) out.push(a.low);
          if (hasVariableChip(a.high)) out.push(a.high);
        });
        else if (hasVariableChip(src.threshold)) out.push(src.threshold);
      };
      collect(d);
      if (d.dualHandle) collect(d.handleB);
      return out;
    })();
    // STATIC. The answer bar on a display card states the equation that powers the correct answer
    // and nothing else — no dice, no rolled state, no dismiss. Rolling belongs to the test bar,
    // which is the surface actually being tested and is where all nine audited blocks put it; a
    // second reroll here rolled values into a card nobody was answering.
    const cardIssues = settleStored(sliderIssues(d, { ...issueCtx, resolve: resolveAnswer }));
    const testData = memoizedByKey(
      sliderTestDataCache, block.data, [t, submitted, i18n.language, isPhone, rolledValues, cardIssues, shownVersionId],
      () => computeSliderTestData(
    d, t, i18n.language, submitted, resolveAnswer, cardIssues, rolledValues),
    );
    const sliderAriaLabel = d.question
      ? t('blocks.slider.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.slider.editAriaBase');

    return (
      // Block-test roll ONLY — `isSelected`, not merely mounted. An unselected card is a DISPLAY
      // surface and display shows chips (project_variable_resolution_three_modes); wrapping the
      // whole collapsed render made slider the only block whose card resolved its question text.
      // Preview is deliberately not wrapped either: it inherits the task-level provider so every
      // block on the page resolves against one shared roll.
      // `null`, not `{}`, for the unselected card. `PreviewHtml` switches on `preview !== null`, so
      // an empty object still means "resolve me": it ran the substitution with nothing to
      // substitute and rendered bare text where the chip should be. That IS the display-mode chip
      // bug, not a variant of it.
      <BlockPreviewProvider value={isSelected ? rolledValues : null}>
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<MoveHorizontal className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.slider.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={sliderAriaLabel}
        answerBarSlot={() => sliderCollapsedBar(d, t, cardIssues, (
          // The card is a DISPLAY surface: roll `null`, so a variable answer shows its equation
          // rather than one roll of it. Same derivation, and same connector, as the editor bar.
          <AnswerBarText
            chips={sliderChips(d as unknown as Record<string, unknown>,
              { type: 'default', trigger: 'correct' } as never, t, i18n.language, null) ?? []}
            noValidAnswerLabel={sliderAnswerAuthored(d) ? t('blocks.answers.noValidAnswer') : undefined}
          />
        ))}
        testEvaluation={submitted && testData.testResult ? (testData.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: submitted !== null,
          chips: testData.testChips,
          onReset: handleTestReset,
          // Every audited block offers reroll from the test bar and nowhere else; slider was the
          // only one testing formula answers with no way to reroll from the surface being tested.
          // See numerical's note: a chip in the QUESTION changes what a student sees, so it earns a
          // reroll even though it contributes no formula to the answer band above.
          onReroll: ((cardFormulaTexts.length > 0 || htmlHasVariableChips(d.question)) && hasRollableVariables(variables))
            ? () => {
              setRolledValues(rollVariableValues(variables));
            }
            : undefined,
          /* No branches here, so this row carries only the version pager and the way into
             editing it — the same component every banked block uses, which renders nothing at
             all for a block with no bank. */
          topNotices: (
            <VersionBranchNotice
              versions={testPager?.versions ?? shownVersions}
              activeVersionId={testPager ? testPager.activeId : shownVersionId}
              onSelectVersion={testPager?.onSelect ?? setTestVersion}
              noun={testPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={null}
              onEdit={() => {
                editShownVersion();
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={testData.notices} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {/* The placeholder is DISPLAY STATE ONLY, gated on !isSelected exactly as GPS/Compass/Angle
            already gate theirs: a selected card IS the live test surface, and a student must never
            be shown authoring scaffolding. These two were the only blocks rendering it above the
            isSelected branch, so their test surface said "No question yet" where the other nine —
            and their own Preview — correctly showed nothing. */}
        {ghostedScenario}
        <BlockQuestion html={d.question} spaced showEmptyNote={!isSelected} />
        {/* Selected, the card IS the live test surface — a real answerable track. Unselected it
            stays the inert summary, so a canvas full of blocks doesn't invite stray drags. */}
        {isSelected ? (
          <div className='flex flex-col gap-3'>
            <SliderTrack
              {...sliderTrackLabels(t, d.dualHandle)}
              min={num(d.min)} max={num(d.max)} scaleMode={d.scaleMode} stepSize={num(d.stepSize)} logStepsPerDecade={num(d.logStepsPerDecade)}
              value={testValue}
              onTouch={() => setTestTouched(true)}
              onChange={setTestValue}
              onCommit={v => commitTest('a', v)}
              valueB={d.dualHandle ? testValueB : undefined}
              onTouchB={() => setTestTouchedB(true)}
              onChangeB={setTestValueB}
              onCommitB={v => commitTest('b', v)}
              showLiveReadout={d.showLiveReadout}
              minLabel={d.showLabels ? d.minLabel : undefined} maxLabel={d.showLabels ? d.maxLabel : undefined}
              orientation={d.orientation} reverseDirection={d.reverseDirection}
              showTickMarks={d.showTickMarks}
              majorTickInterval={num(d.majorTickInterval)} minorTickInterval={num(d.minorTickInterval)}
              showMajorTickValues={shouldShowMajorTickValues(d.showMajorTickValues, d.scaleMode, num(d.min), num(d.max), num(d.majorTickInterval))}
              tickLabels={d.tickLabels.map(tl => ({ id: tl.id, value: num(tl.value), label: tl.label }))}
              formatValue={v => fmtLocale(v, i18n.language, undefined, false, isPhone)}
              unitSuffix={d.unitSuffix}
            />
            {renderSliderFiredFeedback(testData.testResult)}
          </div>
        ) : (
          <SliderAnswerSummary d={d} t={t} locale={i18n.language} isPhone={isPhone} />
        )}
      </CollapsedInputBlock>
      </BlockPreviewProvider>
    );
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────
  // The panel shows every targeted card for both handles at once. Each one names its own slider,
  // so there is no per-handle view of this section and nothing to switch between.

  /*
   * There is no "current handle" any more.
   *
   * Every targeted card names its own slider, in its own trigger, so the panel has nothing left to
   * be a mode about — No Answer, Correct and Default Incorrect were always the block's single joint
   * verdict across both handles, and the tab only ever scoped the Add button and the reorder.
   * What is left below is handle A where a single handle is genuinely meant, and both handles
   * wherever the question is about the block as a whole.
   */
  const viewFeedback = feedback;
  const viewUpdateFeedbackState = updateFeedbackState;
  const viewDeleteFeedbackState = deleteFeedbackState;
  const viewGetTitle = getTitle;
  // A new card starts on the first slider that can actually take one, and says so on its face,
  // with the selector right there to change it — the same reason the tab could go: the choice is
  // visible on the card instead of remembered. Slider 1 unless its condition is one-sided, in
  // which case a card added there would have nowhere to sit.
  const viewAddTargetedState = () => addTargetedStateFor(
    !ONE_SIDED.includes(conditionType) || !dualHandle ? 'a' : 'b',
  );
  /*
   * The handle comes from the CARD being dragged, not from the tab.
   *
   * The targeted list is not filtered by handle — both sliders' cards are always on screen — so
   * taking the handle from the tab meant dragging a Slider 2 card while Slider 1 was selected hit
   * the `from === -1` guard and silently did nothing. A drag that snaps back with no explanation
   * reads as the list being broken.
   */
  const onTargetedDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const dragged = feedback.states.find(st => st.id === String(active.id));
    if (!dragged) return;
    reorderTargetedWithinHandle(parseSliderHandleTrigger(dragged.trigger).handle, String(active.id), String(over.id));
  };

  // Per-handle setters for condition type / threshold / error mode / error symmetry. The Answer
  // Setup section calls these directly for both handles at once, since it renders Slider 1/2
  // Answers as stacked sections rather than a switcher.
  const setConditionTypeA = (next: ConditionType) => { setConditionType(next); push({ conditionType: next }); };
  const setConditionTypeB = (next: ConditionType) => { setHandleBConditionType(next); pushHandleB({ conditionType: next }); };
  const setThresholdA = (v: string) => { setThreshold(v); push({ threshold: v }); };
  const setThresholdB = (v: string) => { setHandleBThreshold(v); pushHandleB({ threshold: v }); };
  /*
   * ONE ANSWER AT A TIME, on both handles.
   *
   * These used to flip the whole handle, which is why a slider could not accept 0.002 with an
   * absolute band and 500 with a percentage one. The handle-level field survives as the legacy
   * fallback for answers authored before the move, and is no longer written.
   */
  const toggleAnswerModeA = (id: string) => {
    const next = answers.map(a => a.id !== id ? a
      : { ...a, toleranceMode: answerToleranceMode(a, restored.data, 'absolute') === 'absolute' ? 'percentage' as const : 'absolute' as const });
    setAnswers(next);
    push({ answers: next });
  };
  const toggleAnswerModeB = (id: string) => {
    const next = handleBAnswers.map(a => a.id !== id ? a
      : { ...a, toleranceMode: answerToleranceMode(a, restored.data.handleB, 'absolute') === 'absolute' ? 'percentage' as const : 'absolute' as const });
    setHandleBAnswers(next);
    pushHandleB({ answers: next });
  };
  const toggleAnswerSymmetryA = (id: string) => {
    const next = answers.map(a => a.id !== id ? a
      : answerSymmetry(a, restored.data) === 'symmetric'
        ? { ...a, toleranceSymmetry: 'asymmetric' as const, toleranceAbove: a.toleranceValue, toleranceBelow: a.toleranceValue }
        : { ...a, toleranceSymmetry: 'symmetric' as const, toleranceValue: a.toleranceAbove || a.toleranceBelow });
    setAnswers(next);
    push({ answers: next });
  };
  const toggleAnswerSymmetryB = (id: string) => {
    const next = handleBAnswers.map(a => a.id !== id ? a
      : answerSymmetry(a, restored.data.handleB) === 'symmetric'
        ? { ...a, toleranceSymmetry: 'asymmetric' as const, toleranceAbove: a.toleranceValue, toleranceBelow: a.toleranceValue }
        : { ...a, toleranceSymmetry: 'symmetric' as const, toleranceValue: a.toleranceAbove || a.toleranceBelow });
    setHandleBAnswers(next);
    pushHandleB({ answers: next });
  };

  const rangeConfig = {
    conditionType,
    answers: answers.map(a => ({ id: a.id, value: resolveAnswer(a.value), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    betweenAnswers: betweenAnswers.map(a => ({ id: a.id, low: resolveAnswer(a.low), high: resolveAnswer(a.high) })),
    toleranceMode, toleranceSymmetry, threshold: resolveAnswer(threshold),
  };
  const acceptedRanges: SliderBoundedInterval[] = computeSliderAcceptedRanges(rangeConfig);
  // The test bar has always said "Unreachable" for an answer key no value can satisfy; the panel
  // showed a live Correct card with a "—" chip for the identical setup. Asked across both handles,
  // not just the viewed one: Correct needs BOTH, so an empty handle B kills it while handle A is
  // on screen looking perfectly well configured.
  const correctUnreachable = gradingMode === 'graded' && isSliderCorrectUnreachable(
    (dualHandle ? (['a', 'b'] as const) : (['a'] as const))
      .map(h => computeSliderAcceptedRanges(handleRangeConfigFrom(fields, h, resolveAnswer))));
  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(viewFeedback.states, gradingMode);
  const valueFeedbackTriggers = targetedStates.map(s => s.trigger);
  const defaultIncorrectUnreachable = isSliderValueDefaultIncorrectUnreachable(acceptedRanges, valueFeedbackTriggers);
  const minN = num(min), maxN = num(max), stepN = num(stepSize);
  const minMaxInverted = (() => {
    const minP = parseFloat(min), maxP = parseFloat(max);
    return Number.isFinite(minP) && Number.isFinite(maxP) && minP >= maxP;
  })();
  const handleOrderInverted = (() => {
    if (!dualHandle) return false;
    const aP = parseFloat(defaultPosition), bP = parseFloat(handleBDefaultPosition);
    return Number.isFinite(aP) && Number.isFinite(bP) && aP > bP;
  })();
  // A zero or negative step/steps-per-decade breaks quantization (e.g. a step of -1 inverts
  // keyboard direction and produces nonsensical reachable-value math) — flagged once the field
  // actually has content, not while it's still blank/unconfigured (that's covered by the
  // separate "setup incomplete" warning on the collapsed card).
  const stepInvalid = (() => {
    const raw = scaleMode === 'log' ? logStepsPerDecade : stepSize;
    if (raw.trim() === '') return false;
    const p = parseFloat(raw);
    return Number.isFinite(p) && p <= 0;
  })();
  const majorTickIntervalN = num(majorTickInterval);
  const minorTickIntervalN = num(minorTickInterval);
  // Thresholds are on the resulting tick *count*, not the raw interval value — a fine interval
  // is perfectly legitimate on a narrow range (e.g. 0-1 with ticks every 0.0001); it only becomes
  // a problem once it would actually produce more ticks than a track can show or a label can fit.
  const majorTickCount = showTickMarks && scaleMode === 'linear' ? sliderTickCount(minN, maxN, majorTickIntervalN) : 0;
  const minorTickCount = showTickMarks && scaleMode === 'linear' ? sliderTickCount(minN, maxN, minorTickIntervalN) : 0;
  const majorTickTooDense = majorTickInterval.trim() !== '' && majorTickCount > MAX_READABLE_MAJOR_TICKS;
  const minorTickTooDense = minorTickInterval.trim() !== '' && minorTickCount > MAX_READABLE_MINOR_TICKS;
  const minorNotSmallerThanMajor = majorTickInterval.trim() !== '' && minorTickInterval.trim() !== ''
    && minorTickIntervalN > 0 && majorTickIntervalN > 0 && minorTickIntervalN >= majorTickIntervalN;
  // Booleans, not messages. These used to build a translated sentence that was rendered inline;
  // now the sentence lives on the status bar (`default-position-outside` / its handle-B twin) and
  // all the field itself needs to know is whether to paint its border red.
  const positionInvalid = (raw: string): boolean => {
    const v = parseFloat(raw);
    return !Number.isFinite(v) || v < minN || v > maxN;
  };
  const defaultPositionInvalid = positionInvalid(defaultPosition);
  const handleBDefaultPositionInvalid = positionInvalid(handleBDefaultPosition);

  const unansweredState = viewFeedback.states.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={viewGetTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => viewUpdateFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          viewUpdateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  /*
   * Offered when EITHER handle can take targeted feedback, because the card chooses its own.
   *
   * A one-sided condition ("at least 40") leaves no value a targeted card could name that the
   * answer key does not already claim. With one handle that was the whole question; with two, the
   * button is for both, so gating it on a single handle's condition type would hide it while the
   * other slider still had room.
   */
  /**
   * A one-sided condition leaves no bounded accepted band for a value trigger to sit outside of,
   * which is why graded refuses one. Outside graded there is no band at all, so the reason does
   * not apply and every value is targetable.
   */
  const canAddMore = gradingMode !== 'graded' || (
    !ONE_SIDED.includes(conditionType) || (dualHandle && !ONE_SIDED.includes(handleBConditionType))
  );

  /**
   * The panel's Correct and Default Incorrect chips, from the SHARED derivation in `lib/`.
   *
   * These were three strings before: this panel built one, the test bar built another, the
   * collapsed card a third, and Flow computed a fourth. Three of this block's reported bugs were
   * those copies disagreeing. `sliderChips` is now the only place that decides, and every surface —
   * this one, the bar, Flow — renders the same list.
   *
   * It takes the block DATA rather than a synthesized `Block`, matching how compass, angle and gps
   * reach their own labels: `lib` reasons about persisted shape, so it wants the data.
   */
  /**
   * WHICH CARD collides, said on the card itself.
   *
   * Slider raised its collision issues only in the issues bar, which names the block and not the
   * card — so on a panel of four targeted states a teacher was told one of them conflicted and
   * left to work out which. Compass, GPS and Tilt have each marked the offending card from the
   * start; slider is the port that never happened.
   *
   * The warning is not that the card is broken. It is that two cards speak to the same
   * submission, so whatever the teacher wrote in both arrives together and may contradict
   * itself — which only they can judge, hence a warning and not an error.
   *
   * Per HANDLE: a condition is judged against the band of the slider it names, never the other
   * one's. The order matters and is the same ladder the other three use — fully inside the band
   * first, partly over it second, colliding with a sibling card last — because the first is the
   * most specific thing that can be said about the same fact.
   */
  const allTargetedTriggers = viewFeedback.states.filter(x => x.type === 'targeted').map(x => x.trigger);
  const overlappingTriggerSet = new Set(findSliderOverlappingFeedbackPairs(allTargetedTriggers).flat());
  const setAnswerHeld = targetedStates.some(x => x.trigger === SET_ANSWER_TRIGGER);
  /** Whether this condition shares ground with the band its own handle accepts. */
  function sharesTheBand(trigger: string): boolean {
    const { handle } = parseSliderHandleTrigger(trigger);
    if (handle === 'b' && !dualHandle) return false;
    const band = computeSliderAcceptedRanges(handleRangeConfigFrom(fields, handle, resolveAnswer));
    return isSliderValueFeedbackFullySubsumed(trigger, band)
      || doesSliderValueFeedbackOverlapAccepted(trigger, band);
  }
  function sliderFeedbackWarning(trigger: string): string | undefined {
    /*
     * BOTH SIDES OF A COLLISION ARE LIT, which is what makes one findable.
     *
     * A value-vs-value pair already did: `overlappingTriggerSet` is built from `.flat()`, so each
     * trigger of the pair is in it. Set Answer was the one-sided case — it was left unmarked on
     * the reasoning that it states the band rather than overlapping it, which is true and is not
     * the point. A teacher looking at one lit card has to find the other, and marking only the
     * Custom one leaves them hunting for what it collides WITH.
     */
    if (trigger === SET_ANSWER_TRIGGER) {
      if (!setAnswerHeld) return undefined;
      return targetedStates.some(x => x.trigger !== SET_ANSWER_TRIGGER && sharesTheBand(x.trigger))
        ? t('blocks.slider.feedbackOverlapWarning') : undefined;
    }
    const { handle } = parseSliderHandleTrigger(trigger);
    if (handle === 'b' && !dualHandle) return undefined;    // orphaned; reported on its own
    const band = computeSliderAcceptedRanges(handleRangeConfigFrom(fields, handle, resolveAnswer));
    /*
     * Against the BAND only where the band is in force: graded, where Correct is checked first
     * and is terminal, or survey where a Set Answer card has claimed it. Without either, nothing
     * is looking at the accepted positions and a condition inside them is the ordinary case.
     */
    if (gradingMode === 'graded' || setAnswerHeld) {
      if (isSliderValueFeedbackFullySubsumed(trigger, band)) {
        // Dead in graded (Correct wins and stops), merely duplicated in survey (Set Answer does
        // not stop), so the two modes cannot share one sentence.
        return gradingMode === 'graded'
          ? t('blocks.slider.feedbackSubsumedWarning')
          : t('blocks.slider.feedbackOverlapWarning');
      }
      if (doesSliderValueFeedbackOverlapAccepted(trigger, band)) {
        return gradingMode === 'graded'
          ? t('blocks.slider.feedbackOverlapsCorrectWarning')
          : t('blocks.slider.feedbackOverlapWarning');
      }
    }
    if (overlappingTriggerSet.has(trigger)) return t('blocks.slider.feedbackOverlapWarning');
    // Two cards carrying the SAME condition, which the band comparison cannot see: it skips
    // `exact`, and `set_answer` has no band of its own.
    if (allTargetedTriggers.filter(x => x === trigger).length > 1) {
      return t('blocks.slider.feedbackOverlapWarning');
    }
    return undefined;
  }

  const sharedChips = (trigger: 'correct' | 'incorrect', acceptedClaimed = true) =>
    // `null` from the derivation means "nothing to state" (no answer configured yet); an empty list
    // renders as nothing, which is the same thing said in the type the row accepts.
    sliderChips(fields as unknown as Record<string, unknown>,
      { type: 'default', trigger } as never, t, i18n.language,
      // The panel at rest shows the equation; it resolves only once the teacher picks a scenario.
      // Same gate the answer bar uses, so the two halves of the editor cannot disagree.
      variableTestMode === null ? null : rolledValues, acceptedClaimed) ?? [];

  /*
   * DECLARED ABOVE `targetedSlot`, not beside the panel it was written for.
   *
   * A targeted card's trigger editor needs these chips to state the authored condition for Set
   * Answer, and `answerConditionChips` is an ordinary prop — built as the card is built, not when
   * it renders. Left below, the map read `sharedChips` inside its own temporal dead zone and the
   * whole block threw the instant one targeted card existed.
   */
  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onTargetedDragEnd}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                titleLocked
                /* Neutral and locked outside graded: the stored evaluation mirrors a verdict
                   the mode has not got, and there is no incorrect to cycle TO. */
                title={s.title} content={s.content}
                evaluation={displayEvaluation(gradingMode, s.evaluation)}
                evalLocked={gradingMode !== 'graded'}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={sliderFeedbackWarning(s.trigger)}
                triggerZone={<SliderTriggerEditor fieldId={`feedback.${s.id}.trigger`} answerTaken={targetedStates.some(x => x.id !== s.id && x.trigger === SET_ANSWER_TRIGGER)} dualHandle={dualHandle} gradingMode={gradingMode} answerConditionChips={<ConditionChipRow chips={neutralChips(sharedChips('correct'))} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />} onPlainFieldFocus={disconnectSharedToolbar} trigger={s.trigger} onChange={trigger => viewUpdateFeedbackState(s.id, { trigger, title: sliderTargetedTitle(trigger, t, dualHandle) })} variables={variables} rolledValues={rolledValues} />}
                /* The preset states the authored condition over BOTH handles, from the same
                   derivation the expanded card and graded's Correct card use — "Slider 1: 19 to 21
                   and Slider 2: 79 to 81", never "Slider 1: < ?" off a trigger holding no value. */
                triggerZoneSummary={s.trigger === SET_ANSWER_TRIGGER
                  ? <ConditionChipRow chips={neutralChips(sharedChips('correct'))} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
                  : <SliderTriggerChip angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} dualHandle={dualHandle} scenario={variableTestMode !== null ? rolledValues : {}} />}
                onTitleChange={() => {}}
                onContentChange={html => viewUpdateFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => viewUpdateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => viewDeleteFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  /**
   * Label a value for BOTH sliders when a state applies to both.
   *
   * Correct, No Answer and Default Incorrect are JOINT: `evaluateSlider` fires Correct only when
   * `submissions.every(...)` is satisfied, and Default Incorrect is what is left when that fails.
   * Only TARGETED feedback is per-slider — its triggers carry a handle prefix (`a:exact:34`).
   *
   * The panel scoped all of them to the tab you were on, so the Correct card claimed the answer was
   * "Slider 1: 283" when the actual condition is "283 AND 1" — the test bar said so all along, one
   * pane over. Scoping a shared state by the tab reads as though switching tabs changes what the
   * block grades, and it does not.
   */
  // Shared with the issues panel, so the message and the highlighted fields cannot disagree about
  // whether the pair is satisfiable — see `sliderAnswersCrossed`.


  /*
   * The answer bar's text, FLATTENED from the same chip list the panel and the bar draw.
   *
   * This was the last of slider's parallel derivations: `jointChip(correctChipFor)` built its own
   * string, which is how the bar came to read "Slider 1: 283, Slider 2: 283" while the panel had it
   * right. The bar is a plain text row rather than a chip row, so it flattens instead of rendering
   * boxes — which is exactly what Flow does, through the same function.
   */
  const correctChip = chipsToPlainText(sharedChips('correct'), {
    and: t('blocks.connectors.and'), or: t('blocks.connectors.or'),
    unreachable: t('blocks.feedbackPanel.unreachable'),
  }) ?? '';
  const positiveChip =
    // The two non-graded modes name their own positive state; only `graded` has an answer to print
    // here, and only `graded` can have one that nothing reaches.
    gradingMode !== 'graded' ? positiveSlotTitleFor(gradingMode, t) :
    correctUnreachable ? t('blocks.feedbackPanel.unreachable') :
    correctChip;

  /** The positive chip as NODES rather than a string, so an unresolved formula renders through
   * `FormulaWithValues` — the app's one chip renderer — instead of being interpolated into text.
   * `lockedTriggerLabel` is typed `string` and cannot carry chips, so this goes through
   * `triggerZoneSummary`, which overrides it. Undefined when there is nothing to chip-render, which
   * lets the plain string path stand for ordinary numeric answers. */

  /**
   * The catch-all's own chip, outside graded.
   *
   * It used to draw `sharedChips('correct')` in every mode — graded's answer bands. A slider with
   * no answer configured has none, so in optional the row rendered EMPTY and the collapsed card
   * showed nothing at all; the name only appeared on expanding, from `lockedTriggerLabel`.
   *
   * The label is the card's own title, so the two cannot disagree, and `catchAllChips` still owns
   * the tick. No enumeration: slider's domain is open, so every position off a targeted one lands
   * here and listing them says nothing — the same call the test bar above already makes.
   */
  /*
   * The panel's catch-all ENUMERATES too, from the same derivation as the bar's.
   *
   * It used to render the bare category name ("Any Other Answer") through `catchAllTitle`, which
   * said the leftovers existed without saying what they were — on a block whose complement is
   * exactly statable. Once Set Answer owns the accepted band, the teacher can read what the
   * catch-all still gets instead of inferring it.
   */
  const participationCatchAllChips = catchAllChips(
    gradingMode, targetedStates.length > 0, t,
    () => sharedChips('incorrect', targetedStates.some(x => x.trigger === SET_ANSWER_TRIGGER)),
  );

  const correctSlot = viewFeedback.states
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={correctUnreachable}
        lockedTriggerLabel={positiveChip}
        triggerZone={correctUnreachable ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : gradingMode !== 'graded' ? <ConditionChipRow chips={participationCatchAllChips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
          : undefined}
        // The ONE derivation, unconditionally. This used to be `positiveChipNode ?? <the shared
        // list>`, and `positiveChipNode` was defined for every formula answer — so it always won,
        // and the shared list was dead code here. That is why this card showed only the active
        // handle's equation while the legend beside it showed both.
        triggerZoneSummary={correctUnreachable
          ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : <ConditionChipRow
              chips={gradingMode === 'graded' ? sharedChips('correct') : participationCatchAllChips}
              unreachableLabel={t('blocks.feedbackPanel.unreachable')} />}
        title={catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? viewGetTitle(state)}
        content={state.content}
        /* The tick goes once a target exists — the catch-all must not affirm an answer the teacher
           ignored more strongly than one they wrote for. Same rule as every other block. */
        evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => viewUpdateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));


  const incorrectSlot = viewFeedback.states
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked
        inactive={defaultIncorrectUnreachable}
        triggerZoneSummary={<ConditionChipRow chips={sharedChips('incorrect')} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />}
        title={viewGetTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => viewUpdateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Editing shell ───────────────────────────────────────────────────────────

  // ── Phone Test tab ──────────────────────────────────────────────────────────
  // The tab is structurally OFF until testSlot, testBar, phoneMode, onPhoneModeChange and both
  // labels are all present — see InputBlockFrame.
  // Built from `fields` (the live editor state this tab edits), exactly as the editor's own
  // warning bar does below — the phone Test tab is the only surface where a teacher cannot also
  // see the card, so an empty `[]` here left them with no issue reporting anywhere.
  const phoneIssues = editorIssues;
  const phoneTest = computeSliderTestData(
    fields, t, i18n.language,
    submitted, resolveAnswer, phoneIssues, rolledValues,
  );
  const phoneTestSlot = (
    // Same reason as the collapsed card: without the provider the phone Test tab renders the
    // question's variable chips raw instead of resolved against the roll being tested.
    <BlockPreviewProvider value={rolledValues}>
    <div className='flex flex-col gap-3'>
      <BlockQuestion html={fields.question} />
      <SliderTrack
        {...sliderTrackLabels(t, fields.dualHandle)}
        min={num(fields.min)} max={num(fields.max)} scaleMode={fields.scaleMode} stepSize={num(fields.stepSize)} logStepsPerDecade={num(fields.logStepsPerDecade)}
        value={testValue}
        onTouch={() => setTestTouched(true)}
        onChange={setTestValue}
        onCommit={v => commitTest('a', v)}
        valueB={fields.dualHandle ? testValueB : undefined}
        onTouchB={() => setTestTouchedB(true)}
        onChangeB={setTestValueB}
        onCommitB={v => commitTest('b', v)}
        showLiveReadout={fields.showLiveReadout}
        minLabel={fields.showLabels ? fields.minLabel : undefined} maxLabel={fields.showLabels ? fields.maxLabel : undefined}
        orientation={fields.orientation} reverseDirection={fields.reverseDirection}
        showTickMarks={fields.showTickMarks}
        majorTickInterval={num(fields.majorTickInterval)} minorTickInterval={num(fields.minorTickInterval)}
        showMajorTickValues={shouldShowMajorTickValues(fields.showMajorTickValues, fields.scaleMode, num(fields.min), num(fields.max), num(fields.majorTickInterval))}
        tickLabels={fields.tickLabels.map(tl => ({ id: tl.id, value: num(tl.value), label: tl.label }))}
        formatValue={v => fmtLocale(v, i18n.language, undefined, false, isPhone)}
        unitSuffix={fields.unitSuffix}
      />
      {renderSliderFiredFeedback(phoneTest.testResult)}
    </div>
    </BlockPreviewProvider>
  );
  const phoneTestBar: TestBarProps = {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: submitted !== null,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: ((hasFormulaAnswer || htmlHasVariableChips(fields.question)) && hasRollableVariables(variables))
      ? () => {
        setRolledValues(rollVariableValues(variables));
      }
      : undefined,
    /* The same row as the canvas card's. The EDITOR's pointer here: on a phone, Edit and Test
       are two tabs over ONE session. */
    topNotices: (
      <VersionBranchNotice
        versions={testPager?.versions ?? shownVersions}
        activeVersionId={testPager ? testPager.activeId : shownVersionId}
        onSelectVersion={testPager?.onSelect ?? selectVersion}
        noun={testPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={null}
        onEdit={() => { editShownVersion(); setPhoneTestMode('edit'); }}
      />
    ),
    notices: (
      <IssuesPanel issues={phoneTest.notices} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  };

  return (
    // The editor is a testing surface too: without the provider its question and feedback previews
    // render variable chips raw while the teacher is actively rolling values to test against.
    <BlockPreviewProvider value={rolledValues}>
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<MoveHorizontal className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.slider.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.slider.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          {/* Two categories, not one. Dual handle adds a second GRADED input with its own answer
              config, and scale mode decides which values are reachable at all (stepSize vs
              logStepsPerDecade) — neither is a presentation choice, and both sat among the
              cosmetic toggles. Answer Format is the same section mcq/numerical/angle/compass
              already use for exactly this kind of control. */}
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            <CheckboxRow fieldId='dualHandle'
              checked={dualHandle}
              onToggle={() => handleDualHandle(!dualHandle)}
              label={t('blocks.slider.fieldDualHandle')}
            />

            {/* The section survives the gate — Dual Handle above it is not gated — so only the
                radio goes. A group offering one choice would not be a choice. */}
            {showScaleMode && (
              <RadioGroup fieldId='scaleMode'
                aria-label={t('blocks.slider.fieldScaleMode')}
                value={scaleMode}
                onChange={v => handleScaleMode(v as SliderScaleMode)}
                options={[
                  { value: 'linear', label: t('blocks.slider.scaleModeLinear') },
                  { value: 'log', label: t('blocks.slider.scaleModeLog') },
                ]}
              />
            )}
          </OptionsSection>

          {/* Grouped by CONTROL KIND, not by topic: the segmented control first, then every
              checkbox in one run. The four "mini-header" sub-labels this used to carry are a
              pattern no audited block uses, and they were what stranded Reverse Direction under
              the orientation switcher — it is a checkbox and belongs with the others. */}
          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            <SegmentedControl fieldId='orientation'
              options={[
                { id: 'horizontal', label: t('blocks.slider.orientationHorizontal') },
                { id: 'vertical', label: t('blocks.slider.orientationVertical') },
              ]}
              value={orientation}
              onChange={v => { const next = v as SliderOrientation; setOrientation(next); push({ orientation: next }); }}
              aria-label={t('blocks.slider.fieldOrientation')}
            stretch
            />
            <CheckboxRow fieldId='showLiveReadout'
              checked={showLiveReadout}
              onToggle={() => { setShowLiveReadout(!showLiveReadout); push({ showLiveReadout: !showLiveReadout }); }}
              label={t('blocks.slider.fieldShowLiveReadout')}
            />
            <CheckboxRow fieldId='showLabels'
              checked={showLabels}
              onToggle={() => { setShowLabels(!showLabels); push({ showLabels: !showLabels }); }}
              label={t('blocks.slider.fieldShowLabels')}
            />
            <CheckboxRow fieldId='showTickMarks'
              checked={showTickMarks}
              onToggle={() => { setShowTickMarks(!showTickMarks); push({ showTickMarks: !showTickMarks }); }}
              label={t('blocks.slider.fieldShowTickMarks')}
            />
            {/* The interval fields live WITH the toggle that gates them, not 200 lines away in the
                editor body — they are display settings, which the standing rule puts in the drawer.
                The tick LABELS list stays in the body: authored text is content, not a setting. */}
            {/* Stays: this is a display toggle, which is what the drawer is for. The two interval
                INPUTS moved to the editor body — they are authored values, not settings, and the rule
                is that the drawer switches things on and off while the editing happens in the panel. */}
            <div className={`ml-6 ${showTickMarks ? '' : 'pointer-events-none opacity-40'}`} aria-disabled={!showTickMarks}>
              <CheckboxRow fieldId='showMajorTickValues'
                checked={showMajorTickValues}
                disabled={!showTickMarks}
                onToggle={() => { setShowMajorTickValues(!showMajorTickValues); push({ showMajorTickValues: !showMajorTickValues }); }}
                label={t('blocks.slider.fieldShowMajorTickValues')}
              />
            </div>

            {/* Last in the section on purpose: it is the option a teacher reaches for least, and it
                was previously interrupting the run of four "Show ..." checkboxes that belong
                together. */}
            <CheckboxRow fieldId='reverseDirection'
              checked={reverseDirection}
              onToggle={() => { setReverseDirection(!reverseDirection); push({ reverseDirection: !reverseDirection }); }}
              label={t('blocks.slider.fieldReverseDirection')}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.slider.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      scenarioBarSlot={variableTestController}
      /**
       * The same slot the collapsed card gets, on the editor. Reuses `positiveChipNode ??
       * rawCorrectChip` rather than re-deriving: those two already encode the two-style rule
       * (equation chips at rest, resolved numbers under a scenario), and a second derivation here
       * is exactly what left compass and angle showing chips through every roll.
       *
       * No issues and no MobileOnlyChip: `warningBarSlot` renders IssuesPanel directly beneath
       * this, so repeating them would say the same thing twice, one line apart.
       */
      answerBarSlot={fields.gradingMode !== 'graded'
        ? collapsedModeBar(fields.gradingMode, t, 'slider')
        : (() => {
            /*
             * `answerBarSummary`, shared with numerical/compass/angle — see its own note for why the
             * four disagreed. Slider's two faults were here:
             *
             *   - It read `positiveChipNode`, which is scoped to the ACTIVE HANDLE TAB
             *     (`viewAnswers`), so a dual-handle block showed only half its answer. This passes
             *     `formulaAnswerFields`, which already spans both handles and is the same list the
             *     scenario bar prints, so the bar and the roll controls cannot disagree about what
             *     varies.
             *   - `positiveChipNode` is the FEEDBACK card's chip. Reusing it here put a feedback
             *     chip in the answer slot, which is why this bar looked unlike the other three even
             *     when its content was right.
             *
             * ONE wrapper around both readings: they used to be wrapped separately, which gave the
             * equation state and the resolved state different chrome for the same slot.
             */
            /*
             * The answer bar RENDERS the Correct chips — it does not derive them. It had its own
             * per-handle collection, which is one more copy of the fact `sliderChips` already
             * states, and the copy is what let the bar and the Correct card disagree about how many
             * handles to show.
             *
             * `ConditionChipRow`, not a single span: those wrap everything in ONE chip, which is
             * why per-answer content kept rendering as one giant chip with "or" inside it. The
             * connectors are chips' siblings, not their contents.
             */
            const chips = sharedChips('correct');
            const authored = sliderAnswerAuthored(fields);
            // `answerBarHasContent`, not `length === 0`: an unreachable list is one entry long, so
            // the shell would wrap nothing and draw an empty tinted bar on a fresh block.
            const content = !answerBarHasContent(chips, authored) ? null : (
              <AnswerBarText chips={chips}
                  noValidAnswerLabel={authored ? t('blocks.answers.noValidAnswer') : undefined} />
            );
            return content === null ? null : (
              <CollapsedBlockBar tone='success' align='start' scrollX>
                <span className='min-w-0'>{content}</span>
              </CollapsedBlockBar>
            );
          })()}
      feedbackPanelProps={{
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: viewAddTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
          {/* The three-way offer, when linking meets an existing bank — §7.1. */}
          {remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!activeScenario}
            linkMenu={linkMenu}
            stimulus={<ScenarioStimulus leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
                version={activeScenarioVersion as Record<string, unknown> | null} />}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          {/* A bank is a niche feature: in the heading it is available without being advertised. */}
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            divider={!!activeScenario}
            action={(
              <div className='flex items-center gap-2'>
              {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2). */}
              {!storedForLink.followsBlockId && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addSliderVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeSliderVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the
                  control moves to — see `QuestionHeadingLink`. Written as
                  `!activeScenario` it knew about scenarios only, so a DRIVEN block
                  showed the link twice and the heading overflowed sideways. */}
              <QuestionHeadingLink
                following={!!activeScenario}
                linkMenu={linkMenu}
              />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={questionHtml}
            onChange={html => {
              // The render-time SNAPSHOT has to move with the ref, not just the ref.
              // `currentDataRef.current` is rebuilt during render, and this handler runs before any
              // re-render, so every push path would otherwise spread the PREVIOUS question and leave
              // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
              // this by reading questionHtmlRef live at push time; these blocks bake the question into
              // `fields` instead, so the two have to be kept in lockstep here.
              questionHtmlRef.current = html;
              setQuestionHtml(html);
              currentDataRef.current = { ...currentDataRef.current, question: html };
              push();
            }}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
          />

          {/* Divider + heading marking where the range/scale setup begins — mirrors the
              "Answer Setup" divider below, giving the two distinct authoring phases (range vs.
              grading) equally clear boundaries. */}
          <EditorSectionHeading label={t('blocks.slider.sliderSetupHeading')} />

          {/*
            * PHONE ONLY.
            *
            * On desktop this box duplicated a control already on screen — and the duplication got
            * worse as the canvas card was brought into line with it: same responsive length, same
            * nudge pairs, same ticks. Two pictures of one slider, the smaller of them not even to
            * scale, since a 384px panel cannot hold the real 420px track and this box was `compact`
            * for exactly that reason. Its corner labels doubled up too: "Logarithmic" restates the
            * scale-mode control a few pixels above it.
            *
            * On a PHONE the editor is full-screen, so the canvas card is not visible at all, and
            * this stops being a duplicate and becomes the only slider you can see while changing
            * the settings that shape it. Which is the whole reason it survives.
            */}
          {isPhone && (
            // pt-8 (not p-4's implied pt-4) always reserves room for the "Preview" corner label:
            // without it, hiding the live readout removes the row that would otherwise separate the
            // label from the track, and the handle visually overlaps the label text.
            <div className='relative rounded-lg border border-ws-border p-4 pt-8'>
              {/* "Preview" first, then the scale it is drawn on: what the box IS, then how to read
                  it. The two were the other way round, which led with a setting the reader has not
                  yet been told the purpose of. */}
              <span aria-hidden='true' className='absolute left-3 top-2 text-xs italic text-ws-muted'>{t('blocks.slider.previewCornerLabel')}</span>
              <span aria-hidden='true' className='absolute right-3 top-2 text-xs italic text-ws-muted'>
                {scaleMode === 'log' ? t('blocks.slider.scaleModeLog') : t('blocks.slider.scaleModeLinear')}
              </span>
              {/* Sits in the 384px editor panel, which cannot absorb a full-height track — `compact`. */}
      <SliderTrack
        {...sliderTrackLabels(t, dualHandle)}
        compact
                min={minN} max={maxN} scaleMode={scaleMode} stepSize={stepN} logStepsPerDecade={num(logStepsPerDecade)}
                value={previewValue}
                onTouch={() => {}}
                onChange={setPreviewValue}
                onCommit={setPreviewValue}
                valueB={dualHandle ? previewValueB : undefined}
                onTouchB={() => {}}
                onChangeB={setPreviewValueB}
                onCommitB={setPreviewValueB}
                showLiveReadout={showLiveReadout}
                minLabel={showLabels ? minLabel : undefined} maxLabel={showLabels ? maxLabel : undefined}
                orientation={orientation} reverseDirection={reverseDirection}
                showTickMarks={showTickMarks}
                majorTickInterval={num(majorTickInterval)} minorTickInterval={num(minorTickInterval)}
                showMajorTickValues={shouldShowMajorTickValues(showMajorTickValues, scaleMode, minN, maxN, majorTickIntervalN)}
                tickLabels={tickLabels.map(tl => ({ id: tl.id, value: num(tl.value), label: tl.label }))}
                formatValue={v => fmtLocale(v, i18n.language, undefined, false, isPhone)}
                unitSuffix={unitSuffix}
              />
            </div>
          )}

          {/* Range setup — required configuration, always in plain view (not hidden behind
              the settings drawer, which is reserved for optional display/mode toggles).
              Field styling matches numerical's labeled-field convention (e.g. its Math Error
              equation-override field): text-xs text-ws-muted label, px-3 py-3 input matching
              numerical's own answer/threshold field convention exactly. Min|MinLabel and
              Max|MaxLabel are paired so a long label sits right next to the value it annotates;
              Step/Steps-per-decade pairs with Unit Suffix; Default Position stays on its own
              row so dual-handle (below) can add a second start-position field beside it. */}
          <div className='flex flex-col gap-3'>
            {showLabels ? (
              <>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='flex flex-col'>
                    <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMin')}</label>
                    <NumberField
                      grammar='scientific'
                      allowNegative
                      value={min}
                      onChange={v => { setMin(v); push({ min: v }); }}
                      hasError={minMaxInverted}
                      ariaLabel={t('blocks.slider.fieldMin')}
                      fieldId={SLIDER_FIELD_IDS.min}
                      onFocus={disconnectSharedToolbar}
                      className='w-full' />
                  </div>
                  <div className='flex flex-col'>
                    <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMinLabel')}</label>
                    <TextField fieldId='minLabel'
                      variant='prose'
                      value={minLabel}
                      onChange={v => { setMinLabel(v); push({ minLabel: v }); }}
                      maxLength={24}
                      ariaLabel={t('blocks.slider.fieldMinLabel')}
                      onFocus={disconnectSharedToolbar} />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='flex flex-col'>
                    <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMax')}</label>
                    <NumberField
                      grammar='scientific'
                      allowNegative
                      value={max}
                      onChange={v => { setMax(v); push({ max: v }); }}
                      hasError={minMaxInverted}
                      ariaLabel={t('blocks.slider.fieldMax')}
                      fieldId={SLIDER_FIELD_IDS.max}
                      onFocus={disconnectSharedToolbar}
                      className='w-full' />
                  </div>
                  <div className='flex flex-col'>
                    <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMaxLabel')}</label>
                    <TextField fieldId='maxLabel'
                      variant='prose'
                      value={maxLabel}
                      onChange={v => { setMaxLabel(v); push({ maxLabel: v }); }}
                      maxLength={24}
                      ariaLabel={t('blocks.slider.fieldMaxLabel')}
                      onFocus={disconnectSharedToolbar} />
                  </div>
                </div>
              </>
            ) : (
              <div className='grid grid-cols-2 gap-2'>
                <div className='flex flex-col'>
                  <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMin')}</label>
                  <NumberField
                      grammar='scientific'
                      allowNegative
                      value={min}
                      onChange={v => { setMin(v); push({ min: v }); }}
                      hasError={minMaxInverted}
                      ariaLabel={t('blocks.slider.fieldMin')}
                      fieldId={SLIDER_FIELD_IDS.min}
                      onFocus={disconnectSharedToolbar}
                      className='w-full' />
                </div>
                <div className='flex flex-col'>
                  <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMax')}</label>
                  <NumberField
                      grammar='scientific'
                      allowNegative
                      value={max}
                      onChange={v => { setMax(v); push({ max: v }); }}
                      hasError={minMaxInverted}
                      ariaLabel={t('blocks.slider.fieldMax')}
                      fieldId={SLIDER_FIELD_IDS.max}
                      onFocus={disconnectSharedToolbar}
                      className='w-full' />
                </div>
              </div>
            )}

            {/* Tick intervals sit HERE, under min/max, not in the options drawer. They are numbers a
                teacher works out from the range directly above them — 0 to 100 in tens — so reading
                one to choose the other means having both on screen. The drawer keeps the on/off
                switch and the show-values toggle; the authoring happens in the panel. */}
            {/* Hidden until "Show tick marks" is on, which is NOT the never-hidden rule being
                broken — it is the rule not applying. That rule governs OPTIONS: a checkbox you
                cannot see is a feature you cannot discover, so it stays visible and greys out. The
                switch itself obeys it, in the drawer. These are editor FIELDS the switch brings
                into existence, and a greyed-out pair of inputs for a feature that is off is clutter
                in the one panel that should hold only what you are editing.

                Log mode is a separate absence entirely: its majors are whole decades, so there is
                no interval to author at all. */}
            {showTickMarks && scaleMode !== 'log' && (
              <div className='grid grid-cols-2 gap-2'>
                <div className='flex flex-col'>
                  <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMajorTickInterval')}</label>
                  <NumberField
                    grammar='scientific'
                    allowNegative
                    value={majorTickInterval}
                    onChange={v => { setMajorTickInterval(v); push({ majorTickInterval: v }); }}
                    hasError={majorTickTooDense || minorNotSmallerThanMajor}
                    ariaLabel={t('blocks.slider.fieldMajorTickInterval')}
                    fieldId={SLIDER_FIELD_IDS.majorTick}
                    onFocus={disconnectSharedToolbar}
                    className='w-full' />
                </div>
                <div className='flex flex-col'>
                  <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldMinorTicksPerMajor')}</label>
                  <NumberField
                    grammar='scientific'
                    allowNegative
                    value={minorTickInterval}
                    onChange={v => { setMinorTickInterval(v); push({ minorTickInterval: v }); }}
                    hasError={minorTickTooDense || minorNotSmallerThanMajor}
                    ariaLabel={t('blocks.slider.fieldMinorTicksPerMajor')}
                    fieldId={SLIDER_FIELD_IDS.minorTick}
                    onFocus={disconnectSharedToolbar}
                    className='w-full' />
                </div>
              </div>
            )}
            {/* min-h on each label reserves the same vertical space whether or not it wraps to a
                second line — "Slider 1/2 Position" is long enough to wrap in a 3-column grid while
                "Step Size" stays on one line, and without a shared minimum height that mismatch
                pushed the Step Size input higher than the Initial Position inputs beside it. */}
            <div className={`grid gap-2 ${dualHandle ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <div className='flex flex-col'>
                {/* No max-w any more. It existed to force "Decimal Places"/"Casas Decimais" to wrap
                    rather than run into the column beside it; "Sig Figs" fits on one line, and the
                    Initial Position fields now share a single spanning heading instead of two long
                    ones. The min-h stays: it keeps this label's baseline aligned with that heading
                    whether either wraps or not. */}
                <label className='mb-1.5 flex min-h-[2.5rem] items-end text-sm font-medium text-ws-text'>
                  {scaleMode === 'log' ? t('blocks.slider.fieldLogSigFigs') : t('blocks.slider.fieldStep')}
                </label>
                {scaleMode === 'log' ? (
                  <NumberField
                    grammar='integer'
                    value={logSigFigs}
                    onChange={v => {
                      setLogSigFigs(v);
                      // Blank is a transient typing state, not a real 0 — leave the stored step alone
                      // until there is a number to convert. (Preserved from the raw input this
                      // replaced; the conversion is the whole reason this field is not the stored value.)
                      if (v === '') return;
                      const steps = stepsPerDecadeFromLogSigFigs(v);
                      setLogStepsPerDecade(steps);
                      push({ logStepsPerDecade: steps });
                    }}
                    hasError={stepInvalid}
                    ariaLabel={t('blocks.slider.fieldLogSigFigs')}
                    fieldId={SLIDER_FIELD_IDS.logSigFigs}
                    onFocus={disconnectSharedToolbar}
                    className='w-full' />
                ) : (
                  <NumberField
                    grammar='scientific'
                    value={stepSize}
                    onChange={v => { setStepSize(v); push({ stepSize: v }); }}
                    hasError={stepInvalid}
                    ariaLabel={t('blocks.slider.fieldStep')}
                    fieldId={SLIDER_FIELD_IDS.step}
                    onFocus={disconnectSharedToolbar}
                    className='w-full' />
                )}
              </div>
              {/*
                * ONE heading across both position fields, with the slider number beside each box.
                * "Initial Position 1" and "Initial Position 2" wrapped to a second line in the
                * three-column dual layout, and repeated a five-syllable phrase to say the only
                * thing that differs between them, which is a digit. The heading spans; the digit
                * sits where the difference is.
                */}
              <div className={`flex flex-col ${dualHandle ? 'col-span-2' : ''}`}>
                {/* Centred over the PAIR when there are two, because it is a heading for both and
                    left-aligning it made it look like a label for the first box with the second
                    trailing off unlabelled. With one box it is an ordinary field label and sits
                    left, matching every other label in this panel. */}
                <label className={`mb-1.5 flex min-h-[2.5rem] items-end text-sm font-medium text-ws-text ${
                  dualHandle ? 'justify-center' : ''}`}>
                  {t('blocks.slider.fieldDefaultPosition')}
                </label>
                <div className={dualHandle ? 'grid grid-cols-2 gap-3' : ''}>
                  <div className='flex items-center gap-1.5'>
                    {dualHandle && (
                      <span aria-hidden='true' className='w-3 shrink-0 text-right text-sm font-medium text-ws-muted'>1</span>
                    )}
                    <NumberField
                      grammar='scientific'
                      allowNegative
                      value={defaultPosition}
                      onChange={v => { setDefaultPosition(v); push({ defaultPosition: v }); }}
                      hasError={defaultPositionInvalid || handleOrderInverted}
                      /* The aria-label still carries the full phrase — the visible "1" is only
                         meaningful next to the heading it sits under, which a screen reader reaching
                         this field directly has not read. */
                      ariaLabel={dualHandle ? t('blocks.slider.fieldDefaultPositionHandle1') : t('blocks.slider.fieldDefaultPosition')}
                      fieldId={SLIDER_FIELD_IDS.posA}
                      onFocus={disconnectSharedToolbar}
                      /* Narrowed only in the dual layout, where the box shares its cell with a
                         number and a neighbour. On its own it fills the column, so the row has no
                         dead gap at the end. */
                      className={dualHandle ? 'w-full max-w-20' : 'w-full'} />
                  </div>
                  {dualHandle && (
                    <div className='flex items-center gap-1.5'>
                      <span aria-hidden='true' className='w-3 shrink-0 text-right text-sm font-medium text-ws-muted'>2</span>
                      <NumberField
                        grammar='scientific'
                        allowNegative
                        value={handleBDefaultPosition}
                        onChange={v => { setHandleBDefaultPosition(v); pushHandleB({ defaultPosition: v }); }}
                        hasError={handleBDefaultPositionInvalid || handleOrderInverted}
                        ariaLabel={t('blocks.slider.fieldDefaultPositionHandle2')}
                        fieldId={SLIDER_FIELD_IDS.posB}
                        onFocus={disconnectSharedToolbar}
                        className='w-full max-w-20' />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='flex flex-col'>
              <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.slider.fieldUnitSuffix')}</label>
              <TextField fieldId='unitSuffix'
                variant='prose'
                value={unitSuffix}
                onChange={v => { setUnitSuffix(v); push({ unitSuffix: v }); }}
                ariaLabel={t('blocks.slider.fieldUnitSuffix')}
                onFocus={disconnectSharedToolbar}
                className='w-full' />
            </div>
          </div>

          {/* Never disabled. A marker is authored CONTENT — a label a teacher wrote at a value —
              and content does not stop being editable because a display toggle is off. Ghosting it
              alongside the tick settings was treating it as one of them. */}
          <div className='flex flex-col gap-3'>
              <p className='text-center text-sm font-medium text-ws-text'>{t('blocks.slider.tickLabelsHeading')}</p>
              <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                {armedTickId ? t('blocks.actions.deleteArmed') : ''}
              </div>
              <div className='flex flex-col gap-2'>
                {tickLabels.map(tl => {
                  const isArmed = armedTickId === tl.id;
                  // A value outside [min, max] has no real position on the track — the live
                  // preview hides it outright rather than clamping it to an end (which would look
                  // like it belongs there). Flagged here so the teacher notices and fixes it
                  // instead of wondering why a label they added never shows up.
                  const tlOutOfRange = tl.value.trim() !== '' && (num(tl.value) < minN || num(tl.value) > maxN);
                  return (
                    <div key={tl.id} data-field-id={tl.id} className='flex flex-col gap-1'>
                      <div className={`flex items-center gap-2 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
                        <NumberField fieldId={`${tl.id}.value`}
                          grammar='scientific'
                          allowNegative
                          value={tl.value}
                          onChange={v => updateTickLabel(tl.id, { value: v })}
                          hasError={tlOutOfRange}
                          ariaLabel={t('blocks.slider.fieldTickValue')}
                          placeholder={t('blocks.slider.fieldTickValue')}
                          onFocus={disconnectSharedToolbar}
                          className='w-28 shrink-0' />
                        <TextField fieldId={`${tl.id}.label`}
                          variant='prose'
                          value={tl.label}
                          onChange={v => updateTickLabel(tl.id, { label: v })}
                          ariaLabel={t('blocks.slider.fieldTickLabel')}
                          placeholder={t('blocks.slider.fieldTickLabel')}
                          onFocus={disconnectSharedToolbar}
                          /* A marker sits in a lane roughly this wide beside the track, and a
                             longer one is drawn ellipsised — so past this the teacher would be
                             authoring text nobody ever sees. Capping the input says that up front
                             instead of letting them type a sentence and silently cutting it. The
                             track caps what it DRAWS independently, for labels saved before this. */
                          maxLength={SLIDER_TICK_LABEL_MAX}
                          className='min-w-0 flex-1' />
                        <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                          aria-label={isArmed ? t('blocks.slider.confirmRemoveTickLabel') : t('blocks.slider.removeTickLabel')}
                          className='shrink-0'
                          onClick={() => {
                            if (isArmed) { removeTickLabel(tl.id); setArmedTickId(null); requestAnimationFrame(() => addTickLabelBtnRef.current?.focus()); }
                            else setArmedTickId(tl.id);
                          }}
                          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedTickId(null); }}
                          onBlur={() => { if (isArmed) setArmedTickId(null); }}
                        >
                          <Trash2 className='h-4 w-4' />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
                <AddRowButton
                  ref={addTickLabelBtnRef}
                  label={t('blocks.slider.addTickLabel')}
                  onClick={addTickLabel}
                />
              </div>
          </div>

          {/* Armed-delete announcement — matches numerical-block.tsx's exact pattern. Checks both
              handles' armed ids since Slider 1/2 Answers now render simultaneously rather than
              behind a switcher. */}
          <div aria-live='assertive' aria-atomic='true' className='sr-only'>
            {(armedAnswerId || armedBetweenId || armedHandleBAnswerId || armedHandleBBetweenId) ? t('blocks.actions.deleteArmed') : ''}
          </div>

          {/* Divider + heading marking where answer-setup begins — slider is the first block
              that needs a genuine pre-answer setup phase (the range), so unlike MCQ/Numerical's
              answer UI (self-evident without a label), this needs an explicit boundary. No
              symmetric "Question Setup" heading above — the question/range area doesn't need
              one, it would just take up space announcing something already obvious. */}
          {/*
            * ANSWER SETUP, hidden in OPTIONAL only.
            *
            * OPTIONAL only. Survey keeps the answer: Set Answer targets the authored band there.
            * The slider's RANGE, step, ticks and labels are the instrument and stay in every mode —
            * they decide what the student can do. This section is only the answer and its tolerance,
            * which survey and optional have nothing to act on. Hidden rather than greyed, because
            * the whole SECTION's job is gone; a single control that merely has no effect is disabled
            * in place instead.
            *
            * Nothing is deleted: switch back to Has Correct and the answers are as they were.
            */}
          {gradingMode !== 'optional' && (<>
            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {/* The scenario's own box, around the ANSWER and nothing else — the range, the
                labels and the tick marks describe the instrument rather than the answer, so
                they stay outside it exactly as they stay outside a version. Without this a
                teacher could link and never reach scenario 2 to author its answer. */}
            <ScenarioSetupFrame
              scenario={activeScenario}
              name={activeScenarioVersion?.name}
              pager={scenarioPager}
              unreviewedCount={unreviewedCount(storedForLink.scenarioBranches)}
              onDismissUnreviewed={dismissUnreviewed}
            >
            {dualHandle && (
              <p className='text-center text-sm font-medium text-ws-text'>{t('blocks.slider.handle1Answers')}</p>
            )}
            <AnswerSetupFields
              flaggedAnswerIds={flaggedAnswerIds}
              onPlainFieldFocus={disconnectSharedToolbar}
              variables={variables}
              rolledValues={rolledValues}
              resolveAnswer={resolveAnswer}
              t={t}
              min={minN}
              max={maxN}
              conditionType={conditionType}
              onConditionTypeChange={setConditionTypeA}
              answers={answers}
              onUpdateAnswer={updateAnswer}
              onAddAnswer={addAnswer}
              onRemoveAnswer={removeAnswer}
              legacyScope={restored.data}
              onToggleAnswerMode={toggleAnswerModeA}
              onToggleAnswerSymmetry={toggleAnswerSymmetryA}
              betweenAnswers={betweenAnswers}
              onUpdateBetweenAnswer={updateBetweenAnswer}
              onAddBetweenAnswer={addBetweenAnswer}
              onRemoveBetweenAnswer={removeBetweenAnswer}
              threshold={threshold}
              onThresholdChange={setThresholdA}
              armedAnswerId={armedAnswerId}
              setArmedAnswerId={setArmedAnswerId}
              armedBetweenId={armedBetweenId}
              setArmedBetweenId={setArmedBetweenId}
            />

            {dualHandle && (
              <>
                <p className='text-center text-sm font-medium text-ws-text'>{t('blocks.slider.handle2Answers')}</p>
                <AnswerSetupFields
                  fieldPrefix='handleB.'
                  flaggedAnswerIds={flaggedAnswerIds}
                  onPlainFieldFocus={disconnectSharedToolbar}
                  variables={variables}
                  rolledValues={rolledValues}
                  resolveAnswer={resolveAnswer}
                  t={t}
                  min={minN}
                  max={maxN}
                  conditionType={handleBConditionType}
                  onConditionTypeChange={setConditionTypeB}
                  answers={handleBAnswers}
                  onUpdateAnswer={updateHandleBAnswer}
                  onAddAnswer={addHandleBAnswer}
                  onRemoveAnswer={removeHandleBAnswer}
                  legacyScope={restored.data.handleB}
                  onToggleAnswerMode={toggleAnswerModeB}
                  onToggleAnswerSymmetry={toggleAnswerSymmetryB}
                  betweenAnswers={handleBBetweenAnswers}
                  onUpdateBetweenAnswer={updateHandleBBetweenAnswer}
                  onAddBetweenAnswer={addHandleBBetweenAnswer}
                  onRemoveBetweenAnswer={removeHandleBBetweenAnswer}
                  threshold={handleBThreshold}
                  onThresholdChange={setThresholdB}
                  armedAnswerId={armedHandleBAnswerId}
                  setArmedAnswerId={setArmedHandleBAnswerId}
                  armedBetweenId={armedHandleBBetweenId}
                  setArmedBetweenId={setArmedHandleBBetweenId}
                />
              </>
            )}
            </ScenarioSetupFrame>
          </>)}
          </DrivenRowFrame>
          </div>
      }
    />
    </BlockPreviewProvider>
  );
}
